import { EventEmitter, Injectable, Injector, NgZone, inject } from '@angular/core';
import {Spot} from '../models/spot';
import {Polygon} from '../models/polygon';
import {PolygonPoint} from '../models/polygon-point';
import {UndoManager} from '../utils/undo-manager';
import {
  ANNOUNCED_LABELS, DocChange, MapCommand, MapDocKind, UndoLabel, cloneDoc, docsEqual, docsEqualIgnoringDerived,
  effectiveChanges, heavierLabel, mergeInto, mergeUndo, withoutBookkeeping
} from '../utils/map-history';
import {ClearCounts, ClearPlan, ClearSelection, NO_GROUP_ID, emptyCounts} from '../models/clear-selection';
import {ImportJobsService} from './import-jobs.service';
import {ProjectBackupsApiService, RestoreResult} from '../../common/services/api/routes/project-backups-api.service';
import {ProjectApiService} from '../../common/services/api/routes/project-api.service';
import {ProjectComment} from '../../common/model/project-comment';
import {BehaviorSubject, Subject} from 'rxjs';
import {BaseGeometry} from '../models/base-geometry';
import {GeometryType} from '../enums/geometry-type.enum';
import {PointField} from '../enums/database/point-field.enum';
import {PolygonField} from '../enums/database/polygon-field.enum';
import {SpotField} from '../enums/database/spot-field.enum';
import {FirebaseApi} from '../../common/services/firebase-api.service';
import {GeometryConverter} from '../utils/geometry-converter';
import {PolygonShape} from '../enums/polygon-shape.enum';
import {ObjectUtil} from '../../common/utils/object.util';
import {LoadProjectStatus} from './project.service';
import {Logger} from '../../common/utils/logger';
import {SettingsService} from './settings.service';
import {SettingsData} from '../models/settings-data';
import {AuthService} from '../../common/services/auth.service';
import {PermissionsService} from '../../common/services/permissions.service';
import {UpdateService} from '../../common/services/update.service';
import {OrganizationService} from '../../common/services/organization.service';
import {BufferRings, BufferSettings, BufferUtil, DEFAULT_MITRE_LIMIT} from '../utils/buffer-util';
import {FileImport, FileLoader, ImportError} from '../file-loaders/file.loader';
import {MmpLoader} from '../file-loaders/mmp-loader';
import {GoogleAnalyticsService} from '../../common/services/google-analytics.service';
import {BasePointEnum} from '../enums/base-point.enum';
import {BasePoint} from '../models/base-point';
import {ProjectData} from '../../common/model/project-data';
import firebase from 'firebase/compat/app';
import {UserInfo} from '../../common/model/user-info';
import {CommonUtil} from '../../common/utils/common.util';
import {FileUtil} from '../../common/utils/file.util';
import {MBTilesFormatError, MBTilesProvider} from '../map/tile-providers/mbtiles-provider';
import {MapService} from './map.service';
import {LayerType} from '../enums/layer-type.enum';
import {MBTilesUtil} from '../utils/mbtiles.util';
import {isOwnStorageUrl} from '../utils/storage-url.util';
import {Layer} from '../models/layer';
import {StringsService} from '../../../../projects/common/src/lib/services/strings.service';
import {ActivatedRoute} from '@angular/router';
import {LogService} from '../../common/services/log.service';
import {NetworkService} from '../../../../projects/common/src/lib/services/network.service';
import {filter, take} from 'rxjs/operators';
import {firstValueFrom} from 'rxjs';
import {SphericalUtil} from '../utils/spherical-util';
import {PolyUtil} from '../utils/poly-util';
import {Sounds} from '../enums/sounds.enum';
import {ProjectExportData} from '../../common/model/project-export-data';
import {GeoGroup} from '../models/geo-group';
import {GeoGroupData} from "../models/geo-group-data";
import {ProjectAccessLevel} from '../enums/project-access-level.enum';
import {canMeasure3d, hasHeight} from '../utils/measure-3d';
import {AltitudeMode3d} from '../enums/altitude-mode.enum';
import {fitPlane} from '../map3d/textured-mesh.util';
import {toLocalMetres} from '../utils/grid-geometry';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ERROR_SNACK_DURATION, openSnack} from '../../common/utils/snack.util';
import {ProductsService} from './products.service';
import {PlanAccessState} from '../../common/services/plan-access-state.service';
import {nextDefaultName} from '../utils/default-name';

export class Geometries {
  isSet: boolean;
  groups: GeoGroup[];
  polygons: Polygon[];
  spots: Spot[];
  layers: Layer[];
}

export interface ProgressCallback {
// import firebase from 'firebase/compat/app';
  onProgressChanged(geometry: BaseGeometry, progress: number);
}

/** What an Undo or Redo did, for the canvas to say so. */
export interface UndoFeedback {
  direction: 'undo' | 'redo';
  /** Null when there was nothing to undo or redo. */
  label: UndoLabel | null;
  /** Whether this kind of step is worth announcing (see ANNOUNCED_LABELS). */
  announce: boolean;
  /** Changes left alone because someone else had changed them since. */
  conflicts: number;
  failed?: boolean;
}

/**
 * Shapes that changed or went away without this panel doing it: an undo, a
 * redo, a restore or a collaborator. Panels showing one refresh or close.
 */
export interface GeometriesUpdate {
  changed: Set<string>;
  removed: Set<string>;
  remote: boolean;
}

/**
 * Why a Subtract changed nothing, as the key of the message that says so:
 * the same shape picked twice, one still being drawn, shapes that do not
 * overlap (a hole or a line has no ground to give or take), or nothing left.
 */
export type SubtractRefusal = 'error_subtract_polygons_itself' | 'error_subtract_polygons_not_locked'
  | 'error_subtract_polygons_no_overlap' | 'error_subtract_polygons_nothing_left';

/** The demo's project: made in this browser, never in the database (see ProjectService.loadProject). */
export const DEMO_PROJECT_ID = 'test_demo';

/** A restore point could not be taken, so nothing was cleared. */
export class RestorePointError extends Error {
  constructor(readonly cause: any) {
    super('restore point failed');
  }
}

/**
 * The map a clear was planned for is no longer the one open -- another
 * project was opened, or this one left or reloaded, while its restore point
 * was being taken -- so nothing was cleared, and what was to follow it (an
 * import) must not go ahead either.
 */
export class ClearSupersededError extends Error {
  constructor() {
    super('clear superseded');
  }
}

/**
 * A version was restored on the server, but the map could not be read back to
 * show it. Callers find out why through `msg.errorCode`, as with the API's
 * own errors.
 */
export class RestoreReloadError extends Error {
  readonly msg = {errorCode: 'restore_reload_failed'};

  constructor() {
    super('restore reload failed');
  }
}

export interface SaveOptions {
  addUndoCheckpoint?: boolean;
  /**
   * Close the step at the end of this task instead of waiting for more of the
   * same edit, and send the save then rather than after the usual pause.
   */
  saveNow?: boolean;
  /**
   * A continuous edit -- typing in a panel, dragging a colour -- whose saves to
   * the same shape in quick succession are one step. Everything else (a click
   * that adds a vertex, a delete) is a step of its own, however fast the next
   * one comes.
   */
  coalesce?: boolean;
  label?: UndoLabel;
  /**
   * A looked-up value (a corner's ground height), not an edit: saved without
   * the shape's last-update stamps changing. A session with the 3D view open
   * saved the heights of shapes other people were drawing under its own name,
   * which made those shapes its own unfinished ones.
   */
  background?: boolean;
}

interface QueuedWrite {
  projectId: string;
  kind: MapDocKind;
  id: string;
  /** Null deletes the document. */
  data: any | null;
  attempts: number;
  /**
   * For a shape the database already holds: the top-level fields this session
   * changed, written one by one with update() so that someone else's change
   * to another field -- a rename while this session drags a vertex -- is
   * kept. Absent, the whole document is written (a new shape, a group, a
   * layer). `points` here writes every vertex at once.
   */
  fields?: Set<string>;
  /** The vertices changed (added, moved or removed), each written as points.<id>. */
  vertices?: Set<string>;
  /** Of those, the ones this session added. */
  addedVertices?: Set<string>;
  /**
   * Of the vertices changed that both versions hold, what changed in each:
   * only that is written, as points.<id>.<attribute>. Written whole, a moved
   * vertex carried this session's copy of its number in the outline, and
   * landing after someone else's insert or delete -- which renumbers the
   * vertices after it -- left two vertices with one number: the outline
   * crossed itself. A changed vertex with no entry here is written whole.
   */
  vertexFields?: Map<string, Set<string>>;
}

/** The field a shape keeps its vertices in, one entry per vertex id. */
const POINTS_FIELD = 'points';
/** Past this many changed vertex values, a write sends the whole vertex map as one field. */
const MAX_VERTEX_PATHS = 500;
/** Who saved a document last, and when: written with every save, not part of the shape. */
const STAMP_KEYS = new Set<string>([PolygonField.KEY_LAST_UPDATE_USER, PolygonField.KEY_LAST_UPDATE_DEVICE,
  PolygonField.KEY_LAST_UPDATE_TIMESTAMP, PolygonField.KEY_LAST_UPDATE_SESSION_ID]);

function isPlainMap(value: any): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * What a save changes in a shape: each top-level field that differs, and each
 * vertex -- with, for a vertex both versions hold, the attributes that differ.
 */
function shapeChanges(before: any, after: any): {fields: Set<string>, vertices: Set<string>, added: Set<string>,
  vertexFields: Map<string, Set<string>>} {
  const fields = new Set<string>();
  const vertices = new Set<string>();
  const added = new Set<string>();
  const vertexFields = new Map<string, Set<string>>();
  new Set([...Object.keys(before), ...Object.keys(after)]).forEach(key => {
    if (key === POINTS_FIELD && isPlainMap(before[key]) && isPlainMap(after[key])) {
      const was = before[key];
      const is = after[key];
      new Set([...Object.keys(was), ...Object.keys(is)]).forEach(id => {
        if (!docsEqual(was[id], is[id])) {
          vertices.add(id);
          if (was[id] === undefined) {
            added.add(id);
          } else if (isPlainMap(was[id]) && isPlainMap(is[id])) {
            const attributes = new Set<string>();
            new Set([...Object.keys(was[id]), ...Object.keys(is[id])]).forEach(attribute => {
              if (!docsEqual(was[id][attribute], is[id][attribute])) {
                attributes.add(attribute);
              }
            });
            if (attributes.size) {
              vertexFields.set(id, attributes);
            }
          }
        }
      });
    } else if (!docsEqual(before[key], after[key])) {
      fields.add(key);
    }
  });
  return {fields, vertices, added, vertexFields};
}

/**
 * A stored vertex that is one. A change to a vertex deleted meanwhile leaves
 * just that change behind, without any of what never changes on a vertex
 * (its id, its shape's, its type), which every vertex is saved with.
 */
function isWholeVertex(vertex: any): boolean {
  return isPlainMap(vertex) && [PointField.KEY_ID, PointField.KEY_POINT_ID, PointField.KEY_POLYGON_ID, PointField.KEY_TYPE]
    .some(key => vertex[key] !== undefined && vertex[key] !== null);
}

/**
 * The vertices in outline order, by the links between neighbours
 * (next_point_id, which wraps round from the last to the first): from the
 * lowest-numbered one round to it again. Null unless the links are one
 * unbroken round through every vertex.
 */
function linkedVertexOrder(points: any, ids: string[]): string[] | null {
  if (ids.some(id => typeof points[id][PointField.KEY_INDEX] !== 'number')) {
    return null;
  }
  const lowest = Math.min(...ids.map(id => points[id][PointField.KEY_INDEX]));
  const starts = ids.filter(id => points[id][PointField.KEY_INDEX] === lowest);
  if (starts.length !== 1) {
    return null;
  }
  const order: string[] = [];
  const seen = new Set<string>();
  let id = starts[0];
  while (order.length < ids.length) {
    if (!id || seen.has(id) || !points[id]) {
      return null;
    }
    order.push(id);
    seen.add(id);
    id = points[id][PointField.KEY_NEXT_POINT_ID];
  }
  return id === starts[0] ? order : null;
}

/**
 * A stored shape with its vertices made sound for drawing, the document
 * itself untouched (a copy is returned where anything changes).
 *
 * Vertices are saved attribute by attribute (QueuedWrite.vertexFields), so two
 * people changing the order of one shape at once -- each inserting or
 * deleting a vertex -- can leave two vertices with the same number, while the
 * links between neighbours, saved the same way, still say how they go. Sorted
 * by number alone, the tie went either way and the outline could cross
 * itself; it is put in the linked order instead (the next save stores the
 * numbers so). A vertex deleted by one person while another's change to it
 * was on its way is only that change, with no id, and is left out (the next
 * save removes it).
 */
function withSoundVertices(doc: any): any {
  const points = doc && doc[POINTS_FIELD];
  if (!isPlainMap(points)) {
    return doc;
  }
  const all = Object.keys(points);
  const ids = all.filter(id => isWholeVertex(points[id]));
  const indexes = new Set(ids.map(id => points[id][PointField.KEY_INDEX]));
  // No vertex in the form this app saves: not leftovers of a collision but a
  // shape stored some other way, which is drawn as it is rather than emptied.
  if (!ids.length || (ids.length === all.length && indexes.size === ids.length)) {
    return doc;
  }
  const kept = {};
  ids.forEach(id => kept[id] = points[id]);
  if (indexes.size < ids.length) {
    const order = linkedVertexOrder(kept, ids);
    if (order) {
      order.forEach((id, index) => kept[id] = {...kept[id], [PointField.KEY_INDEX]: index});
    }
  }
  return {...doc, [POINTS_FIELD]: kept};
}

/** A document as it should be after an undo, redo or remote change; null removes it. */
interface DocTarget {
  kind: MapDocKind;
  id: string;
  data: any | null;
}

/** Steps kept. Each holds the shapes it touched twice over, so not forever. */
const UNDO_LIMIT = 100;
/**
 * Edits to the same shapes this close together are one step: a drag fires a
 * save per move, typing a name one per pause, and nobody wants to undo them
 * one frame at a time.
 */
const UNDO_COALESCE_MS = 300;
/** Saves wait this long for more edits to the same shapes... */
const WRITE_DELAY_MS = 1500;
/** ...but never longer than this while someone keeps editing. */
const WRITE_MAX_DELAY_MS = 10000;
/** Firestore takes 500 writes per batch and about 10 MB; stay under both. */
const MAX_BATCH_OPS = 400;
const MAX_BATCH_BYTES = 8 * 1024 * 1024;
/** A failed write is retried this many times before it is reported and dropped. */
const MAX_WRITE_ATTEMPTS = 3;
/**
 * Refusals about the documents themselves -- one too large, or with too many
 * vertices to index -- which no retry of the same batch can get past.
 */
const DOCUMENT_WRITE_ERRORS = ['invalid-argument', 'out-of-range'];
/** How long a restore point (or a restore) waits for the saves it must come after. */
const RESTORE_POINT_SAVE_WAIT_MS = 30000;
/** How long the last state of something someone else removed is kept (SyncService.removedLately). */
const REMOVED_KEPT_MS = 30 * 60 * 1000;
/**
 * How long what the server imported (POST /import) is waited for through the
 * listeners once nothing more of it has come (awaitServerImport). Thousands of
 * shapes arrive in a few batches of a few seconds; the wait goes on while they
 * keep coming, however many there are.
 */
const IMPORT_ARRIVAL_MS = 60000;

/** What a server import brought, once arrived (awaitServerImport). */
export interface ServerImportArrival {
  /** The import's undo step, to record with the rest of the import (recordImport). */
  changes: DocChange[];
  /** Its shapes and spots on the map. */
  geometries: (Polygon | Spot)[];
  /** Documents it wrote that never arrived. */
  missing: number;
}
/** Restarts of failed listeners before giving up, the first after this long, each one twice as late. */
const LISTENER_RETRY_LIMIT = 5;
const LISTENER_RETRY_BASE_MS = 2000;
/** A listener failure this long after the one before starts the count again. */
const LISTENER_FAILURE_RESET_MS = 10 * 60 * 1000;

/**
 * The lists' order, by name. One collator for every comparison: localeCompare
 * set up its own on each call, and the lists are sorted again after every
 * change, a collaborator's included.
 */
const NAME_ORDER = new Intl.Collator();
const byName = (a: {name?: string}, b: {name?: string}) => NAME_ORDER.compare(a.name || '', b.name || '');

@Injectable({
  providedIn: 'root'
})
export class SyncService {
  private firebaseApi = inject(FirebaseApi);
  private importJobs = inject(ImportJobsService);
  private network = inject(NetworkService);
  private authService = inject(AuthService);
  private permissions = inject(PermissionsService);
  private planAccess = inject(PlanAccessState);
  private organization = inject(OrganizationService);
  private settingsService = inject(SettingsService);
  private logService = inject(LogService);
  private mapService = inject(MapService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private activatedRoute = inject(ActivatedRoute);
  private backupsApi = inject(ProjectBackupsApiService);
  private projectsApi = inject(ProjectApiService);
  /** The open project's comments, as the map shows them (for Clear Map). */
  private projectComments: ProjectComment[] = [];
  private strings = inject(StringsService);
  private snackBar = inject(MatSnackBar);
  private ngZone = inject(NgZone);
  /** For ProductsService, which needs this service and so is looked up when used (see loadedCatalogue). */
  private injector = inject(Injector);


  onGeometryListChanged = new BehaviorSubject<Geometries>(null);

  /**
   * Something that affects how a geometry is PAINTED changed.
   *
   * The map-polygon elements repaint themselves through Angular bindings, but
   * the product-texture layer is a canvas that Angular knows nothing about, so
   * it needs telling. Listeners are expected to coalesce — this fires on every
   * save, including the stream of them a drag produces.
   */
  onAppearanceChanged = new Subject<void>();
  onLoadingStarted = new EventEmitter();
  forceCluster = new EventEmitter();
  onLoadingFinished = new EventEmitter<number>();
  /**
   * Whether the end of a load fits the view to the map. Not after an import
   * that took long: the person has moved on meanwhile, and the view jumped
   * under them (finishImport).
   */
  fitOnLoadingFinished = true;
  /**
   * The last state of shapes, groups and layers someone else (the server
   * included) removed lately, by kind:id: what a Replace's undo puts back,
   * edits made after the import started included (awaitServerImport).
   */
  private removedLately = new Map<string, {snapshot: any, at: number}>();
  onLayerAdded = new EventEmitter<Layer>();
  mapCleared = new EventEmitter();
  savingChangesEvent = new EventEmitter();
  failedToSaveEvent = new EventEmitter();
  changesSavedEvent = new EventEmitter();
  measureUnitsChangedEvent = new EventEmitter();
  updateMeasurementBox = new EventEmitter();
  onPlaySound = new EventEmitter<Sounds>();
  /** Every Undo and Redo, with what it did; see UndoFeedback. */
  undoApplied = new Subject<UndoFeedback>();
  /** Shapes changed or removed by an undo, redo, restore or collaborator. */
  geometriesUpdated = new Subject<GeometriesUpdate>();
  unsubscribeDataListener = null;
  unsubscribeGroupsListener = null;
  unsubscribeLayersListener = null;
  currentPolygonChanged = new BehaviorSubject<Polygon>(null);

  set isLoading(active: boolean) {
    this.loadingProgress = active ? 0 : -1;
  }

  loadingProgress = -1;
  private settings: SettingsData;
  /**
   * The last known state of every shape document: what this session last wrote
   * or last received. It is the "before" of the next change to a shape, so a
   * change is always recorded against what was actually saved.
   */
  private fireGeometries = {};
  private dataGroups = {};
  private polygons: Polygon[] = [];
  private spots: Spot[] = [];
  private layers: Layer[] = [];
  /** Shapes and spots by id; getGeometryById used to scan every vertex of every shape. */
  private geoIndex = new Map<string, Polygon | Spot>();
  /** Map-layer documents as stored, for putting a cleared layer back exactly. */
  private layerDocs = new Map<string, any>();
  private geometriesChanged = false;
  private connectedUsers: UserInfo[];
  private openSessions = 1;
  private elevationService: google.maps.ElevationService;

  // ── Undo ────────────────────────────────────────────────────────────────
  private history = new UndoManager<MapCommand>(UNDO_LIMIT);
  /** Changes recorded but not yet closed into a step (see recordChanges). */
  private pendingChanges = new Map<string, DocChange>();
  private pendingLabel: UndoLabel = null;
  private pendingTimer: any = null;
  /** True until the end of the task that recorded the first pending change. */
  private pendingTaskOpen = false;
  /** Every change in the step being built came from a continuous edit (SaveOptions.coalesce). */
  private pendingCoalesce = false;
  private undoGroupDepth = 0;
  /** An undo or redo is being applied; the buttons wait for it. */
  undoBusy = false;
  /**
   * Shapes that have left the map in this session -- deleted, undone, cleared or
   * removed by someone else. A panel still open on one must not bring it back
   * by saving it, which is how an edit after an undo used to resurrect shapes.
   */
  private removedIds = new Set<string>();
  /** Counts the loads of a project into the map: a switch, a reload, a restore. */
  private loadEpoch = 0;
  /**
   * The load each shape and spot object belongs to: the one it was made or put
   * on the map in. A load replaces every object, but a drawer, a height lookup
   * or a closing panel can still hold an old one, and saving it put it back on
   * the map -- into whichever project was open by then (see saveGeometry).
   */
  private geoEpoch = new WeakMap<BaseGeometry, number>();
  /** When another session last changed each document: the conflict check's evidence. */
  private remoteChangedAt = new Map<string, number>();

  // ── Saving ──────────────────────────────────────────────────────────────
  /**
   * Writes waiting to go to Firestore, the newest per document. Saves and
   * deletes share it, so they reach the database in the order they happened:
   * a delete used to go out at once while an earlier save sat here for a
   * moment and then recreated the shape.
   */
  private writeQueue = new Map<string, QueuedWrite>();
  private writeTimer: any = null;
  private writeQueueSince = 0;
  private writesInFlight = new Set<Promise<void>>();
  /**
   * Documents whose last change the server refused for good, as
   * "project/kind:id" (see unsavedKey). The open project's save status stays
   * "Save failed" until each is saved, deleted or gone: a later successful
   * save used to say "Saved" over the loss.
   */
  private unsavedDocs = new Set<string>();
  /** Whether each listener has delivered its first snapshot yet (the data listener: from the server). */
  private dataListenerPrimed = false;
  private groupsListenerPrimed = false;
  private layersListenerPrimed = false;
  /** The project whose changes this session should follow live, once loaded; null when none. */
  private listenFor: string = null;
  /** The project the listeners are running for; null while they are not. */
  private listeningTo: string = null;
  /** Counts listener starts, so that a failure of listeners since replaced is ignored. */
  private listenerRun = 0;
  private listenerFailures = 0;
  private lastListenerFailure = 0;
  private listenerRetryTimer: any = null;
  /**
   * Pasted raw data (support's Load raw) is on the map instead of the open
   * project's shapes: a read-only view until a project is loaded or another
   * opened. Its shapes, and any edit to them, were saved into the customer's
   * project.
   */
  private rawView = false;
  private openProject: ProjectData;
  currentSessionId: string;

  get currentProject(): ProjectData {
    return this.openProject;
  }

  /**
   * Set again with every change to the project's document (ProjectService).
   * Members, sharing or the bin may have changed who can follow it live.
   */
  set currentProject(project: ProjectData) {
    // A raw data view ends with the project it was opened over: the next one
    // opened is set up (session, presence) as editable before it loads.
    if (this.rawView && project?.project_id !== this.openProject?.project_id) {
      this.rawView = false;
    }
    this.openProject = project;
    if (this.listenFor || this.listeningTo) {
      this.updateDataEventsListeners();
    }
  }

  _currentPolygon: Polygon;

  get currentPolygon() {
    return this._currentPolygon;
  }

  set currentPolygon(polygon: Polygon) {
    if (this._currentPolygon) {
      this._currentPolygon.isLocked = true;
    }
    this._currentPolygon = polygon;
    if (polygon) {
      polygon.isLocked = false;
    }
    this.currentPolygonChanged.next(polygon);
  }

  constructor() {
    const settingsService = this.settingsService;

    settingsService.onSettingsChanged.subscribe(value => {
      if (value) {
        this.settings = value;
      }
    });
    settingsService.onSettingsSaved.subscribe(value => {
      if (value) {
        this.settings = value;
      }
    });
    // Units that arrive after the shapes (an embed learning the visitor's
    // country) or from another device: the side labels and the measures box
    // were left in the old units.
    settingsService.onUnitsReceived.subscribe(() => this.onMeasureUnitsChanged());
    Logger.log('Loading Elevation API');
    this.elevationService = new google.maps.ElevationService();
    // mapsAPILoader.load().then(value => {
    //   Logger.log('Elevation loaded');
    // }).catch(reason => {
    //   Logger.error('Elevation NOT loaded', reason);
    // });
    // Anything still waiting to be saved when the tab closes gets its chance.
    window.addEventListener('pagehide', () => this.flushWrites());
    // And before a reload for a new version, which waits for it.
    inject(UpdateService).beforeReload(() => this.flushWrites());
    // Hidden is the last moment a page can count on: a phone discards a tab
    // in the background without a pagehide, and a closing tab leaves a write
    // little time to get out.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushWrites();
      }
    });
    // Closing or reloading with edits not yet saved asks first. They were
    // lost without a word: saves wait a moment to gather a drag's worth.
    window.addEventListener('beforeunload', event => {
      if (this.hasUnsavedWrites()) {
        this.flushWrites();
        event.preventDefault();
        event.returnValue = '';
      }
    });
  }

  /** Edits waiting to be sent, or sent and not yet confirmed. */
  hasUnsavedWrites(): boolean {
    return this.writeQueue.size > 0 || this.writesInFlight.size > 0;
  }

  getGeometryById(geoId: string) {
    const indexed = this.geoIndex.get(geoId);
    if (indexed) {
      return indexed;
    }
    let geo = null;
    this.polygons.every(poly => {
      if (poly.id === geoId) {
        geo = poly;
        return false;
      }
      poly.points.every(point => {
        if (point.id === geoId) {
          geo = point;
          return false;
        }
        return true;
      });
      return !geo;
    });
    if (!geo) {
      this.spots.every(value => {
        if (value.id === geoId) {
          geo = value;
          return false;
        }
        return true;
      });
    }
    if (!geo) {
      this.layers.every(value => {
        if (value.id === geoId) {
          geo = value;
          return false;
        }
        return true;
      });
    }
    return geo;
  }

  addGeometries(geometries: BaseGeometry[]) {
    geometries.forEach(geo => {
      if (this.geoIndex.has(geo.id)) {
        return;
      }
      switch (geo.type) {
        case GeometryType.TYPE_POLYGON:
          this.polygons.push(geo as Polygon);
          this.geoIndex.set(geo.id, geo as Polygon);
          this.geoEpoch.set(geo, this.loadEpoch);
          break;
        case GeometryType.TYPE_SPOT:
          this.spots.push(geo as Spot);
          this.geoIndex.set(geo.id, geo as Spot);
          this.geoEpoch.set(geo, this.loadEpoch);
          break;
        // case GeometryType.TYPE_CONTOUR_LAYER:
        //   this.layers.push(geo);
        //   break;
      }
    });
    this.dispatchOnGeometriesChanged();
  }

  setGeometries(geometries: BaseGeometry[]) {
    this.polygons = [];
    this.spots = [];
    this.layers = [];
    this.geoIndex.clear();
    this.addGeometries(geometries);
  }

  getProjectMapData(): ProjectExportData {
    return {
      projectId: this.currentProject.project_id,
      projectName: this.currentProject.name,
      layers: this.layers,
      spots: this.spots,
      polygons: this.polygons,
      groups: ObjectUtil.toArray(this.dataGroups) as GeoGroupData[],
      project: MmpLoader.projectSettingsOf(this.currentProject),
    };
  }

  getPolygons(): Array<Polygon> {
    return this.polygons;
  }

  getLayers(): Array<Layer> {
    return this.layers;
  }

  getSpots(): Array<Spot> {
    return this.spots;
  }

  getGeometries(): Array<BaseGeometry> {
    let arr: Array<BaseGeometry> = [];
    arr = arr.concat(this.polygons);
    arr = arr.concat(this.spots);
    return arr;
  }

  getGeometriesData(){
    return Object.values(this.fireGeometries)
  }

  hasGeometries(): boolean {
    return this.polygons.length > 0 || this.spots.length > 0 || this.layers.length > 0;
  }

  hasGeometriesChanged(): boolean {
    return this.geometriesChanged;
  }

  // ── Undo and redo ─────────────────────────────────────────────────────────

  /** Whether Undo would do something now. */
  get canUndo(): boolean {
    return this.canRecordUndo() && !this.undoBusy && this.undoGroupDepth === 0
      && (this.history.hasUndo() || this.pendingChanges.size > 0);
  }

  /** Whether Redo would do something now. An edit not yet closed into a step ends the redo list. */
  get canRedo(): boolean {
    return this.canRecordUndo() && !this.undoBusy && this.undoGroupDepth === 0
      && this.pendingChanges.size === 0 && this.history.hasRedo();
  }

  undo(): Promise<void> {
    return this.stepHistory('undo');
  }

  /**
   * Undo, but only while the newest step is still the one a message offered to
   * undo: pressing a snackbar's Undo after another edit must not take back
   * that edit instead.
   */
  undoIfLatest(label: UndoLabel): Promise<void> {
    this.flushUndo();
    const latest = this.history.peekUndo();
    return latest && latest.label === label ? this.undo() : Promise.resolve();
  }

  /** The newest step, closed: for a message to undo exactly it later (undoIfStillLatest). */
  latestStep(): object | null {
    this.flushUndo();
    return this.history.peekUndo() || null;
  }

  /**
   * Undo, but only while `step` is still the newest. A label is not enough
   * where several kinds of change share it: an import's message must not take
   * back a picture placed by hand since.
   */
  undoIfStillLatest(step: object | null): Promise<void> {
    this.flushUndo();
    return step && this.history.peekUndo() === step ? this.undo() : Promise.resolve();
  }

  redo(): Promise<void> {
    return this.stepHistory('redo');
  }

  /**
   * Makes everything recorded until the matching endUndoGroup one step, however
   * long it takes. For work that waits on the network half way through -- an
   * import, deleting a group and its shapes -- which would otherwise fall
   * apart into several steps.
   */
  beginUndoGroup() {
    if (this.undoGroupDepth === 0) {
      this.flushUndo();
    }
    this.undoGroupDepth++;
  }

  endUndoGroup() {
    this.undoGroupDepth = Math.max(0, this.undoGroupDepth - 1);
    if (this.undoGroupDepth === 0) {
      this.flushUndo();
    }
  }

  /**
   * Closes the step being built. Called before every undo and redo: a Ctrl+Z
   * pressed straight after an edit used to undo the step before it, and the
   * edit then landed on top and wiped the redo list.
   */
  flushUndo() {
    clearTimeout(this.pendingTimer);
    this.pendingTimer = null;
    if (this.pendingChanges.size === 0) {
      return;
    }
    const changes = effectiveChanges(this.pendingChanges.values());
    const label = this.pendingLabel || UndoLabel.EDIT;
    this.pendingChanges = new Map();
    this.pendingLabel = null;
    this.pendingCoalesce = false;
    // A step that changed nothing -- a label dragged back to where it was, a
    // description saved unedited -- is not a step.
    if (changes.length > 0) {
      this.history.add(this.makeCommand(label, changes));
    }
  }

  /** Undo is recorded for whoever may edit, and in the demo, whose map is local. */
  private canRecordUndo(): boolean {
    return this.canEdit() || this.isDemoMode();
  }

  /**
   * Adds changes to the step being built.
   *
   * A step is one gesture. Changes made in the same task belong together (a
   * split deletes one shape and draws two; moving a vertex shared by three
   * shapes saves three). A continuous edit to the same shapes in quick
   * succession -- typing a name, dragging a colour -- is one step too. Any
   * other change after the task has ended starts a new step: two quick clicks
   * that add two vertices are two steps. Inside beginUndoGroup/endUndoGroup
   * everything is one step.
   *
   * This replaced a 300 ms timer that merged whatever happened inside it, in
   * order, so a quick second edit could fold into the first and undo would
   * replay them in the wrong order.
   */
  private recordChanges(changes: DocChange[], label: UndoLabel,
                        options: {immediate?: boolean, standalone?: boolean, coalesce?: boolean} = {}) {
    if (!changes.length || this.history.isExecuting || !this.canRecordUndo()) {
      return;
    }
    const grouped = this.undoGroupDepth > 0;
    const continuing = this.pendingTaskOpen || (!!options.coalesce && this.pendingCoalesce
      && changes.some(c => this.pendingChanges.has(`${c.kind}:${c.id}`)));
    if (options.standalone && !grouped) {
      this.flushUndo();
    } else if (this.pendingChanges.size > 0 && !grouped && !continuing) {
      this.flushUndo();
    }
    this.pendingCoalesce = this.pendingChanges.size === 0 ? !!options.coalesce : this.pendingCoalesce && !!options.coalesce;
    mergeInto(this.pendingChanges, changes);
    this.pendingLabel = heavierLabel(this.pendingLabel, label);
    if (!this.pendingTaskOpen) {
      this.pendingTaskOpen = true;
      setTimeout(() => this.pendingTaskOpen = false, 0);
    }
    if (grouped) {
      return;
    }
    if (options.standalone) {
      this.flushUndo();
      return;
    }
    clearTimeout(this.pendingTimer);
    this.pendingTimer = setTimeout(() => this.flushUndo(), options.immediate ? 0 : UNDO_COALESCE_MS);
  }

  private makeCommand(label: UndoLabel, changes: DocChange[]): MapCommand {
    const command: MapCommand = {
      label,
      changes,
      appliedAt: Date.now(),
      conflicts: 0,
      undo: () => this.applyCommand(command, 'undo'),
      redo: () => this.applyCommand(command, 'redo'),
    };
    return command;
  }

  private async stepHistory(direction: 'undo' | 'redo'): Promise<void> {
    // Not while an undo is being applied, nor half way through an import that
    // is still recording its one step.
    if (this.undoBusy || this.undoGroupDepth > 0) {
      return;
    }
    // Rights can be taken away mid-session. The history is then of no use and
    // must not be replayed against a project this session can no longer write.
    if (!this.canRecordUndo()) {
      this.history.clear();
      this.pendingChanges.clear();
      return;
    }
    this.flushUndo();
    const next = direction === 'undo' ? this.history.peekUndo() : this.history.peekRedo();
    if (!next) {
      this.undoApplied.next({direction, label: null, announce: true, conflicts: 0});
      return;
    }
    this.undoBusy = true;
    try {
      const command = direction === 'undo' ? await this.history.undo() : await this.history.redo();
      if (command) {
        this.undoApplied.next({
          direction,
          label: command.label,
          announce: ANNOUNCED_LABELS.includes(command.label),
          conflicts: command.conflicts,
        });
      }
    } catch (e) {
      Logger.error(`${direction} failed`, e);
      if (e instanceof RestoreReloadError) {
        // Done on the server all the same: only showing the map failed.
        openSnack(this.snackBar, this.strings.getString('restore_reload_failed'), this.strings.getString('dismiss'),
          ERROR_SNACK_DURATION, 'error');
      } else {
        this.undoApplied.next({direction, label: next.label, announce: true, conflicts: 0, failed: true});
      }
    } finally {
      this.undoBusy = false;
      // For the measurements box and the drawing preview. Every shape the step
      // touched was relabelled as it was applied (applyDocStates), and a
      // restore reloads them all; relabelling the whole project here took
      // hundreds of milliseconds per Ctrl+Z on large ones.
      this.measureUnitsChangedEvent.emit();
    }
  }

  /**
   * Takes a step back (or does it again) against the map as it is now.
   *
   * Changes are undone newest first and redone oldest first, each merged into
   * the document's current state (mergeUndo): only the fields the step changed
   * move, and only where nobody has changed them since. Two rules keep it from
   * overriding other people:
   *  - a shape someone else deleted is never brought back by undoing an edit
   *    to it;
   *  - a shape someone else changed after this step is not deleted by undoing
   *    its creation (or redoing its removal).
   * Everything is applied in one pass: one list refresh, one hole update, one
   * batched write, however many shapes a Clear Map took with it.
   */
  private async applyCommand(command: MapCommand, direction: 'undo' | 'redo'): Promise<void> {
    const changes = direction === 'undo' ? [...command.changes].reverse() : command.changes;
    const targets: DocTarget[] = [];
    let conflicts = 0;
    for (const change of changes) {
      const from = direction === 'undo' ? change.after : change.before;
      const to = direction === 'undo' ? change.before : change.after;
      const current = this.currentDocData(change.kind, change.id);
      const changedElsewhere = (this.remoteChangedAt.get(change.id) || 0) > command.appliedAt;
      if (to === null) {
        if (current === null) {
          continue;
        }
        // Heights, area and the vertex numbering follow the shape: another
        // session saving the corners' heights is no reason to keep it.
        if (changedElsewhere && !docsEqualIgnoringDerived(current, from)) {
          conflicts++;
          continue;
        }
        targets.push({kind: change.kind, id: change.id, data: null});
      } else if (from === null) {
        if (current === null) {
          // Nothing downstream changes the data it is given (writeDoc copies it),
          // so the recorded document is used as it is.
          targets.push({kind: change.kind, id: change.id, data: to});
        }
      } else if (current === null) {
        conflicts++;
      } else {
        const merged = mergeUndo(current, from, to);
        conflicts += merged.conflicts;
        if (!docsEqual(merged.value, current)) {
          targets.push({kind: change.kind, id: change.id, data: merged.value});
        }
      }
    }
    this.applyDocStates(targets, {write: true});
    command.appliedAt = Date.now();
    command.conflicts = conflicts;
    this.onPlaySound.emit(targets.some(t => t.data === null) ? Sounds.ZAP : Sounds.TICK);
    this.flushWrites();
    // The comments the step removed, back (or away again) through the API.
    if (command.comments && command.comments.ids.length
      && command.comments.projectId === this.currentProject?.project_id) {
      const {projectId, ids} = command.comments;
      if (direction === 'undo') {
        await this.projectsApi.restoreComments(projectId, ids);
      } else {
        const result = await this.projectsApi.clearComments(projectId, ids);
        if (result && Array.isArray(result.cleared) && result.cleared.length) {
          command.comments.ids = result.cleared;
        }
      }
    }
  }

  /** A document's current state in the same form a change records it, or null. */
  private currentDocData(kind: MapDocKind, id: string): any | null {
    if (kind === 'geo') {
      const geo = this.geoIndex.get(id);
      return geo ? withoutBookkeeping(geo.toFirebaseData(true)) : null;
    }
    if (kind === 'group') {
      return this.dataGroups[id] ? withoutBookkeeping(this.dataGroups[id]) : null;
    }
    return this.layers.some(l => l.id === id) ? withoutBookkeeping(this.layerDocOf(id)) : null;
  }

  initCurrentPolygon(position: google.maps.LatLngLiteral, showSurface: boolean, shape: PolygonShape): Polygon {
    if (!this._currentPolygon) {
      this.currentPolygon = this.createPolygon(position, showSurface, shape);
    }
    return this._currentPolygon;
  }

  createPolygon(firstPoint: google.maps.LatLngLiteral, showSurface: boolean, shape: PolygonShape): Polygon {
    Logger.log('createPolygon 0', this.canEdit(), this.isDemoMode());
    if (!this.canEdit() && !this.isDemoMode()) {
      return null;
    }
    Logger.log('createPolygon 1');
    const polygon = this.newPolygon(shape, showSurface);
    const point = this.newPoint(polygon, firstPoint);
    polygon.points.push(point);
    if (shape === PolygonShape.CIRCLE) {
      polygon.points.push(this.newPoint(polygon, firstPoint));
      polygon.calculateDistanceLabels();
    }
    Logger.log('createPolygon 2');
    polygon.updatePath();
    this.saveGeometry({addUndoCheckpoint: true}, polygon);
    Logger.log('createPolygon 3');
    return polygon;
  }

  getExtraPoints(polygon: Polygon) {
    return new Promise<PolygonPoint[]>((resolve, reject) => {
      const ref = this.firebaseApi.getProjectRef(this.currentProject.project_id).collection(`data/${polygon.id}/points`);
      ref.ref.get().then(snaps => {
        if (!snaps.empty) {
          const points: PolygonPoint[] = [];
          snaps.forEach(doc => {
            const dataPoint = doc.data();
            points.push(GeometryConverter.dataToPoint(polygon, dataPoint));
          });
          resolve(points);
        } else {
          reject();
        }
      }).catch(reject);
    });
  }

  addPointToPolygon(polygon: Polygon, latLng: google.maps.LatLngLiteral): Promise<void> {
    if (!this.canEdit() && !this.isDemoMode()) {
      return Promise.reject();
    }
    // An area being drawn never ends on a copy of its first corner: unlocking
    // takes it off (Canvas.unlockPolygon). One saved open with the copy still
    // there (before that) carried on from it -- the line ran on from the first
    // corner, and locking closed the outline a second time.
    if (!polygon.isLocked && polygon.shape === PolygonShape.POLYGON && polygon.showSurface
      && polygon.getPointsCount() > 2 && polygon.isClosed()) {
      polygon.removePoint(polygon.getLastPoint());
    }
    const point = this.newPoint(polygon, latLng);
    point.firstAnimation = true;
    polygon.addPoint(point);
    return this.saveGeometry({addUndoCheckpoint: true}, polygon);
  }

  insertPolygonPoint(polygon: Polygon, position: google.maps.LatLngLiteral, index: number, saveNow: boolean = false) {
    if (!this.canEdit()) {
      return Promise.reject();
    }
    const point = this.newPoint(polygon, position);
    point.firstAnimation = true;
    polygon.insertPoint(point, index);
    return this.saveGeometry({addUndoCheckpoint: true, saveNow}, point.parent);
    // this.saveGeometry(point, true).then(resolve).catch(reject);
  }

  movePolygon(polygon: Polygon, pivot: PolygonPoint, to: google.maps.LatLngLiteral) {
    if (!this.canEdit()) {
      return;
    }

    if (!pivot) {
      Logger.error('PIVOT IS NULL');
      return;
    }
    if (!to) {
      Logger.error('To point IS NULL');
      return;
    }

    const offsetX = to.lat - pivot.lat;
    const offsetY = to.lng - pivot.lng;

    this.movePolygonByOffset(polygon, pivot, offsetX, offsetY);
    this.updateHoles();
  }

  /**
   * `moving`: the shapes this one drag has moved so far, each moved once
   * (a buffer's source takes its other buffers along, and not this one again).
   *
   * A move takes a shape's buffers along with it rather than drawing them
   * again: while it runs, saves do not redraw buffers (see followBuffers). An
   * inner buffer moved on its own is moved off the shape it was drawn within,
   * so it lets go of it, as one edited by hand does.
   */
  movePolygonByOffset(polygon: Polygon, pivot: PolygonPoint, offsetX: number, offsetY: number, save: boolean = true, moveParent: boolean = true, moveChildren: boolean = true,
                      moving: Set<Polygon> = new Set<Polygon>()) {
    if (!this.canEdit()) {
      return;
    }
    if (!moving.size && polygon.bufferParams && !polygon.bufferParams.ring && !polygon.isBuffer) {
      const set = this.bufferSetOf(polygon);
      const rings = this.polygons.filter(p => p.isHole && set.some(s => s.id === p.parentId)
        && p.bufferParams && p.bufferParams.ring);
      [...set, ...rings].forEach(p => p.bufferParams = null);
      const others = [...set, ...rings].filter(p => p !== polygon);
      if (others.length) {
        this.saveGeometry({addUndoCheckpoint: true, coalesce: true}, ...others);
      }
    }
    this.bufferWork++;
    try {
      this.moveByOffset(polygon, pivot, offsetX, offsetY, moveParent, moveChildren, moving);
    } finally {
      this.bufferWork--;
    }
  }

  private moveByOffset(polygon: Polygon, pivot: PolygonPoint, offsetX: number, offsetY: number, moveParent: boolean,
                       moveChildren: boolean, moving: Set<Polygon>) {
    if (!pivot) {
      Logger.error('PIVOT IS NULL');
      return;
    }
    moving.add(polygon);

    const pointsCount = polygon.getPointsCount();

    let moved = false;
    if (pointsCount > 0) {
      if (polygon.shape !== PolygonShape.CIRCLE) {
        let pivotId;
        if (!pivot) {
          pivot = polygon.getFirstPoint();
          pivotId = pivot.id;
        } else {
          pivotId = pivot.id;
        }

        polygon.points.forEach(ap => {
          const checkpoint = pivotId >= 0 && ap.id === pivotId;
          // if (!checkpoint) {
          // }
          ap.moveOffset(offsetX, offsetY);
        });

        // pivot.moveOffset(0, 0); TODO
        moved = true;
        // this.saveGeometry(true, polygon);
      } else {
        if (pointsCount === 1) {

        } else if (pointsCount > 1) {
          const first = polygon.getFirstPoint();
          const newCenter = {
            lat: first.lat + offsetX,
            lng: first.lng + offsetY
          };
          const second = first.next;
          first.move(newCenter);
          second.moveOffset(offsetX, offsetY);

          polygon.points = [];
          polygon.points.push(first);
          polygon.points.push(second);

          moved = true;
          // this.saveGeometry(true, first);
        }
      }
    }

    polygon.moveLabelBy(offsetX, offsetY);

    if (polygon.holes && moveChildren) {
      polygon.holes.forEach(hole => {
        if (hole === polygon || moving.has(hole)) {
          return;
        }
        if (hole.isHole) {
          this.movePolygonByOffset(hole, hole.getFirstPoint(), offsetX, offsetY, false, false, false, moving);
        } else if (polygon.isBuffer) {
          // A buffer's source, cut out of its band (see updateHoles), moves
          // with it -- and with the source go its own holes and its other
          // buffers. Moved alone, they stayed behind: a courtyard was left
          // outside its building, and a second buffer was measured round the
          // building where it no longer was.
          this.movePolygonByOffset(hole, hole.getFirstPoint(), offsetX, offsetY, false, true, true, moving);
        }
      });
    }
    // polygon.buffers.forEach(buffer => {
    //   if (buffer !== polygon) {
    //     this.movePolygonByOffset(buffer, buffer.getFirstPoint(), offsetX, offsetY, false);
    //   }
    // });

    polygon.update();
    polygon.updatePointIndexes();

    // The buffers drawn around this shape follow it (buffersOf).
    if (moveParent) {
      this.buffersOf(polygon).forEach(buffer => {
        // The buffer being dragged, which brought this source along, has moved.
        if (moving.has(buffer)) {
          return;
        }
        // With the holes in its band (see createBuffer), first, so that the
        // buffer is measured around them where they now are; its source is
        // the shape moving here.
        (buffer.holes || []).filter(hole => hole.isHole && hole !== polygon && hole !== buffer && !moving.has(hole))
          .forEach(hole => this.movePolygonByOffset(hole, hole.getFirstPoint(), offsetX, offsetY, false, false, false, moving));
        this.movePolygonByOffset(buffer, buffer.getFirstPoint(), offsetX, offsetY, false, false, false, moving);
      });
    }

      this.saveGeometry({addUndoCheckpoint: moved}, polygon);
  }

  /**
   * The buffers drawn round a shape, which follow it when it moves. Found by
   * their own parentId, so a shape with two buffers has both; older projects
   * also saved the link the other way round, on the source. Inner buffers
   * too, found by the shape they were drawn within: they are areas of their
   * own, with no parentId to find them by.
   */
  private buffersOf(polygon: Polygon): Set<Polygon> {
    const buffers = new Set<Polygon>(this.polygons.filter(p => p !== polygon
      && ((p.isBuffer && p.parentId === polygon.id)
        || (p.bufferParams && !p.bufferParams.ring && p.bufferParams.source === polygon.id))));
    if (polygon.parentId) {
      const legacy = this.geoIndex.get(polygon.parentId);
      if (legacy instanceof Polygon && legacy.isBuffer && legacy.parentId === polygon.id) {
        buffers.add(legacy);
      }
    }
    return buffers;
  }

  /**
   * Every shape a move of `polygon` takes along (movePolygonByOffset), itself
   * first, without moving anything: what the move preview shows. The same
   * walk as moveByOffset's, step for step -- its holes; for a buffer, the
   * shape it is drawn round, with that shape's holes and other buffers; its
   * own buffers, with the holes in their bands -- so the preview never shows
   * a shape staying behind that will move, or one moving that will not.
   */
  movingWith(polygon: Polygon): Polygon[] {
    const moving = new Set<Polygon>();
    const walk = (shape: Polygon, moveParent: boolean, moveChildren: boolean) => {
      moving.add(shape);
      if (shape.holes && moveChildren) {
        shape.holes.forEach(hole => {
          if (hole === shape || moving.has(hole)) {
            return;
          }
          if (hole.isHole) {
            walk(hole, false, false);
          } else if (shape.isBuffer) {
            walk(hole, true, true);
          }
        });
      }
      if (moveParent) {
        this.buffersOf(shape).forEach(buffer => {
          if (moving.has(buffer)) {
            return;
          }
          (buffer.holes || []).filter(hole => hole.isHole && hole !== shape && hole !== buffer && !moving.has(hole))
            .forEach(hole => walk(hole, false, false));
          walk(buffer, false, false);
        });
      }
    };
    walk(polygon, true, true);
    return [...moving];
  }

  // ── Buffers ────────────────────────────────────────────────────

  /**
   * Draws a buffer round `source` -- or, for an area, within it -- and
   * returns its pieces, largest first; none when the settings leave nothing
   * (an inner buffer wider than the area, say). With `save`, the pieces and
   * the holes they leave are one undo step.
   *
   * Round an area or along a line each piece is a buffer (isBuffer), and
   * round an area the area is cut out of it: a band. Within an area a piece
   * is an area of its own -- a setback, a buildable zone -- with nothing cut
   * out of it, so it is measured, exported and priced as an area.
   *
   * Each piece keeps how it was drawn (bufferParams): it is drawn again when
   * its shape changes (followBuffers) and can be edited (editBuffer). A
   * buffer used to be a copy that kept its shape however the original was
   * edited: drag a field's corner out and its 10 m band was 2 m wide there,
   * still priced as the band it no longer was.
   */
  createBuffer(source: Polygon, settings: BufferSettings, save: boolean = true): Polygon[] {
    if (!source || (!this.canEdit() && !this.isDemoMode())) {
      return [];
    }
    const clean = SyncService.bufferSettingsFor(source, settings);
    const pieces = BufferUtil.generate(source, clean);
    if (!pieces.length) {
      return [];
    }
    const made: Polygon[] = [];
    const rings: Polygon[] = [];
    let set = '';
    pieces.forEach((piece, part) => {
      const buffer = this.newBufferPiece(source, clean, part, pieces.length);
      set = set || buffer.id;
      buffer.bufferParams = {...clean, source: source.id, set, part};
      rings.push(...this.shapeBufferPiece(buffer, source, piece, []).rings);
      made.push(buffer);
    });
    if (save) {
      this.bufferWork++;
      try {
        // With their holes, as one step: undoing the buffer takes them too.
        this.saveGeometry({addUndoCheckpoint: true}, ...made, ...rings);
      } finally {
        this.bufferWork--;
      }
    }
    this.googleAnalyticsService.logEvent('canvas', 'create_buffer');
    return made;
  }

  /**
   * Draws a buffer again with new settings: all its pieces, in one undo step
   * that also removes the pieces and rings no longer needed. Returns the
   * pieces; none, leaving the buffer as it was, when its shape is gone or the
   * settings leave nothing.
   */
  editBuffer(piece: Polygon, settings: BufferSettings): Polygon[] {
    if (!piece || !piece.bufferParams || (!this.canEdit() && !this.isDemoMode())) {
      return [];
    }
    const source = this.bufferSourceOf(piece);
    if (!source) {
      return [];
    }
    this.bufferWork++;
    try {
      const redrawn = this.redrawBufferSet(source, this.bufferSetOf(piece), settings);
      if (!redrawn) {
        return [];
      }
      this.beginUndoGroup();
      try {
        const removed = this.dropBufferShapes(redrawn.spare);
        if (removed.length) {
          this.recordChanges(removed, UndoLabel.EDIT);
        }
        this.saveGeometry({addUndoCheckpoint: true, label: UndoLabel.EDIT}, ...redrawn.save);
      } finally {
        this.endUndoGroup();
      }
      return redrawn.save.filter(p => p.bufferParams && !p.bufferParams.ring);
    } finally {
      this.bufferWork--;
    }
  }

  /**
   * Lets a buffer go of the shape it was drawn round, for good: each piece
   * becomes an area like any other, which no longer follows the shape nor
   * has it cut out as the shape is now. Where a band had the shape cut out, a
   * hole with the shape's outline takes its place, so its area stays what it
   * was. All the pieces drawn with this one go too (one still following
   * would draw the others back), in one undo step. A piece edited by hand,
   * which already let go of its settings, goes alone. Returns the pieces.
   */
  detachBuffer(piece: Polygon): Polygon[] {
    if (!piece || (!this.canEdit() && !this.isDemoMode()) || !this.isAttachedBuffer(piece)) {
      return [];
    }
    const pieces = piece.bufferParams ? this.bufferSetOf(piece) : [piece];
    const save: Polygon[] = [];
    for (const p of pieces) {
      const source = p.isBuffer && p.parentId ? this.geoIndex.get(p.parentId) : null;
      const cut = source instanceof Polygon && source !== p && (p.holes || []).includes(source);
      const hole = cut ? this.cutOutHole(p, source as Polygon) : null;
      if (hole) {
        // The shape it is cut round is still there with its own label: a
        // second one on the same spot only covered it.
        hole.showName = false;
        hole.showArea = false;
        hole.showPerimeter = false;
      }
      const rings = this.polygons.filter(r => r.isHole && r !== p && r.parentId === p.id && r.bufferParams);
      // A corridor along a line was an area only as a buffer (Polygon.hasArea),
      // labelled as the line was; an area now, labelled as a new one is.
      if (p.isBuffer && !p.showSurface) {
        p.showSurface = true;
        p.showArea = !!this.settingsService.local.showPolygonAreaLabel;
        p.showPerimeter = !!this.settingsService.local.showPolygonPerimeterLabel;
      }
      p.isBuffer = false;
      p.parentId = '';
      p.bufferParams = null;
      rings.forEach(r => r.bufferParams = null);
      p.holes = [...(p.holes || []).filter(h => h !== source), ...(hole ? [hole] : [])];
      p.update();
      if (p.label) {
        p.updateLabel();
      }
      save.push(p, ...rings, ...(hole ? [hole] : []));
    }
    this.bufferWork++;
    try {
      this.saveGeometry({addUndoCheckpoint: true, label: UndoLabel.EDIT}, ...save);
    } finally {
      this.bufferWork--;
    }
    this.googleAnalyticsService.logEvent('canvas', 'detach_buffer');
    return pieces;
  }

  /** A piece of a buffer still tied to its shape: following it, or with the shape cut out of it. */
  isAttachedBuffer(piece: Polygon): boolean {
    if (!piece || piece.isHole) {
      return false;
    }
    return piece.bufferParams ? !piece.bufferParams.ring : piece.isBuffer && !!piece.parentId;
  }

  /** The shape a buffer was drawn round, while it is on the map. */
  bufferSourceOf(piece: Polygon): Polygon | null {
    const id = piece && piece.bufferParams && piece.bufferParams.source;
    const source = id ? this.geoIndex.get(id) : null;
    return source instanceof Polygon && source !== piece ? source : null;
  }

  /** The pieces drawn with this one, itself included, largest first. */
  bufferSetOf(piece: Polygon): Polygon[] {
    const params = piece && piece.bufferParams;
    if (!params || params.ring) {
      return [];
    }
    return this.polygons
      .filter(p => p.bufferParams && !p.bufferParams.ring && p.bufferParams.source === params.source
        && (p.bufferParams.set || p.id) === (params.set || piece.id))
      .sort((a, b) => a.bufferParams.part - b.bufferParams.part);
  }

  /**
   * The settings that mean something for this shape: within it only for an
   * area, sides and ends only for a line, a mitre limit only for sharp
   * corners.
   */
  static bufferSettingsFor(source: Polygon, settings: BufferSettings): BufferSettings {
    const clean: BufferSettings = {distance: Number(settings.distance), corner: settings.corner || 'round'};
    if (source.showSurface) {
      if (settings.inside) {
        clean.inside = true;
      }
    } else {
      clean.side = settings.side || 'both';
      if (clean.side === 'both') {
        clean.end = settings.end || 'round';
      }
    }
    if (clean.corner === 'sharp') {
      clean.mitreLimit = Number(settings.mitreLimit) >= 1 ? Number(settings.mitreLimit) : DEFAULT_MITRE_LIMIT;
    }
    return clean;
  }

  /** A new piece of a buffer round (or within) `source`, styled as it is; no outline yet. */
  private newBufferPiece(source: Polygon, settings: BufferSettings, part: number, total: number): Polygon {
    const inside = !!settings.inside;
    // An outline of its own, whatever the original was: a circle's points
    // are read as a centre and a radius, a rectangle's as four corners.
    const buffer = this.newPolygon(PolygonShape.POLYGON, inside || source.showSurface);
    // "Field 3 (buffer)", in the user's language: an English "-buffer"
    // went into lists, labels and exports whatever the language.
    const name = this.strings.getString(inside ? 'buffer_inner_name' : 'buffer_name', undefined, source.name);
    buffer.name = total > 1 ? this.strings.getString('buffer_piece_name', undefined, name, String(part + 1)) : name;
    buffer.isBuffer = !inside;
    buffer.parentId = inside ? '' : source.id;
    buffer.lineColor = source.lineColor;
    buffer.lineWidth = source.lineWidth;
    buffer.lineStyle = source.lineStyle;
    buffer.fillColor = source.fillColor;
    buffer.fillOpacity = source.fillOpacity;
    // Round a shape, its products, as a split or a duplicate keeps them: the
    // project's default product, which a new shape gets, priced a pipe's
    // corridor by area. Within an area, none: the ground is the area's, and
    // its products already price it.
    buffer.setProducts(inside ? [] : source.productIds);
    buffer.isLocked = true;
    // A round corner is dozens of short sides: a length on each of them
    // buried the map in "3.94 m" labels. The panel turns them on.
    buffer.showMiddleDistances = false;
    return buffer;
  }

  /**
   * Gives a piece its outline and the rings it leaves inside, reusing
   * `existing` rings first, and links its holes. Returns the rings in use and
   * the ones left over.
   */
  private shapeBufferPiece(buffer: Polygon, source: Polygon, piece: BufferRings, existing: Polygon[]):
      {rings: Polygon[], spare: Polygon[]} {
    // The buffer's own points (they were the original's).
    buffer.points = piece.outline.map(p => this.newPoint(buffer, p));
    // What the buffer closes round without covering -- the bay of a
    // C-shaped area, the yard inside a fence line that closes on itself, a
    // courtyard an inner buffer keeps clear of -- is a hole in it. Drawn into
    // its outline, that ring was joined to it by a seam, and the perimeter
    // counted the ring and the seam twice.
    const rings = piece.holes.map((ring, index) => {
      const hole = existing[index] || this.newPolygon(PolygonShape.POLYGON, buffer.showSurface);
      hole.isHole = true;
      hole.parentId = buffer.id;
      hole.name = buffer.name;
      hole.lineColor = buffer.lineColor;
      hole.lineWidth = buffer.lineWidth;
      hole.lineStyle = buffer.lineStyle;
      hole.fillColor = buffer.fillColor;
      hole.fillOpacity = buffer.fillOpacity;
      // A hole is never priced (see setHole).
      hole.productId = null;
      hole.showMiddleDistances = false;
      hole.isLocked = true;
      hole.bufferParams = {...buffer.bufferParams, ring: true};
      // Closed: the ring ends on a copy of its first point.
      hole.points = ring.map(p => this.newPoint(hole, p));
      hole.update();
      return hole;
    });
    // Round an area, the area is cut out of it; holes drawn into it by hand
    // stay; then its own rings.
    buffer.holes = [
      ...(buffer.isBuffer && this.isCutFromBuffer(source) ? [source] : []),
      ...this.polygons.filter(p => p.isHole && p !== buffer && p.parentId === buffer.id
        && !(p.bufferParams && p.bufferParams.ring)),
      ...rings,
    ];
    buffer.update();
    if (buffer.isBuffer) {
      const distance = buffer.bufferParams.distance;
      const along = source.showSurface ? null : SyncService.pointAlong(source.linePath || [], 0.25);
      if (along) {
        // A corridor's label on the corridor, a quarter of the way along the
        // line (half way is where the line's own label is) and set off to the
        // buffer's side: above the line's highest point it landed on whatever
        // was there (a pipe's corridor on the next field's name).
        const turn = buffer.bufferParams.side === 'right' ? 90 : -90;
        buffer.updateLabel(SphericalUtil.computeOffset(along.at, distance / 2, along.heading + turn));
      } else {
        // A band's label on the band, above the original's top: at the
        // centre it covered the original's own label.
        const outline = source.path && source.path.length ? source.path : source.points.map(p => p.latLng);
        const top = outline.reduce((a, b) => b.lat > a.lat ? b : a, outline[0]);
        if (top) {
          buffer.updateLabel(SphericalUtil.computeOffset(top, distance / 2, 0));
        }
      }
    }
    return {rings, spare: existing.slice(rings.length)};
  }

  /** The point `fraction` of the way along a line, and the line's heading there; null for one with no length. */
  private static pointAlong(path: google.maps.LatLngLiteral[], fraction: number):
      {at: google.maps.LatLngLiteral, heading: number} | null {
    const sides = path.slice(1).map((to, i) => ({from: path[i], to, length: SphericalUtil.computeDistanceBetween(path[i], to)}));
    let left = sides.reduce((sum, side) => sum + side.length, 0) * fraction;
    for (const side of sides) {
      if (side.length > 0 && left <= side.length) {
        const heading = SphericalUtil.computeHeading360(side.from, side.to);
        return {at: SphericalUtil.computeOffset(side.from, left, heading), heading};
      }
      left -= side.length;
    }
    return null;
  }

  /**
   * Draws a buffer's pieces again from its shape as it is now, with
   * `settings`: the pieces and rings there are, reused in order, and new ones
   * when it now has more. Returns what to save and what is left over to
   * remove; null, leaving it as it is, when the settings leave nothing now.
   */
  private redrawBufferSet(source: Polygon, members: Polygon[], settings: BufferSettings):
      {save: Polygon[], spare: Polygon[]} | null {
    if (!members.length) {
      return null;
    }
    const clean = SyncService.bufferSettingsFor(source, settings);
    const pieces = BufferUtil.generate(source, clean);
    if (!pieces.length) {
      return null;
    }
    const set = members[0].bufferParams.set || members[0].id;
    const save: Polygon[] = [];
    const spare: Polygon[] = [];
    pieces.forEach((piece, part) => {
      const buffer = members[part] || this.newBufferPiece(source, clean, part, pieces.length);
      // Round or within, as the settings now say.
      buffer.isBuffer = !clean.inside;
      buffer.parentId = clean.inside ? '' : source.id;
      buffer.bufferParams = {...clean, source: source.id, set, part};
      const existing = this.polygons.filter(p => p.isHole && p.parentId === buffer.id
        && p.bufferParams && p.bufferParams.ring);
      const shaped = this.shapeBufferPiece(buffer, source, piece, existing);
      save.push(buffer, ...shaped.rings);
      spare.push(...shaped.spare);
    });
    spare.push(...members.slice(pieces.length));
    return {save, spare};
  }

  /**
   * Takes pieces and rings a redrawn buffer no longer needs off the map, with
   * their holes. Returns the changes, for the caller's undo step.
   */
  private dropBufferShapes(spare: Polygon[]): DocChange[] {
    const docs = this.withHoles(spare.filter(p => this.geoIndex.has(p.id)));
    if (!docs.length) {
      return [];
    }
    const removed: DocChange[] = docs.map(geo => ({kind: 'geo' as MapDocKind, id: geo.id, before: this.snapshotOf(geo), after: null}));
    this.bufferWork++;
    try {
      return [...removed, ...this.removeGeometries(docs, {record: false})];
    } finally {
      this.bufferWork--;
    }
  }

  /**
   * A piece of a buffer, or a ring in one, deleted by hand: the rest of that
   * buffer lets go of its shape, as one edited by hand does. Still following
   * it, the next change of the shape drew the deleted piece back. Returns the
   * changes, for the delete's undo step (undoing it links them again).
   */
  private detachBufferSetsOf(removed: Array<Polygon | Spot>): DocChange[] {
    if (this.bufferWork > 0) {
      return [];
    }
    const removedIds = new Set(removed.map(geo => geo.id));
    const members = new Set<Polygon>();
    removed.forEach(geo => {
      if (!(geo instanceof Polygon) || !geo.bufferParams) {
        return;
      }
      const piece = geo.bufferParams.ring ? this.geoIndex.get(geo.parentId) : geo;
      const set = piece instanceof Polygon ? this.bufferSetOf(piece) : [];
      set.forEach(p => {
        members.add(p);
        this.polygons.filter(r => r.isHole && r.parentId === p.id && r.bufferParams && r.bufferParams.ring)
          .forEach(r => members.add(r));
      });
    });
    const changes: DocChange[] = [];
    members.forEach(p => {
      if (removedIds.has(p.id) || !this.geoIndex.has(p.id)) {
        return;
      }
      const before = this.snapshotOf(p);
      p.bufferParams = null;
      const after = this.snapshotOf(p);
      this.writeDoc('geo', p.id, after);
      changes.push({kind: 'geo', id: p.id, before, after});
    });
    return changes;
  }

  /**
   * Moving shapes or drawing buffers: a buffer saved meanwhile is not being
   * edited by hand, and its shape's save does not draw it again.
   */
  private bufferWork = 0;

  /**
   * The buffers drawn round the shapes being saved, drawn again from them as
   * they now are: what to save with them, and the changes removing what a
   * buffer no longer needs, both for the same undo step. A buffer edited by
   * hand lets go of its shape here, as it is saved (see Polygon.bufferParams).
   */
  private followBuffers(geometries: BaseGeometry[]): {save: Polygon[], removed: DocChange[]} {
    const result = {save: [] as Polygon[], removed: [] as DocChange[]};
    if (this.bufferWork > 0 || this.history.isExecuting || (!this.canEdit() && !this.isDemoMode())) {
      return result;
    }
    const linked = this.polygons.filter(p => p.bufferParams);
    if (!linked.length) {
      return result;
    }
    const saved = this.documentsOf(geometries).filter(g => g instanceof Polygon) as Polygon[];
    const savedIds = new Set(saved.map(p => p.id));
    const sources = new Set<Polygon>();
    saved.forEach(poly => {
      const params = poly.bufferParams;
      if (params && this.outlineChanged(poly)) {
        // Edited by hand: drawn again, the edit would be undone. The whole
        // buffer lets go, its other pieces and rings too: one piece drawn
        // again would put back the one edited.
        const pieceId = params.ring ? poly.parentId : poly.id;
        const piece = this.geoIndex.get(pieceId);
        const set = piece instanceof Polygon ? this.bufferSetOf(piece) : [];
        const members = new Set<Polygon>([poly, ...set]);
        set.forEach(p => this.polygons
          .filter(r => r.isHole && r.parentId === p.id && r.bufferParams && r.bufferParams.ring)
          .forEach(r => members.add(r)));
        members.forEach(p => {
          p.bufferParams = null;
          if (!savedIds.has(p.id)) {
            result.save.push(p);
          }
        });
        return;
      }
      if (poly.isHole && poly.parentId && this.outlineChanged(poly)) {
        // A setback is kept from the area's courtyards as from its boundary.
        const area = this.geoIndex.get(poly.parentId);
        if (area instanceof Polygon && linked.some(b => b.bufferParams && b.bufferParams.source === area.id
          && b.bufferParams.inside)) {
          sources.add(area);
        }
      }
      if (linked.some(b => b.bufferParams && b.bufferParams.source === poly.id) && this.outlineChanged(poly)) {
        sources.add(poly);
      }
    });
    if (!sources.size) {
      return result;
    }
    this.bufferWork++;
    try {
      sources.forEach(source => {
        const sets = new Map<string, Polygon[]>();
        this.polygons
          .filter(b => b.bufferParams && !b.bufferParams.ring && b.bufferParams.source === source.id)
          .forEach(b => {
            const key = b.bufferParams.set || b.id;
            sets.set(key, [...(sets.get(key) || []), b]);
          });
        sets.forEach(members => {
          members.sort((a, b) => a.bufferParams.part - b.bufferParams.part);
          const settings = members[0].bufferParams;
          // An inner buffer of a shape that is no longer an area means nothing:
          // left as it was drawn.
          if (settings.inside && !source.showSurface) {
            return;
          }
          const redrawn = this.redrawBufferSet(source, members, settings);
          if (redrawn) {
            result.save.push(...redrawn.save);
            result.removed.push(...this.dropBufferShapes(redrawn.spare));
          }
        });
      });
    } finally {
      this.bufferWork--;
    }
    return result;
  }

  /** Whether a shape's outline (or its kind) differs from the one last saved. */
  private outlineChanged(poly: Polygon): boolean {
    const cached = this.fireGeometries[poly.id];
    return !!cached && SyncService.outlineKey(cached) !== SyncService.outlineKey(poly.toFirebaseData(true));
  }

  /** A stored shape's outline and kind, as a string to compare. */
  private static outlineKey(data: any): string {
    const points = Object.values((data && data.points) || {}) as any[];
    points.sort((a, b) => (Number(a[PointField.KEY_INDEX]) || 0) - (Number(b[PointField.KEY_INDEX]) || 0));
    return `${data[PolygonField.KEY_SHAPE]}|${!!data[PolygonField.KEY_NO_SURFACE]}|`
      + points.map(p => `${Number(p[PointField.KEY_LAT]).toFixed(9)},${Number(p[PointField.KEY_LONG]).toFixed(9)}`).join(';');
  }

  clonePolygon(polygon: Polygon): Polygon {
    const clone = this.newPolygon(polygon.shape, polygon.showSurface);
    clone.name = polygon.name;
    clone.description = polygon.description;
    clone.lineWidth = polygon.lineWidth;
    clone.lineColor = polygon.lineColor;
    clone.lineStyle = polygon.lineStyle;
    clone.fillColor = polygon.fillColor;
    clone.fillOpacity = polygon.fillOpacity;
    clone.showSurface = polygon.showSurface;
    clone.isLocked = true;
    clone.showMiddleDistances = polygon.showMiddleDistances;
    clone.showPins = polygon.showPins;
    clone.showPinsWhenUnlocked = polygon.showPinsWhenUnlocked;
    clone.showName = polygon.showName;
    clone.showArea = polygon.showArea;
    clone.showPerimeter = polygon.showPerimeter;
    clone.showPriceLabel = polygon.showPriceLabel;
    clone.groupId = polygon.groupId;
    clone.isHole = polygon.isHole;
    clone.isBuffer = polygon.isBuffer;
    clone.setProducts(polygon.productIds);
    clone.textureRotation = polygon.textureRotation;
    clone.altitudeMode3d = polygon.altitudeMode3d;
    clone.altitude3d = polygon.altitude3d;
    clone.heightOffset3d = polygon.heightOffset3d;
    clone.extruded3d = polygon.extruded3d;
    // A duplicate keeps its grid, and both halves of a split keep one grid.
    clone.grid = polygon.grid ? {...polygon.grid} : null;
    if (clone.label && polygon.label) {
      clone.label.hasBackground = polygon.label.hasBackground;
    }
    // Its own list: sharing the original's meant removing a photo from the
    // copy removed it from the original too, and the next save of the
    // original lost it for good.
    clone.images = [...(polygon.images || [])];

    polygon.points.forEach(p => {
      const clonePoint = this.newPoint(clone, {lat: p.lat, lng: p.lng});
      // The corners' heights come along, so a copy of a shape traced in 3D is
      // not measured from the ground under a roof (moving a corner clears its
      // ground height again, see BasePoint.move).
      clonePoint.altitude = p.altitude;
      clonePoint.altitude3d = p.altitude3d;
      // A duplicate's corners are the original's, so their notes and photos
      // come too, each with its own list of photos as the shape has. A split
      // or a join puts new corners in place of these, and none of it with them.
      clonePoint.description = p.description;
      clonePoint.showInReport = p.showInReport;
      clonePoint.showLabelOnMap = p.showLabelOnMap;
      clonePoint.label.hasBackground = p.label.hasBackground;
      clonePoint.updateLabel(p.getDescriptionPosition());
      clonePoint.images = [...(p.images || [])];
      clone.points.push(clonePoint);
    });
    return clone;
  }

  /**
   * Build a fully-initialised hole polygon (label, defaults, parent's styling)
   * from a bare ring of points produced by a geometry op (split/join). Without
   * this the hole has no line/fill style and a broken details panel.
   */
  buildHole(srcPoints: Array<google.maps.LatLngLiteral>, parent: Polygon): Polygon {
    const hole = this.newPolygon(PolygonShape.POLYGON, parent.showSurface);
    hole.isHole = true;
    hole.parentId = parent.id;
    hole.name = parent.name;
    hole.lineColor = parent.lineColor;
    hole.lineWidth = parent.lineWidth;
    hole.lineStyle = parent.lineStyle;
    hole.fillColor = parent.fillColor;
    hole.fillOpacity = parent.fillOpacity;
    hole.points = srcPoints.map(p => this.newPoint(hole, {lat: p.lat, lng: p.lng}));
    this.lockPolygon(hole, hole.name, true, false);
    return hole;
  }

  deletePolygonPoint(point: PolygonPoint): Promise<void> {
    if (!this.canEdit()) {
      return Promise.reject();
    }
    if (point.parent.points.length === 1) {
      return Promise.reject();
    }
    return this.deleteGeometry(point, {addUndoCheckpoint: true});
  }

  checkIfPolygonContained(polygon: Polygon): Promise<Polygon> {
    // The smallest area around it, not the first one in the list: a courtyard
    // in a building in a field is the building's hole.
    return Promise.resolve(PolyUtil.findContainer(this.polygons, polygon));
  }

  setHole(parent: Polygon, poly: Polygon) {
    poly.isHole = true;
    poly.parentId = parent.id;
    // A hole is never priced: the project's default product, given to the
    // shape as it was drawn, only put "Estimate $0.00" on the map.
    poly.productId = null;
    this.updateHolesFor(parent);
  }

  updateSpotsElevation(spot: Spot): Promise<google.maps.ElevationResult[]> {
    return this.updateElevations(spot, this.spots);
  }

  updatePointsElevation(point: PolygonPoint, polygon: Polygon): Promise<google.maps.ElevationResult[]> {
    return this.updateElevations(point, polygon.points);
  }

  updateElevations(point: BasePoint, list: BasePoint[]): Promise<google.maps.ElevationResult[]> {
    if (!this.elevationService) {
      this.elevationService = new google.maps.ElevationService();
    }
    return new Promise<google.maps.ElevationResult[]>((resolve, reject) => {
      // The point asked about, and in the same request (up to 512) the others
      // still without a height, so their panels open with it.
      const selectedPoints: BasePoint[] = [point];
      for (const s of list) {
        if (selectedPoints.length >= 512) {
          break;
        }
        if (s !== point && (s.altitude === 0 || s.altitude === BasePointEnum.NO_FIRST_ALTITUDE)) {
          selectedPoints.push(s);
        }
      }
      // Where each point stood, at what height, when asked. The answer is
      // for that place: a point dragged away meanwhile (which clears its
      // height, BasePoint.move) used to take the ground height of where it
      // had been, and having one, was never looked up again. Nor does an
      // answer replace a height given meanwhile.
      const asked = selectedPoints.map(s => ({lat: s.latLng.lat, lng: s.latLng.lng, altitude: s.altitude}));
      const unchanged = (s: BasePoint, i: number) => s.latLng.lat === asked[i].lat && s.latLng.lng === asked[i].lng
        && (s.altitude === asked[i].altitude || (Number.isNaN(s.altitude) && Number.isNaN(asked[i].altitude)));
      this.elevationService.getElevationForLocations({
        locations: selectedPoints.map(s => new google.maps.LatLng(s.latLng))
      }, (results, status) => {
        Logger.log('RESULTS ', results);
        if (status === google.maps.ElevationStatus.OK && results && results.length === selectedPoints.length) {
          const filled = selectedPoints.filter((s, i) => {
            if (!unchanged(s, i)) {
              return false;
            }
            s.altitude = results[i].elevation;
            return true;
          });
          // A closed shape's closing point is its first corner again.
          new Set(filled.filter(s => s instanceof PolygonPoint && s.parent)
            .map(s => (s as PolygonPoint).parent)).forEach(polygon => this.mirrorClosingPoint(polygon));
          // Saved once, each shape or spot one document, where this map is
          // edited (or is the demo's own); a visitor keeps them for this
          // visit. A save per point serialised a 5,000-vertex shape 512 times
          // and froze the page.
          if (filled.length && this.canRecordUndo()) {
            this.saveGeometry({addUndoCheckpoint: false, background: true}, ...filled);
          }
          resolve(results);
        } else {
          reject(status);
        }
      });
    });
  }

  newGeoId(): string {
    return this.firebaseApi.newGeoId();
  }

  /**
   * The name a new shape or spot of this kind gets ("Polygon 12", "Spot 4"):
   * shown as the hint in the lock and new-spot dialogs, and used when nothing
   * is typed there.
   */
  defaultNameFor(geo: Polygon | Spot): string {
    if (geo instanceof Spot) {
      return nextDefaultName(this.strings.getString('spot'), this.spots.filter(s => s !== geo).map(s => s.name));
    }
    const kind = SyncService.shapeKind(geo.shape, geo.showSurface);
    return nextDefaultName(this.strings.getString(kind),
      this.polygons.filter(p => p !== geo && SyncService.shapeKind(p.shape, p.showSurface) === kind).map(p => p.name));
  }

  /** A name worth keeping: not empty and not only spaces. */
  static hasName(name: any): boolean {
    return typeof name === 'string' && name.trim() !== '';
  }

  /**
   * A shape's document with no points: a shape that does not exist. Shapes
   * are created with their first point; one with none is never shown, never
   * saved, and its stored document is deleted.
   */
  static isEmptyShapeDoc(data: any): boolean {
    if (!data || data.type !== GeometryType.TYPE_POLYGON) {
      return false;
    }
    const points = data.points;
    const count = Array.isArray(points) ? points.length
      : points && typeof points === 'object' ? Object.keys(points).length : 0;
    return count === 0 && !(Number(data[PolygonField.KEY_NUM_POINTS]) > 0);
  }

  /** How old an empty shape's document must be before a load deletes it: never one someone is making. */
  private static readonly EMPTY_SHAPE_GRACE_MS = 10 * 60 * 1000;

  newPolygon(shape: PolygonShape, showSurface: boolean) {
    const polygon = new Polygon(this.newGeoId(), shape);
    // "Polygon 1", in the user's language: it was "Polygon-0", English, from
    // zero, and a line was a "Polygon" too.
    // Each kind numbered on its own: the first line after three polygons
    // is "Polyline 1", not "Polyline 4".
    const kind = SyncService.shapeKind(shape, showSurface);
    polygon.name = nextDefaultName(this.strings.getString(kind),
      this.polygons.filter(p => SyncService.shapeKind(p.shape, p.showSurface) === kind).map(p => p.name));
    polygon.showSurface = showSurface;
    this.setPolygonDefaultValues(polygon);
    this.geoEpoch.set(polygon, this.loadEpoch);

    return polygon;
  }

  /** Which kind a new shape is named after (the strings key). */
  private static shapeKind(shape: PolygonShape, showSurface: boolean): string {
    return shape === PolygonShape.CIRCLE ? 'circle'
      : shape === PolygonShape.RECTANGLE ? 'rectangle'
      : showSurface !== false ? 'polygon' : 'polyline';
  }

  setPolygonDefaultValues(polygon: Polygon) {
    polygon.fillColor = this.settings.fillColor;
    polygon.fillOpacity = 1 - this.settings.fillOpacity;
    polygon.lineWidth = this.lineWidthFor(polygon);
    polygon.lineColor = this.settings.lineColor;
    polygon.lineStyle = this.settings.lineStyle;
    polygon.showArea = polygon.showSurface && this.settingsService.local.showPolygonAreaLabel;
    polygon.showPerimeter = !polygon.showSurface || this.settingsService.local.showPolygonPerimeterLabel;
    polygon.showName = this.settingsService.local.showPolygonNameLabel;
    polygon.showPins = false;
    polygon.showMiddleDistances = this.settings.showDistanceLabel;
    polygon.label.hasBackground = this.settingsService.local.showPolygonTitleLabelBackground
    polygon.lastUserUpdateId = this.authService.getUserId;
    // Not a default the catalogue does not hold (an .mmp from another account
    // used to set one, see applyImportedProjectSettings): it priced nothing
    // and the picker showed no product.
    const defaultProductId = this.currentProject && this.currentProject.default_product_id;
    const catalogue = defaultProductId ? this.loadedCatalogue() : null;
    // Nor one archived or hidden since: no picker offers it any more.
    const offered = (p: any) => !!p && !p.archived && p.enabled !== false;
    if (defaultProductId && (!catalogue || offered(catalogue[defaultProductId]))) {
      polygon.productId = defaultProductId;
    }
  }

  /**
   * The settings' line width, for this shape. A line 0 wide is not drawn at
   * all, and a 0 saved before the width slider started at 1 is still in some
   * people's settings; an area keeps it, as its fill still shows.
   */
  private lineWidthFor(polygon: Polygon): number {
    const width = this.settings.lineWidth;
    return polygon.showSurface ? width : Math.max(1, Number(width) || 0);
  }

  newPoint(polygon: Polygon, latLng: google.maps.LatLngLiteral, altitude: number = 0.0): PolygonPoint {
    const point = new PolygonPoint(this.newGeoId(), polygon, latLng);
    if (altitude) {
      point.altitude = altitude;
    } else {
      const picked = this.takePickedPlace(latLng);
      point.altitude = picked.altitude;
      // Snapped onto a corner in 3D: drawn where that corner is (see holdPickedAltitude).
      if (picked.lift) {
        point.altitude3d = picked.lift;
      }
    }
    return point;
  }

  // ── Heights for 3D measuring ─────────────────────────────────────

  /**
   * The surface height under a 3D click, held for the point that click is
   * about to add (see Canvas.on3dMapClick). The flat drawing path takes only
   * a lat/lng, so the height waits here; it is taken only by a point at
   * exactly the clicked position, so a snapped point does not inherit it.
   */
  private pickedAltitude: google.maps.LatLngAltitudeLiteral | null = null;
  /** The own height (altitude3d) held with it: a corner snapped onto another one is drawn where that one is. */
  private pickedLift = 0;

  /**
   * `lift`: the own height the point takes, when it was snapped onto another
   * corner in 3D (canvas/utils/snap-3d.ts) -- iOS gives it the corner's too.
   * Its surface height alone left a corner snapped onto a raised one drawn
   * below it.
   */
  holdPickedAltitude(position: google.maps.LatLngAltitudeLiteral | null, lift = 0) {
    const hasAltitude = !!position && isFinite(position.altitude) && position.altitude !== 0;
    const hasLift = !!position && Number.isFinite(lift) && lift !== 0;
    this.pickedAltitude = hasAltitude || hasLift ? position : null;
    this.pickedLift = hasLift ? lift : 0;
  }

  /** The held height, once, for a point at exactly its position (see newPoint, and a move in 3D). */
  takePickedAltitude(latLng: google.maps.LatLngLiteral): number {
    return this.takePickedPlace(latLng).altitude;
  }

  /** The held height and own height, once, for a point at exactly its position. */
  takePickedPlace(latLng: google.maps.LatLngLiteral): {altitude: number, lift: number} {
    const picked = this.pickedAltitude;
    const lift = this.pickedLift;
    this.pickedAltitude = null;
    this.pickedLift = 0;
    const here = !!picked && picked.lat === latLng.lat && picked.lng === latLng.lng;
    return here ? {altitude: isFinite(picked.altitude) ? picked.altitude || 0 : 0, lift} : {altitude: 0, lift: 0};
  }

  /** Corners already looked up this session (id at position), so a sync does not ask twice. */
  private heightsAsked = new Set<string>();

  /** Gives every corner of one shape a height; resolves true when it has them all. */
  fillHeights(polygon: Polygon): Promise<boolean> {
    return this.fillMissingHeights([polygon], true).then(() => canMeasure3d(polygon) && polygon.points.every(hasHeight));
  }

  /**
   * Looks up the corners' heights that 3D measuring needs: the ground
   * elevation from the Elevation API for each corner without one. Corners
   * drawn in the 3D view already carry the height of what was clicked.
   *
   * Batched 512 corners to a request across shapes, rather than one request
   * per shape, and saved, so a project is looked up once -- unless a corner
   * moves, which clears its height (BasePoint.move). Resolves true when any
   * height was filled in.
   */
  fillMissingHeights(polygons: Polygon[], retry = false): Promise<boolean> {
    const keyOf = (p: BasePoint) => `${p.id}@${p.latLng.lat},${p.latLng.lng}`;
    const epoch = this.loadEpoch;
    const measurable = polygons.filter(canMeasure3d);
    // A closed shape's closing point is its first corner again: it takes that
    // corner's heights rather than a lookup of its own, which gave it the
    // ground's under a corner clicked on a roof.
    const mirrored = measurable.filter(polygon => this.mirrorClosingPoint(polygon));
    // A shape placed in 3D stands where its corners were put, not on the
    // ground under them: a corner of one that lost its height (moved by
    // itself) takes the plane through the others instead of a lookup, which
    // dropped it to the street under a roof. One with no heights at all is
    // looked up as any other.
    const placed = measurable.filter(polygon => polygon.altitudeMode3d === AltitudeMode3d.PLACED
      && polygon.points.some(hasHeight));
    const planed = placed.filter(polygon => this.fillFromPlane(polygon));
    mirrored.push(...planed.filter(polygon => !mirrored.includes(polygon)));
    planed.forEach(polygon => this.mirrorClosingPoint(polygon));
    const wanted: Array<{polygon: Polygon, point: BasePoint, key: string}> = [];
    measurable.filter(polygon => !placed.includes(polygon)).forEach(polygon => polygon.points.forEach((point, i) => {
      const key = keyOf(point);
      if (!this.isClosingPoint(polygon, i) && !hasHeight(point) && (retry || !this.heightsAsked.has(key))) {
        wanted.push({polygon, point, key});
      }
    }));
    if (wanted.length === 0) {
      return Promise.resolve(this.keepHeights(mirrored));
    }
    wanted.forEach(w => this.heightsAsked.add(w.key));
    if (!this.elevationService) {
      this.elevationService = new google.maps.ElevationService();
    }
    const batches: Array<typeof wanted> = [];
    for (let i = 0; i < wanted.length; i += 512) {
      batches.push(wanted.slice(i, i + 512));
    }
    return Promise.all(batches.map(batch => new Promise<Polygon[]>(resolve => {
      this.elevationService.getElevationForLocations({
        locations: batch.map(w => new google.maps.LatLng(w.point.latLng)),
      }, (results, status) => {
        if (status !== google.maps.ElevationStatus.OK || !results || results.length !== batch.length) {
          Logger.error('Corner heights unavailable', status);
          resolve([]);
          return;
        }
        // Each answer is for where the corner stood when asked. One dragged
        // away meanwhile (which cleared its height, BasePoint.move) used to
        // take the ground height of where it had been, and having one, was
        // never looked up again: it is left to be looked up where it now is,
        // and where it was is asked again should it go back. One given a
        // height meanwhile keeps it.
        const filled = batch.filter((w, i) => {
          if (keyOf(w.point) !== w.key) {
            this.heightsAsked.delete(w.key);
            return false;
          }
          if (hasHeight(w.point)) {
            return false;
          }
          // A spot exactly at sea level would read as "not looked up" (0), so
          // it is nudged by a millimetre.
          w.point.altitude = results[i].elevation || 0.001;
          return true;
        });
        resolve(filled.map(w => w.polygon));
      });
    }))).then(lists => {
      // Another project was opened, or this one reloaded, while the heights
      // were on their way: these shapes have left the map.
      if (epoch !== this.loadEpoch) {
        return false;
      }
      const looked = [...new Set(lists.flat())];
      looked.forEach(polygon => this.mirrorClosingPoint(polygon));
      return this.keepHeights([...new Set([...mirrored, ...looked])]);
    });
  }

  /**
   * Gives a placed shape's corners without a height one from the others: on
   * the plane through them when there are three or more not in a line, else
   * their mean. True when any was given.
   */
  private fillFromPlane(polygon: Polygon): boolean {
    const corners = polygon.points.filter((_, i) => !this.isClosingPoint(polygon, i));
    const missing = corners.filter(point => !hasHeight(point));
    const known = corners.filter(hasHeight);
    if (!missing.length || !known.length) {
      return false;
    }
    const origin = known[0].latLng;
    const sample = (point: BasePoint) => ({...toLocalMetres(origin, point.latLng), h: point.altitude});
    const plane = known.length >= 3 ? fitPlane(known.map(sample)) : null;
    const mean = known.reduce((sum, point) => sum + point.altitude, 0) / known.length;
    missing.forEach(point => {
      const at = toLocalMetres(origin, point.latLng);
      // Never exactly 0, which reads as "not looked up".
      point.altitude = (plane ? plane(at.x, at.y) : mean) || 0.001;
    });
    return true;
  }

  /** Keeps heights just filled in; true when there were any. */
  private keepHeights(touched: Polygon[]): boolean {
    if (touched.length === 0) {
      return false;
    }
    // Only someone who may edit the project keeps the lookup. Only the
    // corners it filled are written, and the shapes stay whoever's they were:
    // this runs for every shape on the 3D map, other people's half-drawn
    // ones included.
    if (this.canEdit()) {
      this.saveGeometry({addUndoCheckpoint: false, background: true}, ...touched)
        .catch(err => Logger.error('Saving corner heights failed', err));
    }
    this.updateMeasurementBox.emit();
    return true;
  }

  /** Whether the point at `index` is a closed shape's closing point: its first corner repeated. */
  private isClosingPoint(polygon: Polygon, index: number): boolean {
    return index > 0 && index === polygon.points.length - 1 && polygon.isClosed();
  }

  /**
   * Gives a closed shape's closing point its first corner's heights; true when
   * they differed. It is that corner again, so it must never stand at another
   * height: the closing side would climb or drop to it, and the 3D area and
   * slope would be fitted through a corner that is not there.
   */
  private mirrorClosingPoint(polygon: Polygon): boolean {
    const last = polygon.points.length - 1;
    if (!this.isClosingPoint(polygon, last)) {
      return false;
    }
    const first = polygon.points[0];
    const closing = polygon.points[last];
    if (closing.altitude === first.altitude && closing.altitude3d === first.altitude3d) {
      return false;
    }
    closing.altitude = first.altitude;
    closing.altitude3d = first.altitude3d;
    return true;
  }

  newSpot(): Spot {
    const spot = new Spot(this.newGeoId());
    spot.name = nextDefaultName(this.strings.getString('spot'), this.spots.map(s => s.name));
    spot.iconId = this.settingsService.local.defaultSpotIconId;
    spot.iconColor = this.settingsService.local.defaultSpotIconColor;
    spot.descriptionLabelSize = this.settings.descriptionLabelSize;
    spot.showLabelOnMap = this.settingsService.local.showSpotNameLabel;
    spot.showLabelBackground = this.settingsService.local.showSpotNameLabelBackground;
    this.geoEpoch.set(spot, this.loadEpoch);
    return spot;
  }

  /**
   * Saves many shapes at once -- what an import brings in -- as one step unless
   * `record` is false, and sends them straight away rather than after the
   * usual pause.
   */
  saveGeometries(geoList: BaseGeometry[], options: {record?: boolean, label?: UndoLabel} = {}) {
    if (!this.canEdit()) {
      return;
    }
    const changes: DocChange[] = [];
    for (const geo of geoList) {
      if (!geo.id || (geo.type !== GeometryType.TYPE_POLYGON && geo.type !== GeometryType.TYPE_SPOT)) {
        continue;
      }
      const before = this.fireGeometries[geo.id] ? withoutBookkeeping(this.fireGeometries[geo.id]) : null;
      const after = withoutBookkeeping(geo.toFirebaseData(true));
      this.removedIds.delete(geo.id);
      this.writeDoc('geo', geo.id, after);
      if (options.record) {
        changes.push({kind: 'geo', id: geo.id, before, after});
      }
    }
    this.recordChanges(changes, options.label || UndoLabel.IMPORT);
    this.flushWrites();
  }

  /**
   * Listens for other people's changes to the shapes, groups and layers.
   *
   * View-only visitors of a shared link still read the project once; the
   * people who work on it, and embeds, follow it live -- including an
   * organisation Viewer, who cannot edit but should not be looking at a map
   * someone cleared an hour ago.
   *
   * Enabled once per load of a project (finishLoadingGeometries), for that
   * project only; disabled when it is left or loaded again.
   */
  setDataEventsEnabled(enabled: boolean) {
    this.logService.log(`setDataEventsEnabled ${enabled}`);
    clearTimeout(this.listenerRetryTimer);
    this.listenerRetryTimer = null;
    this.listenerFailures = 0;
    this.listenFor = enabled && this.currentProject ? this.currentProject.project_id : null;
    this.updateDataEventsListeners();
  }

  /**
   * Starts or stops the listeners to match whether this session may follow
   * the project now, which its document changing (members, sharing, the bin)
   * can change. Listeners already running for it are left alone: every
   * presence or session change used to restart all three, and each restart
   * read every shape, group and layer of the project again, in every session.
   */
  updateDataEventsListeners() {
    const project = this.currentProject;
    // Not the demo, whose map is in this browser only: there is no project
    // to follow, and the refused listeners ended in "Live updates stopped".
    const listen = !!project && project.project_id === this.listenFor && !this.isDemoMode()
      && (this.canEdit() || CommonUtil.inIframe() || this.isProjectMember());
    if (listen && this.listeningTo === project.project_id) {
      return;
    }
    this.stopDataListeners();
    if (listen) {
      this.startDataListeners(project.project_id);
    }
  }

  private startDataListeners(projectId: string) {
    this.logService.log(`startDataListeners ${projectId}`);
    const run = ++this.listenerRun;
    this.listeningTo = projectId;
    // Imports into the project, running or just done, for everyone on it.
    this.importJobs.watch(projectId);
    this.dataListenerPrimed = false;
    this.groupsListenerPrimed = false;
    this.layersListenerPrimed = false;
    const failed = (error: any) => this.onDataListenerFailed(run, projectId, error);
    this.unsubscribeDataListener = this.firebaseApi.getProjectDataRef(projectId).ref
      .onSnapshot(snapshot => this.onDataSnapshot(snapshot), failed);
    this.unsubscribeGroupsListener = this.firebaseApi.getProjectDataGroupsRef(projectId).ref
      .onSnapshot(snapshot => this.onGroupsSnapshot(snapshot), failed);
    this.unsubscribeLayersListener = this.firebaseApi.getProjectMapLayersRef(projectId).ref
      .onSnapshot(snapshot => this.onLayersSnapshot(snapshot), failed);
  }

  /**
   * Keeps a project's shapes and groups listened to while a load reads them,
   * until the listeners finishLoadingGeometries starts have taken over; the
   * function returned lets go (and may be called more than once).
   *
   * Firestore answers a read of a query it is already listening to from that
   * listen, and a listener joining one starts from what it holds. Read on its
   * own, every shape was downloaded -- and billed -- twice on each open: once
   * by the load, and again in full by the listener's first snapshot, as there
   * is no persistent cache to keep the first read for it. The layers need no
   * hold: they are read after the listeners have started.
   */
  private holdProjectReads(projectId: string): () => void {
    const ignore = () => undefined;
    const unsubscribes = [
      this.firebaseApi.getProjectDataRef(projectId).ref.onSnapshot(ignore, ignore),
      this.firebaseApi.getProjectDataGroupsRef(projectId).ref.onSnapshot(ignore, ignore),
    ];
    return () => unsubscribes.splice(0).forEach(unsubscribe => unsubscribe());
  }

  private stopDataListeners() {
    if (this.unsubscribeDataListener) {
      this.unsubscribeDataListener();
      this.unsubscribeDataListener = null;
    }
    if (this.unsubscribeGroupsListener) {
      this.unsubscribeGroupsListener();
      this.unsubscribeGroupsListener = null;
    }
    if (this.unsubscribeLayersListener) {
      this.unsubscribeLayersListener();
      this.unsubscribeLayersListener = null;
    }
    this.listeningTo = null;
    this.importJobs.watch(null);
  }

  /**
   * A listener that fails (resource-exhausted, internal, permission-denied) is
   * ended by Firestore, and this session stopped hearing about other people's
   * changes without a word -- while still saving on top of what it last saw.
   * All three are started again, a little later each time; the first snapshot
   * after a restart catches up on what was missed. When they keep failing,
   * they are left off and the user is told.
   */
  private onDataListenerFailed(run: number, projectId: string, error: any) {
    Logger.error('Project data listener stopped', projectId, error);
    // Another of the three already reported it, or these listeners have been replaced.
    if (run !== this.listenerRun || this.listeningTo !== projectId) {
      return;
    }
    this.stopDataListeners();
    const now = Date.now();
    if (now - this.lastListenerFailure > LISTENER_FAILURE_RESET_MS) {
      this.listenerFailures = 0;
    }
    this.lastListenerFailure = now;
    this.listenerFailures++;
    clearTimeout(this.listenerRetryTimer);
    this.listenerRetryTimer = null;
    if (this.listenerFailures > LISTENER_RETRY_LIMIT) {
      this.listenFor = null;
      // Firestore may report this from outside Angular's zone.
      this.ngZone.run(() => openSnack(this.snackBar, this.strings.getString('live_updates_stopped'),
        this.strings.getString('dismiss'), ERROR_SNACK_DURATION, 'error'));
      return;
    }
    this.listenerRetryTimer = setTimeout(() => {
      this.listenerRetryTimer = null;
      this.updateDataEventsListeners();
    }, LISTENER_RETRY_BASE_MS * 2 ** (this.listenerFailures - 1));
  }

  /** A document this session wrote itself, coming back from Firestore. */
  private isOwnEcho(data: any): boolean {
    return !!this.currentSessionId && !!data && data[PolygonField.KEY_LAST_UPDATE_SESSION_ID] === this.currentSessionId;
  }

  /**
   * Shape changes from Firestore, applied in one pass.
   *
   * A listener starting (after a load, or again after it failed) delivers
   * every document as "added". Those used to be re-applied one by one (every
   * shape reloaded, the list rebuilt per shape) and, when the project had been
   * empty, the first real addition was dropped. Now the first snapshot of a
   * listener only brings in what this session does not already hold --
   * including shapes deleted while it was not listening. A first snapshot
   * served from the local cache does not count as the first: it may hold only
   * part of the project, and the server's that follows is the full picture.
   *
   * A removal is applied to the map only. It used to go through deleteGeometry,
   * which deleted the document again: every open copy of a project echoed every
   * deletion back, late enough to undo a quick Undo.
   */
  private onDataSnapshot(snapshot: firebase.firestore.QuerySnapshot) {
    const first = !this.dataListenerPrimed;
    this.dataListenerPrimed = this.dataListenerPrimed || !snapshot.metadata.fromCache;
    const now = Date.now();
    const targets: DocTarget[] = [];
    const rebased: string[] = [];
    snapshot.docChanges().forEach(change => {
      const id = change.doc.id;
      if (change.type === 'removed') {
        // Taken back with a batch the server refused, and on its way again:
        // not a deletion (see resentWrites).
        if (this.isBeingResent(id)) {
          return;
        }
        if (this.fireGeometries[id] || this.geoIndex.has(id)) {
          const geo = this.geoIndex.get(id);
          this.rememberRemoved('geo', id, geo ? this.snapshotOf(geo) : withoutBookkeeping(this.fireGeometries[id]));
          delete this.fireGeometries[id];
          this.remoteChangedAt.set(id, now);
          // Unsaved edits to a shape someone else deleted do not bring it back.
          this.writeQueue.delete(`geo:${id}`);
          targets.push({kind: 'geo', id, data: null});
        }
        return;
      }
      const data = change.doc.data();
      const cached = this.fireGeometries[id];
      // A restarted listener delivers every document again. One someone else
      // saved last, still at the version received, is passed over unread;
      // every save moves the time (see writeDoc). A version this session saved
      // can hold other people's fields too, now that saves write only theirs.
      if (first && cached && !this.isOwnEcho(data)
        && cached[PolygonField.KEY_LAST_UPDATE_TIMESTAMP] === data[PolygonField.KEY_LAST_UPDATE_TIMESTAMP]
        && cached[PolygonField.KEY_LAST_UPDATE_SESSION_ID] === data[PolygonField.KEY_LAST_UPDATE_SESSION_ID]) {
        return;
      }
      let next = data;
      const queued = this.writeQueue.get(`geo:${id}`);
      if (queued) {
        // A delete or a whole new document waiting to go replaces this one on
        // the server, so showing this one here would only flicker.
        if (!queued.data || !queued.fields) {
          return;
        }
        // Edits waiting to be sent are written field by field, so they go on
        // top of this version rather than in place of it. A rename arriving
        // during a drag used to be dropped here, and then overwritten.
        next = this.withUnsentChanges(data, queued);
      }
      // This session's own save coming back, or nothing new.
      if (cached && docsEqual(cached, next)) {
        return;
      }
      this.fireGeometries[id] = next;
      this.remoteChangedAt.set(id, now);
      targets.push({kind: 'geo', id, data: next});
      if (queued) {
        rebased.push(id);
      }
    });
    // Removed while this listener was not running. Only trusted from the server:
    // a snapshot served from the local cache may simply not hold everything.
    if (first && !snapshot.metadata.fromCache) {
      const present = new Set(snapshot.docs.map(doc => doc.id));
      this.geoIndex.forEach((geo, id) => {
        if (!present.has(id) && !this.writeQueue.has(`geo:${id}`)) {
          delete this.fireGeometries[id];
          targets.push({kind: 'geo', id, data: null});
        }
      });
    }
    if (targets.length) {
      this.geometriesChanged = true;
      this.applyDocStates(targets, {write: false, remote: true});
      // A shape put together from both is saved as it now stands: its area
      // and vertex numbering follow the merged vertices.
      rebased.forEach(id => {
        const geo = this.geoIndex.get(id);
        if (geo) {
          this.writeDoc('geo', id, this.snapshotOf(geo), {quiet: true});
        }
      });
    }
    this.updateMeasurementBox.emit();
  }

  /**
   * A shape as the server has it, with this session's edits that are still
   * waiting to be sent put back on top: what it will be once they are. A
   * vertex changed here but deleted by someone else stays deleted, and is
   * taken out of the waiting write so that sending it does not bring it back.
   */
  private withUnsentChanges(remote: any, write: QueuedWrite): any {
    const local = write.data;
    const merged = {...remote};
    const fields = this.fieldsOf(write);
    const byVertex = !fields.has(POINTS_FIELD) && write.vertices.size > 0;
    if (byVertex && !isPlainMap(remote[POINTS_FIELD])) {
      // Not stored one entry per vertex: this session's vertices go whole.
      write.fields.add(POINTS_FIELD);
      fields.add(POINTS_FIELD);
    }
    fields.forEach(field => {
      if (local[field] === undefined) {
        delete merged[field];
      } else {
        merged[field] = local[field];
      }
    });
    if (!fields.has(POINTS_FIELD) && write.vertices.size > 0) {
      const mine = isPlainMap(local[POINTS_FIELD]) ? local[POINTS_FIELD] : {};
      const points = {...remote[POINTS_FIELD]};
      [...write.vertices].forEach(id => {
        const attributes = write.vertexFields?.get(id);
        if (mine[id] === undefined) {
          delete points[id];
        } else if (attributes && isWholeVertex(points[id]) && isPlainMap(mine[id])) {
          // Only what this session changed goes on top. The rest -- the
          // vertex's number in the outline among it, which someone else's
          // insert or delete may have moved -- stays as the server has it.
          const vertex = {...points[id]};
          attributes.forEach(attribute => {
            if (mine[id][attribute] === undefined) {
              delete vertex[attribute];
            } else {
              vertex[attribute] = mine[id][attribute];
            }
          });
          points[id] = vertex;
        } else if (!attributes && (points[id] !== undefined || write.addedVertices.has(id))) {
          points[id] = mine[id];
        } else {
          write.vertices.delete(id);
          write.vertexFields?.delete(id);
        }
      });
      merged[POINTS_FIELD] = points;
    }
    return merged;
  }

  private onGroupsSnapshot(snapshot: firebase.firestore.QuerySnapshot) {
    const first = !this.groupsListenerPrimed;
    // As for the shapes (onDataSnapshot): a first snapshot from the local
    // cache may hold only part of the project.
    this.groupsListenerPrimed = this.groupsListenerPrimed || !snapshot.metadata.fromCache;
    const now = Date.now();
    const targets: DocTarget[] = [];
    snapshot.docChanges().forEach(change => {
      const id = change.doc.id;
      if (change.type === 'removed') {
        if (this.dataGroups[id]) {
          this.rememberRemoved('group', id, withoutBookkeeping(this.dataGroups[id]));
          this.remoteChangedAt.set(id, now);
          targets.push({kind: 'group', id, data: null});
        }
        return;
      }
      const data = change.doc.data();
      if ((this.dataGroups[id] && this.isOwnEcho(data)) || docsEqual(this.dataGroups[id], data)) {
        return;
      }
      // A first snapshot is the project's groups (the load read them too),
      // not somebody's change.
      if (!first) {
        this.remoteChangedAt.set(id, now);
      }
      targets.push({kind: 'group', id, data});
    });
    // Groups the load read (or a stopped listener knew) that were deleted
    // before this listener started. Only trusted from the server, as above.
    if (first && !snapshot.metadata.fromCache) {
      const present = new Set(snapshot.docs.map(doc => doc.id));
      Object.keys(this.dataGroups).forEach(id => {
        if (!present.has(id) && !this.writeQueue.has(`group:${id}`)) {
          targets.push({kind: 'group', id, data: null});
        }
      });
    }
    this.applyDocStates(targets, {write: false, remote: true});
  }

  /**
   * Map layers were read once when the project opened, so a layer someone else
   * added, cleared or restored never appeared until a reload. The initial load
   * (loadMapLayers) and this listener may both announce the same layers;
   * addMapLayer puts each on the map once.
   */
  private onLayersSnapshot(snapshot: firebase.firestore.QuerySnapshot) {
    const first = !this.layersListenerPrimed;
    // As for the shapes (onDataSnapshot): a first snapshot from the local
    // cache may hold only part of the project.
    this.layersListenerPrimed = this.layersListenerPrimed || !snapshot.metadata.fromCache;
    const now = Date.now();
    const targets: DocTarget[] = [];
    snapshot.docChanges().forEach(change => {
      const id = change.doc.id;
      const known = this.layers.some(layer => layer.id === id);
      if (change.type === 'removed') {
        if (known) {
          this.rememberRemoved('layer', id, this.layerSnapshot(id));
        }
        this.layerDocs.delete(id);
        if (known) {
          this.remoteChangedAt.set(id, now);
          targets.push({kind: 'layer', id, data: null});
        }
        return;
      }
      const data = {...change.doc.data(), id};
      if (known && (this.isOwnEcho(data) || docsEqual(this.layerDocs.get(id), data))) {
        this.layerDocs.set(id, data);
        return;
      }
      this.layerDocs.set(id, data);
      if (!first) {
        this.remoteChangedAt.set(id, now);
      }
      targets.push({kind: 'layer', id, data});
    });
    // Layers the load read (or a stopped listener knew) that were deleted
    // before this listener started: a listener started again after failing
    // hears nothing of what was deleted while it was down, so the layer
    // stayed drawn here, and a change to it wrote it back for everyone. Only
    // trusted from the server, as above.
    if (first && !snapshot.metadata.fromCache) {
      const present = new Set(snapshot.docs.map(doc => doc.id));
      const held = new Set([...this.layers.map(layer => layer.id), ...this.loadingLayers.keys()]);
      held.forEach(id => {
        if (id && !present.has(id) && !this.writeQueue.has(`layer:${id}`)) {
          this.layerDocs.delete(id);
          targets.push({kind: 'layer', id, data: null});
        }
      });
    }
    this.applyDocStates(targets, {write: false, remote: true});
  }

  /**
   * Saves shapes, spots or vertices (a vertex is saved as its shape).
   *
   * With `addUndoCheckpoint` the change becomes part of an undo step. The step
   * records the whole shape, before and after, so everything the edit moved is
   * covered: setting a vertex's angle moves the next vertex, and a rectangle
   * keeps its sides square by moving two. Recording the vertex alone meant
   * undoing those did nothing.
   */
  saveGeometry(options: SaveOptions, ...geometries: BaseGeometry[]): Promise<void> {
    // Tell the texture layer before the save round trip, not after: the point
    // of the notification is that the paint follows the drag.
    this.onAppearanceChanged.next();
    // The buffers drawn round these shapes follow them, in this same save and
    // undo step (see createBuffer).
    const followed = this.followBuffers(geometries);
    const changes: DocChange[] = options.addUndoCheckpoint ? [...followed.removed] : [];
    const followedIds = new Set([...followed.save.map(p => p.id), ...followed.removed.map(c => c.id)]);
    let listChanged = followed.removed.length > 0;
    for (const geo of this.documentsOf([...geometries, ...followed.save])) {
      const onMap = this.geoIndex.get(geo.id);
      if (!onMap) {
        // Gone from the map (deleted, undone, cleared, or by someone else): a
        // panel still editing it must not bring it back. Nor may an object
        // from before the last load -- one a drawer, a height lookup or a
        // closing panel still held -- whatever project is open now.
        if (this.removedIds.has(geo.id) || this.isFromEarlierLoad(geo)
          || (!this.canEdit() && !this.isDemoMode())) {
          continue;
        }
        this.indexGeo(geo);
        listChanged = true;
      } else if (onMap !== geo) {
        // A copy from before an undo or reload brought the shape back as a new
        // object: the one on the map is the shape now, this one is history.
        continue;
      }
      const cached = this.fireGeometries[geo.id];
      const before = cached ? withoutBookkeeping(cached) : null;
      const after = withoutBookkeeping(geo.toFirebaseData(true));
      if (before && (before[PolygonField.KEY_TITLE] !== after[PolygonField.KEY_TITLE]
        || before[SpotField.KEY_NAME] !== after[SpotField.KEY_NAME]
        || (before[PolygonField.KEY_GROUP_ID] || '') !== (after[PolygonField.KEY_GROUP_ID] || ''))) {
        // Renamed or regrouped: the lists sort and group by those.
        listChanged = true;
      }
      this.writeDoc('geo', geo.id, after, {immediate: options.saveNow, quiet: options.background});
      if (options.addUndoCheckpoint) {
        changes.push({kind: 'geo', id: geo.id, before, after});
      }
    }
    if (changes.length) {
      // A buffer piece made because its shape grew one is not something drawn.
      const label = options.label
        || (changes.some(c => c.before === null && !followedIds.has(c.id)) ? UndoLabel.DRAW : UndoLabel.EDIT);
      this.recordChanges(changes, label, {immediate: options.saveNow, coalesce: options.coalesce});
    }
    if (listChanged) {
      this.dispatchOnGeometriesChanged();
    }
    return Promise.resolve();
  }

  /**
   * Deletes a shape (with the holes cut out of it), a spot, or a vertex. The
   * holes used to go without being recorded, so undoing a delete brought back
   * a solid shape.
   */
  deleteGeometry(geometry: BaseGeometry, options: SaveOptions): Promise<void> {
    if (!this.canEdit()) {
      return Promise.reject();
    }
    if (geometry instanceof PolygonPoint) {
      const polygon = geometry.parent;
      if (!polygon) {
        return Promise.resolve();
      }
      const index = polygon.points.indexOf(geometry);
      const last = polygon.points.length - 1;
      if (polygon.isLocked && polygon.shape !== PolygonShape.CIRCLE && last > 0 && (index === 0 || index === last)
        && polygon.isClosed()) {
        // A closed shape's first corner and its closing point are one corner.
        // Deleting either left the other where it was, so the corner stayed
        // and the area did not change. Both go, and the shape closes again at
        // its new first corner, in the same step. Only a locked one: in a
        // shape still being drawn, a last point clicked onto the first is a
        // point of its own, and deleting it took the first corner too.
        polygon.removePoint(polygon.points[last]);
        polygon.removePoint(polygon.points[0]);
        if (polygon.points.length >= 3) {
          const first = polygon.getFirstPoint();
          polygon.points.push(this.newPoint(polygon, {lat: first.lat, lng: first.lng}));
          this.mirrorClosingPoint(polygon);
          polygon.update();
        }
      } else {
        polygon.removePoint(geometry);
      }
      if (polygon.getPointsCount() > 0) {
        return this.saveGeometry({...options, label: options.label || UndoLabel.EDIT}, polygon);
      }
      geometry = polygon;
    }
    this.removeGeometries([geometry], {record: !!options.addUndoCheckpoint, label: options.label || UndoLabel.DELETE});
    return Promise.resolve();
  }

  /**
   * Takes shapes and spots (and the holes of those shapes) off the map and out
   * of the project in one pass, as one step when `record` is set. Deleting a
   * hundred shapes used to rebuild the list and re-link the holes a hundred
   * times and send a hundred separate deletes.
   *
   * Returns what else it changed, for a caller recording the step itself:
   * the rest of a buffer letting go of a piece removed (see
   * detachBufferSetsOf), and the rings left in buffers around these shapes
   * (see bufferRingsFor). Only the rings were returned, so undoing a split or
   * a join of a buffer's piece brought the piece back to a buffer that had
   * let go of it.
   */
  removeGeometries(geometries: BaseGeometry[], options: {record: boolean, label?: UndoLabel}): DocChange[] {
    if (!this.canEdit() && !this.isDemoMode()) {
      return [];
    }
    const docs = this.withHoles(geometries.filter(g => this.geoIndex.has(g.id)) as Array<Polygon | Spot>);
    if (!docs.length) {
      return [];
    }
    const changes: DocChange[] = docs.map(geo => ({kind: 'geo' as MapDocKind, id: geo.id, before: this.snapshotOf(geo), after: null}));
    // Kept apart from `changes`: those are all removed below, these stay.
    const detached = this.detachBufferSetsOf(docs);
    const rings = this.bufferRingsFor(new Set(docs.map(geo => geo.id)));
    const added: DocChange[] = rings.map(ring => ({kind: ring.kind, id: ring.id, before: null, after: ring.data}));
    this.applyDocStates([...changes.map(c => ({kind: c.kind, id: c.id, data: null})), ...rings], {write: true});
    if (options.record) {
      this.recordChanges([...changes, ...detached, ...added], options.label || UndoLabel.DELETE);
    }
    this.flushWrites();
    return [...detached, ...added];
  }

  /**
   * A buffer's band is cut round its source shape, borrowed as it is now (see
   * updateHoles), so with the source gone -- deleted, split, joined, cleared
   * -- the band filled in: a solid disc over the pieces, its area grown by
   * the source's, totalled and priced so although nobody touched the buffer.
   * The source's outline stays behind as a hole of each buffer around it, in
   * the same step (undoing it brings the source back and takes the ring out).
   * Returned as documents to add, for the caller's one pass.
   */
  private bufferRingsFor(removedIds: Set<string>): DocTarget[] {
    const rings: DocTarget[] = [];
    this.polygons.forEach(buffer => {
      if (!buffer.isBuffer || !buffer.parentId || removedIds.has(buffer.id) || !removedIds.has(buffer.parentId)) {
        return;
      }
      const source = this.geoIndex.get(buffer.parentId);
      if (!(source instanceof Polygon) || source === buffer || !this.isCutFromBuffer(source)
        || !(buffer.holes || []).includes(source)) {
        return;
      }
      const hole = this.cutOutHole(buffer, source);
      if (hole) {
        rings.push({kind: 'geo', id: hole.id, data: this.snapshotOf(hole)});
      }
    });
    return rings;
  }

  /**
   * A hole in `buffer` with the outline `source` has now: what the band had
   * cut out of it while it followed the source, kept once it no longer does.
   * Null for an outline with no area.
   */
  private cutOutHole(buffer: Polygon, source: Polygon): Polygon | null {
    const outline = (source.path || []).map(p => ({lat: p.lat, lng: p.lng}));
    if (outline.length < 3) {
      return null;
    }
    // Closed: the ring ends on a copy of its first point.
    const first = outline[0];
    const last = outline[outline.length - 1];
    if (first.lat !== last.lat || first.lng !== last.lng) {
      outline.push({...first});
    }
    const hole = this.newPolygon(PolygonShape.POLYGON, buffer.showSurface);
    hole.isHole = true;
    hole.parentId = buffer.id;
    hole.name = buffer.name;
    hole.lineColor = buffer.lineColor;
    hole.lineWidth = buffer.lineWidth;
    hole.lineStyle = buffer.lineStyle;
    hole.fillColor = buffer.fillColor;
    hole.fillOpacity = buffer.fillOpacity;
    // A hole is never priced (see setHole).
    hole.productId = null;
    hole.showMiddleDistances = false;
    hole.points = outline.map(p => new PolygonPoint(this.newGeoId(), hole, p));
    hole.isLocked = true;
    hole.update();
    return hole;
  }

  /** Vertices stand for their shape; each document once. */
  private documentsOf(geometries: BaseGeometry[]): Array<Polygon | Spot> {
    const docs = new Map<string, Polygon | Spot>();
    for (const geo of geometries) {
      if (!geo) {
        continue;
      }
      if (geo instanceof PolygonPoint) {
        if (geo.parent && geo.parent.id) {
          docs.set(geo.parent.id, geo.parent);
        }
      } else if ((geo instanceof Polygon || geo instanceof Spot) && geo.id) {
        docs.set(geo.id, geo);
      }
    }
    return Array.from(docs.values());
  }

  /** The shapes given plus every hole cut out of them. */
  private withHoles(geometries: Array<Polygon | Spot>): Array<Polygon | Spot> {
    const out = new Map<string, Polygon | Spot>();
    const ids = new Set(geometries.map(g => g.id));
    geometries.forEach(g => out.set(g.id, g));
    this.polygons.forEach(p => {
      if (p.isHole && p.parentId && ids.has(p.parentId)) {
        out.set(p.id, p);
      }
    });
    geometries.forEach(g => {
      if (g instanceof Polygon) {
        (g.holes || []).filter(h => h.isHole && this.geoIndex.has(h.id)).forEach(h => out.set(h.id, h));
      }
    });
    return Array.from(out.values());
  }

  /** A shape as it stands, in the form a change records it. */
  private snapshotOf(geo: Polygon | Spot): any {
    return withoutBookkeeping(geo.toFirebaseData(true));
  }

  /**
   * No shape or spot is stored without a name. A blank one -- empty, or only
   * spaces -- gets the default its kind would get, on the object too, so the
   * map and the panels show what was saved. Every write of a geometry passes
   * here: a dialog left empty, an import, a duplicate, an old shape saved
   * again. A spot made with its name field untouched used to be saved
   * nameless.
   */
  private nameIfBlank(id: string, data: any) {
    const isSpot = data[SpotField.KEY_TYPE] === GeometryType.TYPE_SPOT;
    const key = isSpot ? SpotField.KEY_NAME : PolygonField.KEY_TITLE;
    if (SyncService.hasName(data[key])) {
      return;
    }
    const geo = this.geoIndex.get(id);
    const name = geo ? this.defaultNameFor(geo) : this.strings.getString(isSpot ? 'spot' : 'polygon');
    data[key] = name;
    if (geo) {
      geo.name = name;
      geo.updateLabel();
    }
  }

  private indexGeo(geo: Polygon | Spot) {
    if (this.geoIndex.has(geo.id)) {
      return;
    }
    if (geo instanceof Polygon) {
      this.polygons.push(geo);
    } else {
      this.spots.push(geo);
    }
    this.geoIndex.set(geo.id, geo);
    this.geoEpoch.set(geo, this.loadEpoch);
  }

  /**
   * Made or put on the map before the last load: a switch or a reload replaced
   * it. Objects this service has never seen (an import's, say) are new.
   */
  private isFromEarlierLoad(geo: BaseGeometry): boolean {
    const epoch = this.geoEpoch.get(geo);
    return epoch !== undefined && epoch !== this.loadEpoch;
  }

  // ── Writing ─────────────────────────────────────────────────────────────

  /**
   * Records a document's new state (null: deleted) and queues it for Firestore.
   *
   * The cache is always updated, so the next change to the shape is recorded
   * against this state even where nothing is written (the demo map). The write
   * is only queued for someone who may edit the project, and it carries the
   * project it belongs to: switching projects while a save waits used to be
   * able to write one project's shapes into the next.
   *
   * A shape the database already holds is written field by field, vertex by
   * vertex (see QueuedWrite.fields): written whole, this session's copy of
   * every other field replaced what collaborators had saved meanwhile.
   * `quiet` keeps the last-update stamps the shape had (SaveOptions.background).
   */
  private writeDoc(kind: MapDocKind, id: string, data: any | null, options: {immediate?: boolean, quiet?: boolean} = {}) {
    if (kind === 'geo' && data !== null && SyncService.isEmptyShapeDoc(data)) {
      // A shape with no points does not exist: its document goes rather than
      // being saved empty, and it leaves the map. One never stored has
      // nothing to delete.
      this.removeGeosLocal(new Set([id]));
      if (!this.fireGeometries[id] && !this.writeQueue.has(`geo:${id}`)) {
        return;
      }
      data = null;
    }
    if (kind === 'geo' && data !== null) {
      this.nameIfBlank(id, data);
    }
    const previous = kind === 'geo' ? this.fireGeometries[id] : null;
    let stored = null;
    if (data !== null) {
      // Layer documents hold a Firestore timestamp, which a JSON copy would
      // flatten into a plain map; they are flat, so a shallow copy will do.
      stored = kind === 'layer' ? this.cleanLayerDoc(data) : cloneDoc(data);
      if (options.quiet && previous) {
        // Still whoever's it was; only the time moves, so that every version
        // has its own (onDataSnapshot relies on it).
        STAMP_KEYS.forEach(key => {
          if (previous[key] !== undefined) {
            stored[key] = previous[key];
          }
        });
        stored[PolygonField.KEY_LAST_UPDATE_TIMESTAMP] = Date.now();
      } else {
        stored[PolygonField.KEY_LAST_UPDATE_USER] = this.authService.getUserId || '';
        stored[PolygonField.KEY_LAST_UPDATE_TIMESTAMP] = Date.now();
        if (this.currentSessionId) {
          stored[PolygonField.KEY_LAST_UPDATE_SESSION_ID] = this.currentSessionId;
        }
      }
    }
    if (kind === 'geo') {
      if (stored) {
        this.fireGeometries[id] = stored;
      } else {
        delete this.fireGeometries[id];
      }
    } else if (kind === 'layer') {
      if (stored) {
        this.layerDocs.set(id, stored);
      } else {
        this.layerDocs.delete(id);
      }
    }
    const projectId = this.currentProject && this.currentProject.project_id;
    if (!projectId || !this.canEdit()) {
      return;
    }
    const key = `${kind}:${id}`;
    const queued = this.writeQueue.get(key);
    let write: QueuedWrite = {projectId, kind, id, data: stored, attempts: 0};
    // A shape whose last write the server refused for good is written whole
    // again. `previous` holds what was refused, so a write of the fields
    // changed since would leave that out -- and, once it went through, put
    // the save status back to "Saved" over the loss.
    const refused = this.unsavedDocs.has(this.unsavedKey(projectId, kind, id));
    if (kind === 'geo' && stored && previous && !refused) {
      const changes = shapeChanges(previous, stored);
      // Only the stamps moved: a save of an unchanged shape writes nothing,
      // and the cache keeps the stamps the server has (onDataSnapshot
      // compares them).
      if (!queued && changes.vertices.size === 0 && [...changes.fields].every(field => STAMP_KEYS.has(field))) {
        this.fireGeometries[id] = previous;
        return;
      }
      if (!options.quiet) {
        // Whoever saves last is named, as with a whole write. Sent only when
        // they differed, someone else's rename landing just before left
        // their name on the shape -- the one this session is drawing then
        // came back as theirs.
        STAMP_KEYS.forEach(field => {
          if (stored[field] !== undefined) {
            changes.fields.add(field);
          }
        });
      }
      write = {...write, fields: changes.fields, vertices: changes.vertices, addedVertices: changes.added,
        vertexFields: changes.vertexFields};
    }
    if (queued) {
      write = this.mergeWrites(queued, write);
    }
    if (this.writeQueue.size === 0) {
      // Said once per batch of saves, not per document: a clear queues
      // thousands, and every "saving" repaints the canvas.
      this.writeQueueSince = Date.now();
      this.onSavingChanges();
    }
    this.writeQueue.set(key, write);
    clearTimeout(this.writeTimer);
    const overdue = Date.now() - this.writeQueueSince >= WRITE_MAX_DELAY_MS;
    this.writeTimer = setTimeout(() => this.flushWrites(), options.immediate || overdue ? 0 : WRITE_DELAY_MS);
  }

  /**
   * Sends every queued write now. Resolves when those, and any already on their
   * way, have been committed or have failed.
   *
   * The queue is swapped out before the commit, so what is edited while a
   * commit is in flight waits for the next one. It used to be cleared when the
   * commit finished, which threw those edits away.
   */
  flushWrites(): Promise<void> {
    clearTimeout(this.writeTimer);
    this.writeTimer = null;
    if (this.writeQueue.size > 0) {
      const writes = Array.from(this.writeQueue.values());
      this.writeQueue = new Map();
      const commit = this.commitWrites(writes);
      this.writesInFlight.add(commit);
      commit.finally(() => this.writesInFlight.delete(commit));
    }
    return Promise.all(Array.from(this.writesInFlight)).then(() => undefined);
  }

  /**
   * flushWrites, for a caller that must have the saves on the server first --
   * a restore point has to hold them -- but cannot wait for ever: offline, a
   * Firestore write settles only once the connection is back, and the dialog
   * waiting on it stayed locked. Rejects at once when the browser is offline
   * with saves pending, and after `ms` when they still have not arrived.
   */
  private flushWritesWithin(ms: number): Promise<void> {
    const flushed = this.flushWrites();
    if (!this.hasUnsavedWrites()) {
      return flushed;
    }
    if (!navigator.onLine) {
      return Promise.reject(new Error('Offline with saves pending'));
    }
    let timer: any;
    const late = new Promise<void>((resolve, reject) => {
      timer = setTimeout(() => reject(new Error('Saves still pending')), ms);
    });
    return Promise.race([flushed, late]).finally(() => clearTimeout(timer));
  }

  /** Batched: at most MAX_BATCH_OPS writes and MAX_BATCH_BYTES per batch. */
  private commitWrites(writes: QueuedWrite[]): Promise<void> {
    const collections = new Map<string, firebase.firestore.CollectionReference>();
    const collectionOf = (write: QueuedWrite) => {
      const key = `${write.projectId}/${write.kind}`;
      let ref = collections.get(key);
      if (!ref) {
        const name = write.kind === 'geo' ? 'data' : write.kind === 'group' ? 'dataGroups' : 'layers';
        ref = this.firebaseApi.getProjectRef(write.projectId).ref.collection(name);
        collections.set(key, ref);
      }
      return ref;
    };
    const chunks: QueuedWrite[][] = [];
    let chunk: QueuedWrite[] = [];
    let bytes = 0;
    for (const write of writes) {
      const size = !write.data ? 64 : JSON.stringify(write.fields ? this.changedValues(write) : write.data).length;
      if (chunk.length > 0 && (chunk.length >= MAX_BATCH_OPS || bytes + size > MAX_BATCH_BYTES)) {
        chunks.push(chunk);
        chunk = [];
        bytes = 0;
      }
      chunk.push(write);
      bytes += size;
    }
    if (chunk.length > 0) {
      chunks.push(chunk);
    }
    Logger.log(`Saving ${writes.length} documents in ${chunks.length} batches`);
    const dropped: QueuedWrite[] = [];
    return Promise.all(chunks.map(part => this.commitBatch(part, collectionOf, dropped))).then(results => {
      if (dropped.length) {
        this.reportDropped(dropped);
      }
      // Once for the whole flush, however many batches it took.
      const projectId = this.currentProject && this.currentProject.project_id;
      const unsaved = Array.from(this.unsavedDocs).some(key => key.startsWith(`${projectId}/`));
      if (results.every(ok => ok) && !unsaved) {
        this.onChangesSaved();
      } else {
        this.onFailedSavingChanges();
      }
    });
  }

  private unsavedKey(projectId: string, kind: MapDocKind, id: string): string {
    return `${projectId}/${kind}:${id}`;
  }

  /**
   * Commits one batch. On failure it puts back what nothing newer has
   * replaced, to retry shortly (what something newer replaced goes along with
   * that), and adds to `dropped` what it gives up on.
   *
   * A batch is saved whole or not at all, so one document the server refuses
   * -- too large, too many vertices to index -- took every other write in it
   * down too, on every retry: an import's hundreds of shapes with it. Such a
   * batch is split in halves until the refused documents stand alone, and
   * only those are lost.
   */
  private commitBatch(writes: QueuedWrite[], collectionOf: (write: QueuedWrite) => firebase.firestore.CollectionReference,
                      dropped: QueuedWrite[]): Promise<boolean> {
    const keyOf = (write: QueuedWrite) => `${write.kind}:${write.id}`;
    const batch = this.firebaseApi.newBatch();
    // A document Firestore cannot hold at all -- an id with a '/', an array
    // inside an array, an empty or '__x__' field name, all of which an
    // imported file can bring -- throws here, before anything is sent. It
    // took the whole flush down, never retried; now only it is given up on,
    // and reported with the rest of `dropped`.
    const refused: QueuedWrite[] = [];
    for (const write of writes) {
      try {
        const ref = collectionOf(write).doc(write.id);
        if (!write.data) {
          batch.delete(ref);
        } else if (!write.fields) {
          batch.set(ref, write.data);
        } else {
          const update = this.updateOf(write);
          if (update.length) {
            batch.update(ref, update[0], update[1], ...update.slice(2));
          }
        }
      } catch (e) {
        Logger.error('A document Firestore cannot hold was not saved', write.kind, write.id, e);
        refused.push(write);
      }
    }
    if (refused.length) {
      dropped.push(...refused);
      refused.forEach(write => this.resentWrites.delete(write));
      writes = writes.filter(write => !refused.includes(write));
    }
    return batch.commit().then(() => {
      writes.forEach(write => this.resentWrites.delete(write));
      writes.forEach(write => this.unsavedDocs.delete(this.unsavedKey(write.projectId, write.kind, write.id)));
      return !refused.length;
    }, error => {
      Logger.error('Error saving data!', error);
      // Whatever of these goes again goes as a new write (below). Let go of
      // before the listener hears the batch taken back, which it does after
      // this: a document refused for good is then taken off the map.
      writes.forEach(write => this.resentWrites.delete(write));
      const code = error?.code;
      // A change to a shape someone else deleted meanwhile: update() needs
      // the document to be there.
      const gone = code === 'not-found';
      if ((gone || DOCUMENT_WRITE_ERRORS.includes(code)) && writes.length > 1) {
        // Each as its document is now. Resent as they were, a shape of an
        // import edited while the batch was on its way went back to how it
        // was imported (the edit, sent as a change to a shape the server did
        // not have yet, was refused too), and a later save already sent was
        // undone by the older one.
        const again = this.toSendAgain(writes);
        // The listener hears the refused batch taken back, its new shapes
        // removed; those on their way again are not deleted (onDataSnapshot).
        again.filter(write => write.kind === 'geo' && write.data && !write.fields)
          .forEach(write => this.resentWrites.add(write));
        const half = Math.ceil(again.length / 2);
        return Promise.all([again.slice(0, half), again.slice(half)].filter(part => part.length)
          .map(part => this.commitBatch(part, collectionOf, dropped)))
          .then(results => results.every(ok => ok));
      }
      // A document changed again since has a newer write waiting. It takes
      // this one's fields along, as it would otherwise send only its own.
      const current = writes.filter(write => {
        const newer = this.writeQueue.get(keyOf(write));
        if (newer) {
          this.writeQueue.set(keyOf(write), this.mergeWrites(write, newer));
        }
        return !newer;
      });
      if (gone) {
        // Someone else deleted the shape meanwhile, and the deletion stands,
        // as it does for edits still waiting to be sent (onDataSnapshot): the
        // listener takes it off the map. Or it was never there: the batch
        // that was to create it was refused, and goes again (above). A shape
        // still on the map is written whole, a little later -- time for the
        // listener to report a deletion, which takes the write out of the
        // queue again. It used to count as saved, and the edit was lost.
        const onMap = current.filter(write => write.kind === 'geo' && write.data
          && write.projectId === this.currentProject?.project_id && !this.rawView
          && this.geoIndex.has(write.id) && !this.removedIds.has(write.id) && !!this.fireGeometries[write.id]);
        if (!onMap.length) {
          return true;
        }
        dropped.push(...onMap.filter(write => write.attempts + 1 >= MAX_WRITE_ATTEMPTS));
        onMap.filter(write => write.attempts + 1 < MAX_WRITE_ATTEMPTS).forEach(write => this.writeQueue.set(keyOf(write),
          {projectId: write.projectId, kind: write.kind, id: write.id, data: this.fireGeometries[write.id],
            attempts: write.attempts + 1}));
        clearTimeout(this.writeTimer);
        this.writeTimer = setTimeout(() => this.flushWrites(), 5000);
        return false;
      }
      if (DOCUMENT_WRITE_ERRORS.includes(code)) {
        dropped.push(...current);
        return false;
      }
      const retry = current.filter(write => write.attempts + 1 < MAX_WRITE_ATTEMPTS);
      dropped.push(...current.filter(write => write.attempts + 1 >= MAX_WRITE_ATTEMPTS));
      if (retry.length > 0) {
        retry.forEach(write => {
          const latest = this.withLatestData(write);
          if (latest) {
            this.writeQueue.set(keyOf(write), {...latest, attempts: write.attempts + 1});
          }
        });
        clearTimeout(this.writeTimer);
        this.writeTimer = setTimeout(() => this.flushWrites(), 5000);
      }
      return false;
    });
  }

  /**
   * Whole writes of shapes going again after their batch was refused (see
   * commitBatch). The listener hears a refused batch taken back before it
   * hears it sent again, and took the shapes it was to create off the map in
   * between: they came back as new shapes, and a panel open on one saved
   * nothing more.
   */
  private resentWrites = new Set<QueuedWrite>();

  /** Whether a shape of the open project is on its way again after its batch was refused (see resentWrites). */
  private isBeingResent(id: string): boolean {
    if (!this.resentWrites.size) {
      return false;
    }
    const projectId = this.currentProject?.project_id;
    for (const write of this.resentWrites) {
      if (write.kind === 'geo' && write.id === id && write.projectId === projectId) {
        return true;
      }
    }
    return false;
  }

  /**
   * A write going again after a failure, with the document as it is now: a
   * later save already sent must not be undone by this older one landing
   * after it. Null for a shape deleted here since, which is not written
   * again. Not from a raw data view, whose shapes (often the same ids) are
   * not the project's.
   */
  private withLatestData(write: QueuedWrite): QueuedWrite | null {
    const latest = write.kind === 'geo' && write.data && write.projectId === this.currentProject?.project_id
      && !this.rawView ? this.fireGeometries[write.id] : write.data;
    return latest === undefined ? null : {...write, data: latest};
  }

  /**
   * The writes of a refused batch, to send again in halves: each together
   * with a newer write of its document waiting in the queue, taken out of it
   * to go now -- left there, it was lost when the listener heard a new shape
   * of the refused batch removed -- else with its document as it is now.
   */
  private toSendAgain(writes: QueuedWrite[]): QueuedWrite[] {
    const again: QueuedWrite[] = [];
    writes.forEach(write => {
      const key = `${write.kind}:${write.id}`;
      const newer = this.writeQueue.get(key);
      if (newer) {
        this.writeQueue.delete(key);
        again.push(this.mergeWrites(write, newer));
        return;
      }
      const latest = this.withLatestData(write);
      if (latest) {
        again.push(latest);
      }
    });
    return again;
  }

  /**
   * Two writes to one document as one: the newer data, and every field and
   * vertex either of them changed. A write of the whole document (or a
   * delete) takes in the rest.
   */
  private mergeWrites(older: QueuedWrite, newer: QueuedWrite): QueuedWrite {
    if (!newer.data || !newer.fields) {
      return newer;
    }
    if (!older.data || !older.fields) {
      return {...newer, fields: undefined, vertices: undefined, addedVertices: undefined, vertexFields: undefined};
    }
    const vertices = new Set([...older.vertices, ...newer.vertices]);
    // A vertex either one writes whole is written whole.
    const vertexFields = new Map<string, Set<string>>();
    vertices.forEach(id => {
      const was = older.vertices.has(id) ? older.vertexFields?.get(id) : new Set<string>();
      const is = newer.vertices.has(id) ? newer.vertexFields?.get(id) : new Set<string>();
      if (was && is) {
        vertexFields.set(id, new Set([...was, ...is]));
      }
    });
    return {
      ...newer,
      fields: new Set([...older.fields, ...newer.fields]),
      vertices,
      addedVertices: new Set([...older.addedVertices, ...newer.addedVertices]),
      vertexFields,
    };
  }

  /** The top-level fields a shape write sends; the vertex map whole when too many vertex values changed. */
  private fieldsOf(write: QueuedWrite): Set<string> {
    const fields = new Set(write.fields);
    const paths = [...write.vertices].reduce((sum, id) => sum + (write.vertexFields?.get(id)?.size || 1), 0);
    if (paths > MAX_VERTEX_PATHS || [...write.vertices].some(id => !id)) {
      fields.add(POINTS_FIELD);
    }
    return fields;
  }

  /** What a write sends of one vertex: the attributes changed (see QueuedWrite.vertexFields), or all of it. */
  private vertexWrite(write: QueuedWrite, points: any, id: string): Array<[string[], any]> {
    const attributes = write.vertexFields?.get(id);
    if (attributes && isPlainMap(points[id])) {
      return [...attributes].map(attribute => [[POINTS_FIELD, id, attribute], points[id][attribute]] as [string[], any]);
    }
    return [[[POINTS_FIELD, id], points[id]]];
  }

  /** A shape write's changed values, for sizing the batch it goes in. */
  private changedValues(write: QueuedWrite): any[] {
    const fields = this.fieldsOf(write);
    const points = isPlainMap(write.data[POINTS_FIELD]) ? write.data[POINTS_FIELD] : {};
    return [...[...fields].map(field => write.data[field]),
      ...(fields.has(POINTS_FIELD) ? [] : [...write.vertices]
        .flatMap(id => this.vertexWrite(write, points, id).map(([, value]) => value)))];
  }

  /**
   * A shape write as update() takes it: field path, value, field path,
   * value... A field or vertex no longer there is deleted.
   */
  private updateOf(write: QueuedWrite): any[] {
    const valueOf = (value: any) => value === undefined ? firebase.firestore.FieldValue.delete() : value;
    const fields = this.fieldsOf(write);
    const args: any[] = [];
    fields.forEach(field => args.push(new firebase.firestore.FieldPath(field), valueOf(write.data[field])));
    if (!fields.has(POINTS_FIELD)) {
      const points = isPlainMap(write.data[POINTS_FIELD]) ? write.data[POINTS_FIELD] : {};
      write.vertices.forEach(id => this.vertexWrite(write, points, id)
        .forEach(([path, value]) => args.push(new firebase.firestore.FieldPath(...path), valueOf(value))));
    }
    return args;
  }

  /**
   * Says, by name, what will not be saved, and keeps the save status at "Save
   * failed" until it is (see unsavedDocs). The only sign used to be that
   * status, which the next successful save turned back to "Saved".
   */
  private reportDropped(writes: QueuedWrite[]) {
    // Only what is still on the map waits to be saved. A refused new shape is
    // taken off by the listener, and a refused delete brings the shape back.
    const shown = (write: QueuedWrite) => write.kind === 'geo' ? this.geoIndex.has(write.id)
      : write.kind === 'group' ? !!this.dataGroups[write.id]
      : this.layers.some(layer => layer.id === write.id);
    writes.filter(write => write.data && shown(write))
      .forEach(write => this.unsavedDocs.add(this.unsavedKey(write.projectId, write.kind, write.id)));
    const names = [...new Set(writes
      .map(write => write.data && (write.data[PolygonField.KEY_TITLE] || write.data[SpotField.KEY_NAME]))
      .filter(name => typeof name === 'string' && name.trim() !== ''))];
    // The list and the count of the rest read as the language has them.
    const join = (list: string, name: string) => this.strings.getString('list_join', undefined, list, name);
    const message = names.length > 3
      ? this.strings.getString('save_lost_items_more', undefined, names.slice(0, 3).reduce(join), String(names.length - 3))
      : names.length
        ? this.strings.getString('save_lost_items', undefined, names.reduce(join))
        : this.strings.getString('save_lost_changes');
    // A commit can settle outside Angular's zone (a flush a listener started).
    this.ngZone.run(() => openSnack(this.snackBar, message, this.strings.getString('dismiss'),
      ERROR_SNACK_DURATION, 'error'));
  }

  rootData(data, parentPath) {
    const newData = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        newData[parentPath + key] = data[key];
      }
    }
    return newData;
  }

  getProjectDataRef() {
    return this.firebaseApi.getProjectDataRef(this.currentProject.project_id);
  }

  getProjectDataGroupsRef() {
    return this.firebaseApi.getProjectDataGroupsRef(this.currentProject.project_id);
  }

  /**
   * Puts documents into the state given -- an undo or redo, a clear, or someone
   * else's change -- in one pass: one list refresh, one hole update, one
   * repaint, and (with `write`) one batched save, however many there are.
   */
  private applyDocStates(targets: DocTarget[], options: {write: boolean, remote?: boolean}) {
    if (!targets.length) {
      return;
    }
    const removed = new Set<string>();
    const changed = new Set<string>();
    // The areas holes are cut from, before and after: an area's stored total
    // is net of its holes, so deleting, restoring or moving a hole changes it
    // although the area itself was not edited. Only the hole was written, and
    // the area kept its old total until someone edited it.
    const holeAreas = new Set<string>();
    const noteHoleArea = (id: string) => {
      const geo = this.geoIndex.get(id);
      if (geo instanceof Polygon && geo.isHole && geo.parentId) {
        holeAreas.add(geo.parentId);
      }
    };
    targets.forEach(target => target.kind === 'geo' && noteHoleArea(target.id));
    const empty: Array<{id: string, data: any}> = [];
    this.dispatchSuspended++;
    try {
      for (const target of targets) {
        if (target.kind === 'geo') {
          if (target.data !== null && SyncService.isEmptyShapeDoc(target.data)) {
            // A shape with no points does not exist (isEmptyShapeDoc).
            removed.add(target.id);
            empty.push({id: target.id, data: target.data});
          } else if (target.data === null) {
            removed.add(target.id);
          } else if (this.upsertGeoLocal(target.id, target.data)) {
            changed.add(target.id);
          }
        } else if (target.kind === 'group') {
          if (target.data === null) {
            delete this.dataGroups[target.id];
          } else {
            this.dataGroups[target.id] = target.data;
          }
        } else if (target.data === null) {
          this.removeLayerLocal(target.id);
        } else {
          this.upsertLayerLocal(target.data);
        }
        if (target.data === null) {
          // Gone from the map, so no longer waiting to be saved (a refused
          // new document is taken off by the listener).
          this.unsavedDocs.delete(this.unsavedKey(this.currentProject?.project_id, target.kind, target.id));
        }
        if (options.write) {
          this.writeDoc(target.kind, target.id, target.data);
        }
      }
      this.removeGeosLocal(removed);
      // Loaded or sent by someone else: an empty shape's stored document is
      // deleted, once it is old enough not to be one being made right now.
      if (!options.write && empty.length && this.canEdit()) {
        const cutoff = Date.now() - SyncService.EMPTY_SHAPE_GRACE_MS;
        empty.filter(e => (Number(e.data[PolygonField.KEY_LAST_UPDATE_TIMESTAMP]) || 0) < cutoff)
          .forEach(e => this.writeDoc('geo', e.id, null, {quiet: true}));
      }
      this.updateHolesAround(new Set([...changed, ...removed]));
      if (options.write) {
        changed.forEach(noteHoleArea);
        holeAreas.forEach(id => {
          const area = this.geoIndex.get(id);
          const stored = this.fireGeometries[id];
          if (removed.has(id) || changed.has(id) || !(area instanceof Polygon) || !stored
            || stored[PolygonField.KEY_TOTAL_AREA] === area.area) {
            return;
          }
          // Its figures only: still whoever last edited it (quiet).
          this.writeDoc('geo', id, this.snapshotOf(area), {quiet: true});
        });
      }
      // Someone else's change never decides which shape this session is
      // drawing; removing it is handled by removeGeosLocal.
      if (!options.remote) {
        this.settleCurrentPolygon(changed);
      }
    } finally {
      this.dispatchSuspended--;
    }
    this.dispatchOnGeometriesChanged();
    this.onAppearanceChanged.next();
    this.updateMeasurementBox.emit();
    this.geometriesUpdated.next({changed, removed, remote: !!options.remote});
  }

  /**
   * Brings a shape or spot to the state given, creating it if it is not on the map.
   *
   * The model gets its own copy: `data` is also the cached document (or an
   * undo step's), and the converter hands its arrays to the model as they
   * are. A photo added to the shape then went into the cached document too,
   * so the next save found nothing changed and never sent it.
   */
  private upsertGeoLocal(id: string, stored: any): boolean {
    const data = withSoundVertices(cloneDoc(stored));
    const existing = this.geoIndex.get(id);
    if (existing instanceof Polygon) {
      // A shape's photos are left out of its document once there are none,
      // and the converter only sets them when there are: the last photo,
      // removed by someone else or by an undo, stayed on the shape here.
      if (!data[PolygonField.KEY_IMAGES]) {
        existing.images = [];
      }
      GeometryConverter.loadDataToPolygon(existing, data);
      existing.update();
      return true;
    }
    if (existing instanceof Spot) {
      // As for a shape, above.
      if (!data[SpotField.KEY_IMAGES]) {
        existing.images = [];
      }
      GeometryConverter.loadDataToSpot(existing, data);
      existing.update();
      return true;
    }
    let geo: Polygon | Spot = null;
    if (data.type === GeometryType.TYPE_POLYGON) {
      const polygon = GeometryConverter.dataToPolygon(data);
      if (polygon.getPointsCount() > 100) {
        polygon.showMiddleDistances = false;
      }
      polygon.update();
      geo = polygon;
    } else if (data.type === GeometryType.TYPE_SPOT) {
      geo = GeometryConverter.dataToSpot(data);
    }
    if (!geo) {
      return false;
    }
    this.indexGeo(geo);
    this.removedIds.delete(id);
    return true;
  }

  /** Takes shapes and spots off the map. The database is the caller's business. */
  private removeGeosLocal(ids: Set<string>) {
    if (!ids.size) {
      return;
    }
    this.polygons = this.polygons.filter(p => !ids.has(p.id));
    this.spots = this.spots.filter(s => !ids.has(s.id));
    ids.forEach(id => {
      this.geoIndex.delete(id);
      this.removedIds.add(id);
    });
    if (this._currentPolygon && ids.has(this._currentPolygon.id)) {
      this.currentPolygon = null;
    }
    this.measureUnitsChangedEvent.emit();
  }

  /**
   * Which shape is being drawn, after an undo, redo or remote change. One per
   * person: when a step brought back an unlocked shape of mine, that is the one
   * I went back to, and any other I had open is locked. Deciding by list order
   * used to lock one of the two unpredictably.
   */
  private settleCurrentPolygon(changed: Set<string>) {
    const mine = this.polygons.filter(p => this.isCurrentPolygon(p));
    const current = this._currentPolygon && mine.includes(this._currentPolygon) ? this._currentPolygon : null;
    const chosen = mine.find(p => changed.has(p.id) && p !== current) || current || mine[mine.length - 1] || null;
    mine.filter(p => p !== chosen).forEach(p => {
      p.isLocked = true;
      p.update();
      this.writeDoc('geo', p.id, withoutBookkeeping(p.toFirebaseData(true)));
    });
    if (chosen !== this._currentPolygon) {
      this.currentPolygon = chosen;
    }
  }

  /** A map layer's document, from what was read or written, else from the layer itself. */
  private layerDocOf(id: string): any {
    const stored = this.layerDocs.get(id);
    if (stored) {
      return stored;
    }
    const layer = this.layers.find(l => l.id === id);
    return layer ? this.cleanLayerDoc(layer) : null;
  }

  /**
   * A layer as stored: without what only exists in this browser (the file
   * being uploaded, a preview URL) and without undefined values, which
   * Firestore refuses.
   */
  private cleanLayerDoc(layer: any): any {
    const doc: any = {};
    Object.keys(layer).forEach(key => {
      if (layer[key] !== undefined && key !== 'blob' && key !== 'placeholderUrl') {
        doc[key] = layer[key];
      }
    });
    return doc;
  }

  private upsertLayerLocal(doc: any) {
    const existing = this.layers.find(l => l.id === doc.id);
    if (existing) {
      // Name, opacity, bounds: what changes on a layer after it is placed.
      Object.assign(existing, this.cleanLayerDoc(doc));
      // MBTiles tiles are drawn by the map, not the template.
      MBTilesProvider.setLayerOpacity(existing.id, existing.opacity);
      return;
    }
    const layer = Object.assign(new Layer(), this.cleanLayerDoc(doc)) as Layer;
    this.addMapLayer(layer);
  }

  private removeLayerLocal(id: string) {
    // One still opening is told not to finish (see addMapLayer).
    this.loadingLayers.delete(id);
    this.unreadableLayers.delete(id);
    const index = this.layers.findIndex(l => l.id === id);
    if (index > -1) {
      this.layers.splice(index, 1);
    }
    this.removeLayerOverlay(id);
  }

  /** MBTiles are drawn as a map overlay tagged with the layer id; the others by the template. */
  private removeLayerOverlay(id: string) {
    const overlays = this.mapService.map && this.mapService.map.overlayMapTypes;
    if (overlays) {
      for (let i = overlays.getLength() - 1; i >= 0; i--) {
        const overlay = overlays.getAt(i);
        if (overlay && overlay.alt === id) {
          overlays.removeAt(i);
        }
      }
    }
    // Its database is closed too: dropped, it kept the whole file in memory.
    MBTilesProvider.disposeLayer(id);
  }

  /** Nesting depth of applyDocStates: the list is rebuilt once at the end, not per shape. */
  private dispatchSuspended = 0;

  dispatchOnGeometriesChanged() {
    if (this.dispatchSuspended > 0) {
      return;
    }
    this.polygons.sort(byName);
    this.spots.sort(byName);
    this.layers.sort(byName);

    let currentGroups = ObjectUtil.toArray(this.dataGroups) as GeoGroupData[];
    currentGroups = currentGroups.sort(byName);
    const groups: GeoGroup[] = currentGroups.map(g => Object.assign(new GeoGroup(), g));
    const byId = new Map(groups.map(g => [g.id, g] as [string, GeoGroup]));
    const noGroup = new GeoGroup();
    // A shape whose group no longer exists (deleted by someone else, or
    // restored from a version older than the group) is listed as ungrouped.
    // It used to be left out of every list while still drawn on the map.
    this.polygons.forEach(polygon => {
      const group = polygon.groupId ? byId.get(polygon.groupId) : null;
      if (group) {
        if (!group.polygons) {
          group.polygons = [];
        }
        group.polygons.push(polygon);
      } else {
        noGroup.polygons.push(polygon);
      }
    });
    this.spots.forEach(spot => {
      const group = spot.groupId ? byId.get(spot.groupId) : null;
      if (group) {
        if (!group.spots) {
          group.spots = [];
        }
        group.spots.push(spot);
      } else {
        noGroup.spots.push(spot);
      }
    });
    groups.push(noGroup);

    const data = {
      isSet: true,
      groups: groups,
      polygons: this.polygons,
      spots: this.spots,
      layers: this.layers
    }
    Logger.log('dispatchOnGeometriesChanged', data)

    this.onGeometryListChanged.next(data);
  }

  onSavingChanges() {
    this.savingChangesEvent.emit();
  }

  onChangesSaved() {
    this.geometriesChanged = true;
    this.changesSavedEvent.emit();
  }

  onFailedSavingChanges() {
    this.failedToSaveEvent.emit();
  }

  updatePolygonObject(polygon: Polygon, newData: any): Promise<void> {
    ObjectUtil.every(newData, (key, value) => {
      polygon[key] = newData[key];
    });
    if(polygon.id) {
      if (polygon.label) {
        polygon.updateLabel();
      }
      // The list's picture follows the colours by itself (utils/shape-thumbnail).
      return this.saveGeometry({addUndoCheckpoint: true, coalesce: true}, polygon);
    }
    return Promise.resolve();
  }

  // The panels save a field at a time as it is edited; a burst of those on one
  // shape is one step (see SaveOptions.coalesce).

  updateSpotObject(spot: Spot, newData: any): Promise<void> {
    ObjectUtil.every(newData, (key, value) => {
      spot[key] = newData[key];
    });
    return this.saveGeometry({addUndoCheckpoint: true, coalesce: true}, spot);
  }

  updatePolygonPointObject(point: PolygonPoint, newData: any): Promise<void> {
    ObjectUtil.every(newData, (key, value) => {
      point[key] = newData[key];
    });
    return this.saveGeometry({addUndoCheckpoint: true, coalesce: true}, point);
  }

  lockPolygon(polygon: Polygon, name: string, closePolygon: boolean, addUndoCheckpoint: boolean = true) {
    // Closed only when that makes an area (Polygon.canClose): whatever asks.
    if (closePolygon && polygon.canClose()) {
      if (!polygon.isClosed()) {
        const first = polygon.getFirstPoint();
        polygon.points.push(this.newPoint(polygon, {lat: first.lat, lng: first.lng}));
      }
      // The closing point is the first corner again, heights included. It
      // used to get none, so the 3D lookup gave it the ground's while the
      // first corner kept the roof height it was clicked at.
      this.mirrorClosingPoint(polygon);
    }
    polygon.name = name;
    polygon.isLocked = true;
    // polygon.updatePath();
    // polygon.calculateDistanceLabels();
    // polygon.recalculateStats();
    // polygon.updateLabel();
    polygon.update();
    // Finishing a shape is sent at once, not after the pause that gathers a
    // drag's saves: closing the tab straight after lost the shape.
    this.saveGeometry({addUndoCheckpoint, saveNow: true}, polygon);

    this.updateCurrentPolygon();
    // this.currentPolygon = null;
  }

  /**
   * What a server import added, once the listeners have brought it: the
   * server writes it, and it arrives as anyone else's changes do. Gives back
   * the import's undo step for the caller to record (recordImport) --
   * nothing is recorded while it is awaited, so an edit made meanwhile is a
   * step of its own and Undo keeps working. `cleared` is what a Replace's
   * clear removed (the server says which), each put back by Undo as it last
   * was here -- an edit made after the import started included -- or, if it
   * went before this page saw it, as it was when the import started
   * (`before`). Waits while documents keep coming, and gives up on what
   * has not come once nothing has for IMPORT_ARRIVAL_MS. Null when another
   * project has been opened meanwhile.
   */
  async awaitServerImport(created: {geometries: string[], groups: string[], layers: string[]},
                          groupsBefore: {[id: string]: any},
                          cleared: {geometries: string[], groups: string[], layers: string[]} | null = null,
                          before: DocChange[] = []): Promise<ServerImportArrival | null> {
    const epoch = this.loadEpoch;
    // A group the project had (an .mmp imported again) has arrived once it changed.
    const groupArrived = (id: string) => !!this.dataGroups[id]
      && (!groupsBefore[id] || this.dataGroups[id].updatedOn !== groupsBefore[id].updatedOn);
    // Arrived, or arrived and removed since by someone: waiting for it would wait for nothing.
    const came = (kind: string, id: string, here: boolean) => here || this.removedLately.has(`${kind}:${id}`);
    const removals = cleared ? [
      ...cleared.geometries.map(id => ({kind: 'geo' as MapDocKind, id})),
      ...cleared.groups.map(id => ({kind: 'group' as MapDocKind, id})),
      ...cleared.layers.map(id => ({kind: 'layer' as MapDocKind, id})),
    ] : [];
    const gone = (r: {kind: MapDocKind, id: string}) => r.kind === 'geo' ? !this.geoIndex.has(r.id)
      : r.kind === 'group' ? !this.dataGroups[r.id] : !this.layers.some(layer => layer.id === r.id);
    const count = () => created.geometries.filter(id => came('geo', id, this.geoIndex.has(id))).length
      + created.groups.filter(id => came('group', id, groupArrived(id))).length
      + created.layers.filter(id => came('layer', id, this.layerDocs.has(id))).length
      + removals.filter(gone).length;
    const total = created.geometries.length + created.groups.length + created.layers.length + removals.length;
    let seen = count();
    let lastArrival = Date.now();
    while (seen < total && Date.now() - lastArrival < IMPORT_ARRIVAL_MS && epoch === this.loadEpoch) {
      // Outside Angular: a check every 150 ms ran the whole app's change detection each time.
      await this.ngZone.runOutsideAngular(() => new Promise(resolve => setTimeout(resolve, 150)));
      const now = count();
      if (now > seen) {
        seen = now;
        lastArrival = Date.now();
      }
    }
    if (epoch !== this.loadEpoch) {
      return null;
    }
    if (seen < total) {
      Logger.error('Import: documents written that never arrived', total - seen);
    }
    const changes: DocChange[] = [];
    created.geometries.forEach(id => {
      const geo = this.geoIndex.get(id);
      if (geo) {
        changes.push({kind: 'geo', id, before: null, after: this.snapshotOf(geo)});
      }
    });
    created.groups.forEach(id => {
      if (this.dataGroups[id]) {
        changes.push({kind: 'group', id, before: groupsBefore[id] ? withoutBookkeeping(groupsBefore[id]) : null,
          after: withoutBookkeeping(this.dataGroups[id])});
      }
    });
    created.layers.forEach(id => {
      const after = this.layerSnapshot(id);
      if (after) {
        changes.push({kind: 'layer', id, before: null, after});
      }
    });
    const atStart = new Map(before.map(change => [`${change.kind}:${change.id}`, change.before]));
    removals.filter(gone).forEach(r => {
      const last = this.removedLately.get(`${r.kind}:${r.id}`);
      const was = last ? last.snapshot : atStart.get(`${r.kind}:${r.id}`);
      if (was) {
        changes.push({kind: r.kind, id: r.id, before: was, after: null});
      }
    });
    const geometries = created.geometries.map(id => this.geoIndex.get(id)).filter(geo => !!geo);
    return {changes, geometries, missing: total - seen};
  }

  /** Keeps what someone else removed, as it last was, for a while (removedLately). */
  private rememberRemoved(kind: MapDocKind, id: string, snapshot: any) {
    if (!snapshot) {
      return;
    }
    const now = Date.now();
    this.removedLately.set(`${kind}:${id}`, {snapshot, at: now});
    if (this.removedLately.size > 20000) {
      for (const [key, entry] of this.removedLately) {
        if (now - entry.at > REMOVED_KEPT_MS || this.removedLately.size > 20000) {
          this.removedLately.delete(key);
        }
      }
    }
  }

  /**
   * What a Replace is about to take away, as it is now: what its undo step
   * puts back for anything this page did not see change before the server
   * removed it (awaitServerImport).
   */
  replacedChanges(plan: ClearPlan): DocChange[] {
    const changes: DocChange[] = [];
    for (const geo of [...plan.polygons, ...plan.spots]) {
      if (this.geoIndex.has(geo.id)) {
        changes.push({kind: 'geo', id: geo.id, before: this.snapshotOf(geo), after: null});
      }
    }
    for (const group of plan.groups) {
      if (this.dataGroups[group.id]) {
        changes.push({kind: 'group', id: group.id, before: withoutBookkeeping(this.dataGroups[group.id]), after: null});
      }
    }
    for (const layer of plan.layers) {
      const before = this.layers.some(onMap => onMap.id === layer.id) ? this.layerSnapshot(layer.id) : null;
      if (before) {
        changes.push({kind: 'layer', id: layer.id, before, after: null});
      }
    }
    return changes;
  }

  /**
   * Saves still on their way sent before a Replace, whose restore point the
   * server takes from the database; false when they cannot be (offline, or
   * not within the wait), and the restore point would miss them.
   */
  async flushBeforeReplace(): Promise<boolean> {
    this.flushUndo();
    try {
      await this.flushWritesWithin(RESTORE_POINT_SAVE_WAIT_MS);
      return true;
    } catch (e) {
      Logger.error('Saves pending before a Replace', e);
      return false;
    }
  }

  /** Records what an import brought (awaitServerImport) in the step being built; whether it was anything. */
  recordImport(changes: DocChange[]): boolean {
    this.recordChanges(changes, UndoLabel.IMPORT);
    return changes.length > 0;
  }

  /**
   * After an import, the clear of a Replace included: the first unlocked
   * shape it brought made current, and the map told it has loaded, which
   * fits the view to what is on it now.
   */
  finishImport(geometries: (Polygon | Spot)[], fit: boolean) {
    const unlocked = geometries.find(geo => geo instanceof Polygon && !geo.isLocked) as Polygon;
    // Not over a shape being drawn: an import that took a while ends while the person works.
    if (unlocked && (!this.currentPolygon || this.currentPolygon.isLocked)) {
      this.currentPolygon = unlocked;
    }
    const pointsCount = geometries.reduce((sum, geo) => sum + (geo instanceof Polygon ? geo.points.length : 1), 0);
    this.fitOnLoadingFinished = fit;
    try {
      this.onLoadingFinished.emit(pointsCount);
    } finally {
      this.fitOnLoadingFinished = true;
    }
  }

  /** Changes whenever another project is opened: work started in one is not finished in another. */
  get loadGeneration(): number {
    return this.loadEpoch;
  }

  /** The groups as they are now, to undo an import that changes some of them (awaitServerImport). */
  groupsSnapshot(): {[id: string]: any} {
    return JSON.parse(JSON.stringify(this.dataGroups || {}));
  }

  /**
   * Adds a file to the map. Pictures (placed by hand) and MBTiles maps are
   * added here; the data formats are read by the server (POST /import), and
   * only the demo, which saves nothing, passes what the server read
   * (`parsed`, from POST /import/preview) to be added here.
   */
  loadFile(file: File, parsed?: FileImport) {
    // An import belongs to the project it was started in; one opened while
    // the file was read or uploaded gets none of it.
    const epoch = this.loadEpoch;
    return new Promise((resolve, reject) => {
      const imageTypes = ['jpeg', 'jpg', 'png'];
      const dataFileTypes = FileLoader.DATA_EXTENSIONS;
      Logger.log('loadFile: ', file.type, file.name);
      const extension = FileUtil.getFileExtension(file.name).toLowerCase();
      if (imageTypes.includes(extension)) {
        this.loadingProgress = 0;
        CommonUtil.blobToDataUrl(file).then(url => {
          if (epoch !== this.loadEpoch) {
            this.loadingProgress = -1;
            resolve(null);
            return;
          }
          const layer: Layer = {
            opacity: 1.0,
            projectId: this.currentProject.project_id,
            createdOn: undefined,
            downloadUrl: url,
            id: '',
            // The file's name without its extension ("Site plan", not
            // "Site plan.png"), as a group made from an imported file is named.
            name: file.name.replace(/\.[^.]+$/, '') || file.name,
            storagePath: '',
            type: LayerType.IMAGE_OVERLAY,
            blob: file
          }
          this.loadingProgress = -1;
          this.onLayerAdded.emit(layer);
          resolve(null);
        }).catch(reason => {
          // Settled either way: the import waits on this with its undo group
          // open, and a promise that never settled left Undo dead.
          this.loadingProgress = -1;
          reject(reason);
        });
      } else if (extension === 'mbtiles') {
        this.uploadLayer({
          opacity: 1.0,
          projectId: this.currentProject.project_id, createdOn: undefined, downloadUrl: '', id: '', storagePath: '',
          blob: file, type: LayerType.MBTILES, name: file.name.replace(/\.[^.]+$/, '') || file.name
        }).then(layer => {
          if (epoch !== this.loadEpoch) {
            resolve(null);
            return;
          }
          // Read before the removal below forgets it.
          const unreadable = this.unreadableLayers.get(layer.id);
          if (unreadable) {
            // Uploaded, but not tiles this map can open. Taken out again --
            // inside the import's undo group, adding and removing it cancel
            // out -- and reported as a failed import.
            this.deleteMapLayer(layer).catch(e => Logger.error('Removing an unreadable layer failed', e));
            reject(new ImportError(unreadable, file.name, extension));
            return;
          }
          // The bounds are read when the file is opened, on the object the
          // map lists (the listener may have put up its own). They are
          // optional in an MBTiles file; without them there is nothing to
          // zoom to.
          const bounds = (this.layers.find(l => l.id === layer.id) || layer).bounds;
          if (bounds) {
            this.mapService.fitBounds(bounds);
          }
          this.onLayerAdded.emit(layer);
          resolve(null);
        }).catch(reject);
      } else if (dataFileTypes.includes(extension)) {
        this.loadingProgress = 0;
        (parsed ? Promise.resolve(parsed)
          : Promise.reject(new ImportError('unsupported', file.name, extension))).then(async r => {
          if (epoch !== this.loadEpoch) {
            this.loadingProgress = -1;
            resolve(null);
            return;
          }
          this.beginUndoGroup();
          try {
            this.dropUnknownProducts(r.geometries);
            // A hole is never priced (see setHole), whatever the file said.
            r.geometries.forEach(geo => {
              if (geo instanceof Polygon && geo.isHole) {
                geo.productId = null;
              }
            });
            this.addGeometries(r.geometries);
            // The holes in their areas before anything is saved: an area's
            // stored total is taken as it is saved, and one saved before its
            // holes were linked kept them in it -- the developer API's
            // totalArea among others -- until someone next edited the shape.
            this.updateHoles();
            this.saveGeometries(r.geometries, {record: true, label: UndoLabel.IMPORT});
            if (r.groups?.length) {
              // Sent with the shapes, not waited on: nothing below needs them saved.
              this.saveGeoGroupsData(r.groups);
            }
            this.applyImportedProjectSettings(r.project);
            r.geometries.every(geo => {
              if (geo instanceof Polygon && !geo.isLocked) {
                this.currentPolygon = geo;
                return false;
              }
              return true;
            });

            const pid = this.currentProject.project_id;
            for (const layer of r.layers) {
              if (epoch !== this.loadEpoch) {
                break;
              }
              layer.projectId = pid;
              if (layer.blob && !layer.id) {
                // An image that came inside the file (a KMZ ground overlay):
                // uploaded like one the user placed, already positioned.
                await this.uploadLayer(layer).catch(e => Logger.error('Overlay upload failed', e));
              } else if (layer.id) {
                // Queued with the shapes rather than waited for: offline the
                // write never settled, and the import's undo step stayed open
                // with Undo dead until the connection came back.
                this.writeDoc('layer', layer.id, layer);
                await this.addMapLayer(layer);
                if (epoch !== this.loadEpoch) {
                  break;
                }
                this.recordChanges([{kind: 'layer', id: layer.id, before: null, after: this.layerSnapshot(layer.id)}],
                  UndoLabel.IMPORT);
              }
            }
          } finally {
            this.endUndoGroup();
          }

          this.loadingProgress = -1;
          if (epoch !== this.loadEpoch) {
            resolve(r);
            return;
          }
          this.dispatchOnGeometriesChanged()
          const pointsCount = r.geometries.reduce((sum, g) => sum + (g instanceof Polygon ? g.points.length : 1), 0);
          this.onLoadingFinished.emit(pointsCount);
          resolve(r);
        }).catch(reason => {
          this.onLoadingFinished.emit();
          reject(reason);
        });
      } else {
        // Nothing reads this type. Saying so beats a promise that never settles.
        reject(new ImportError('unsupported', file.name, extension));
      }
    });
  }

  /**
   * Settings an .mmp carried (tax rate, client, default product), filled in
   * only where this project has none: importing into a project that already
   * has its own rate or client must not change its quote.
   */
  private applyImportedProjectSettings(settings: FileImport['project']) {
    const project = this.currentProject;
    if (!settings || !project?.project_id) {
      return;
    }
    const fields: any = {};
    if (typeof settings.taxRate === 'number' && isFinite(settings.taxRate)
      && (project.tax_rate === undefined || project.tax_rate === null)) {
      fields.tax_rate = Math.min(100, Math.max(0, settings.taxRate));
    }
    if (settings.clientName && !project.client_name) {
      fields.client_name = String(settings.clientName).slice(0, 80);
    }
    // Only a product of this project's catalogue that is still offered. An
    // .mmp made in another account named one that does not exist here, and
    // every shape drawn afterwards was given it.
    const catalogue = settings.defaultProductId && !project.default_product_id ? this.loadedCatalogue() : null;
    const product: any = catalogue ? catalogue[settings.defaultProductId] : null;
    if (product && !product.archived && product.enabled !== false) {
      fields.default_product_id = settings.defaultProductId;
    }
    if (!Object.keys(fields).length) {
      return;
    }
    // Project settings are for whoever manages the project (canManageProject,
    // as the database rules have it). A visitor who may draw on a Public or
    // Open Guest project may import too, and had them applied in this browser
    // only: the write was refused, and only logged, and the project's own
    // settings came back with its next update. They are left as they are.
    // The demo keeps them in this browser, its project not being in the
    // database.
    const demo = this.isDemoMode();
    if (!demo && !this.canManageProject()) {
      return;
    }
    Object.assign(project, fields);
    if (demo) {
      return;
    }
    this.firebaseApi.updateProject(project.project_id, fields)
      .catch(e => Logger.error('Could not apply the imported project settings', e));
  }

  /**
   * The open project's product catalogue once it has been read; null until
   * then, since an empty catalogue and one not read yet look the same.
   */
  private loadedCatalogue(): Record<string, unknown> | null {
    const products = this.injector.get(ProductsService);
    return products.productsLoaded ? products.productsMap : null;
  }

  /**
   * Takes off the product an imported shape names when this project's
   * catalogue does not hold it -- another account's, from an .mmp made there.
   * It priced nothing here, and the picker showed no product while the shape
   * still pointed at one. Left alone while the catalogue has not been read:
   * nothing can be told then.
   */
  private dropUnknownProducts(geometries: BaseGeometry[]) {
    const catalogue = this.loadedCatalogue();
    if (!catalogue) {
      return;
    }
    geometries.forEach(geo => {
      if ((geo.productIds || []).some(id => !catalogue[id])) {
        geo.setProducts(geo.productIds.filter(id => !!catalogue[id]));
      }
    });
  }

  // Firebase stuff
  /**
   * `reload` is for the project already open (after a version restore): a
   * read that fails leaves its map to the listeners rather than finishing an
   * empty load.
   */
  loadFromDatabase(project: ProjectData, progressCallback: ProgressCallback,
                   options: {keepHistory?: boolean, reload?: boolean} = {}): Promise<LoadProjectStatus> {
    // Before currentProject changes: anything still queued is the old project's.
    this.flushWrites();
    this.currentProject = project;
    this.firebaseApi.currentProjectId = project.project_id;
    // The project's own shapes again, after any raw data view.
    this.rawView = false;
    return new Promise<LoadProjectStatus>((resolve, reject) => {

      this.onLoadingStarted.emit();

      this.resetLocalState({keepHistory: options.keepHistory});

      this.setDataEventsEnabled(false);

      if (this.isDemoMode()) {
        this.finishLoadingGeometries([], {});

        resolve(LoadProjectStatus.SUCCESS);
        this.onLoadingFinished.emit(0);
        return;
      }

      // Another project opened, or this one left or reloaded, while this load
      // was on its way: a slow project's shapes, layers and counts landed in
      // the one opened after it, and its next save wrote them there.
      const epoch = this.loadEpoch;
      const superseded = () => epoch !== this.loadEpoch || this.currentProject?.project_id !== project.project_id;
      // Before the reads below, so that they and the listeners started once
      // the shapes are in share one download (see holdProjectReads).
      const releaseReads = this.holdProjectReads(project.project_id);
      const currentWorkRef = this.firebaseApi.getProjectDataRef(project.project_id).ref;
      // The groups are read here too, not only by the listener: a visitor who
      // may only look (a view-only link, a public project) gets no listener,
      // and saw every shape ungrouped, without the groups' names, colours and
      // bounds. Without them the shapes still load.
      const groupsRead = this.firebaseApi.getProjectDataGroupsRef(project.project_id).ref.get()
        .catch(error => {
          Logger.error('ERROR Loading groups', error);
          return null as firebase.firestore.QuerySnapshot;
        });
      Promise.all([currentWorkRef.get(), groupsRead]).then(([geosSnapshot, groupsSnapshot]) => {
        if (superseded()) {
          releaseReads();
          resolve(LoadProjectStatus.SUPERSEDED);
          return;
        }
        // Before the shapes are listed, which sorts them into their groups.
        groupsSnapshot?.forEach(groupDoc => {
          if (!this.dataGroups[groupDoc.id]) {
            this.dataGroups[groupDoc.id] = groupDoc.data();
          }
        });

        const geometries = [];
        const geometriesData = {};
        Logger.log('Got Geometries - Loading Now');

        let count = 0;
        let pointsCount = 0;
        try {
          if (!geosSnapshot.empty) {
            const totalGeometriesCount = geosSnapshot.size;
            Logger.time('loadGeos');
            Logger.log('TOTAL POLYGONS to load: ' + totalGeometriesCount);
            let currentPolygon = null;
            geosSnapshot.forEach(geoDoc => {
              // The model gets its own copy of the cached document, its
              // vertices made sound (see upsertGeoLocal).
              geometriesData[geoDoc.id] = geoDoc.data();
              const data = withSoundVertices(cloneDoc(geometriesData[geoDoc.id]));
              let geo;
              Logger.log('LOADING GEO: ', data);
              if (data.type === GeometryType.TYPE_POLYGON) {
                const polygon = GeometryConverter.dataToPolygon(data);
                const pc = polygon.getPointsCount();
                if (pc > 100) {
                  polygon.showMiddleDistances = false;
                }
                pointsCount += pc;
                geo = polygon;
                if (this.isCurrentPolygon(polygon)) {
                  currentPolygon = polygon;
                }

                polygon.update();
              } else if (data.type === GeometryType.TYPE_SPOT) {
                const spot = GeometryConverter.dataToSpot(data);
                pointsCount++;
                geo = spot;
              } else if (data.type === GeometryType.TYPE_CONTOUR_LAYER) {
                // const layer = createLayerFromData(data); TODO
                // geo = layer;
                // layer.create(false);

              }
              if (geo) {
                Logger.log('GEO LOADED: ', geo.name);
                geometries.push(geo);
              }
              count++;

              const progress = count / totalGeometriesCount;
              progressCallback.onProgressChanged(geo, progress);
            });
            this.currentPolygon = currentPolygon;
            Logger.timeEnd('loadGeos');
            Logger.log('FINISHED LOADING: ' + totalGeometriesCount + ' geometries');
            progressCallback.onProgressChanged(null, 100);
          }

        } catch (e) {
          Logger.error(e);
        } finally {
          this.finishLoadingGeometries(geometries, geometriesData);
          // The listeners it started now hold the project's shapes and groups.
          releaseReads();
          this.loadMapLayers(project.project_id);

          resolve(LoadProjectStatus.SUCCESS);
          this.onLoadingFinished.emit(pointsCount);
        }


      }).catch(error => {
        // The Promise was rejected.
        Logger.error('ERROR Loading geos', error);
        releaseReads();
        if (superseded()) {
          resolve(LoadProjectStatus.SUPERSEDED);
          return;
        }
        if (options.reload) {
          // The project stays open. Its listeners bring the map in as soon as
          // it can be read (they retry on their own); without them it stayed
          // empty and followed nothing. Nor is the load reported finished:
          // that saved the empty map's counts and picture over the project's.
          this.loadingProgress = -1;
          this.setDataEventsEnabled(true);
          resolve(LoadProjectStatus.ERROR);
          return;
        }
        this.onLoadingFinished.emit();
        resolve(LoadProjectStatus.ERROR);
      });
    });
  }

  /**
   * Shows pasted raw shape data (support's Load raw) in place of the open
   * project's, read-only: canEdit() is false until a project is loaded again,
   * so nothing on the map is saved, and nothing follows the project live.
   * The open project stays the open project, for everything else to read.
   */
  loadRawData(rawData: any, progressCallback?: ProgressCallback) {
    return new Promise<LoadProjectStatus>((resolve, reject) => {

      this.onLoadingStarted.emit();

      // Before the reset, so that the canvas already sees a read-only map.
      this.rawView = true;
      this.resetLocalState();

      this.setDataEventsEnabled(false);

      if (this.isDemoMode()) {
        this.finishLoadingGeometries([], {}, false);

        resolve(LoadProjectStatus.SUCCESS);
        this.onLoadingFinished.emit(0);
        return;
      }

      const geometries = [];
      const geometriesData = {};
      Logger.log('Got Geometries - Loading Now');

      let count = 0;
      let pointsCount = 0;
      try {
        if (!rawData.empty) {
          const totalGeometriesCount = rawData.length;
          Logger.time('loadGeos');
          Logger.log('TOTAL POLYGONS to load: ' + totalGeometriesCount);
          let currentPolygon = null;
          rawData.forEach(raw => {
            // The model gets its own copy of the cached document (see upsertGeoLocal).
            geometriesData[raw._id] = raw;
            const data = cloneDoc(raw);
            let geo;
            Logger.log('LOADING GEO: ', data);
            if (data.type === GeometryType.TYPE_POLYGON) {
              const polygon = GeometryConverter.dataToPolygon(data);
              const pc = polygon.getPointsCount();
              if (pc > 100) {
                polygon.showMiddleDistances = false;
              }
              pointsCount += pc;
              geo = polygon;
              if (this.isCurrentPolygon(polygon)) {
                currentPolygon = polygon;
              }

              polygon.update();
            } else if (data.type === GeometryType.TYPE_SPOT) {
              const spot = GeometryConverter.dataToSpot(data);
              pointsCount++;
              geo = spot;
            } else if (data.type === GeometryType.TYPE_CONTOUR_LAYER) {
              // const layer = createLayerFromData(data); TODO
              // geo = layer;
              // layer.create(false);
            }
            if (geo) {
              Logger.log('GEO LOADED: ', geo.name);
              geometries.push(geo);
            }
            count++;

            const progress = count / totalGeometriesCount;
            progressCallback?.onProgressChanged(geo, progress);
          });
          this.currentPolygon = currentPolygon;
          Logger.timeEnd('loadGeos');
          Logger.log('FINISHED LOADING: ' + totalGeometriesCount + ' geometries');
          progressCallback?.onProgressChanged(null, 100);
        }

      } catch (e) {
        Logger.error(e);
      } finally {
        this.finishLoadingGeometries(geometries, geometriesData, false);

        resolve(LoadProjectStatus.SUCCESS);
        this.onLoadingFinished.emit(pointsCount);
      }

    });
  }

  /**
   * Saves some of a picture's fields -- where its box is, its turn, its
   * raise in 3D -- merged into its document (the 3D view's gizmo; the panel
   * saves its own).
   */
  updateLayerFields(layer: Layer, fields: Partial<Layer>): Promise<void> {
    if (!layer?.id || !layer.projectId) {
      return Promise.reject(new Error('no layer'));
    }
    return this.firebaseApi.updateLayerData(layer.projectId, layer.id, {...fields}) as Promise<void>;
  }

  uploadLayer(layerRequest: Layer) {
    return new Promise<Layer>((resolve, reject) => {
      this.onLoadingStarted.emit();
      this.loadingProgress = 0;
      if (layerRequest.id) {
        this.firebaseApi.updateLayerData(layerRequest.projectId, layerRequest.id, layerRequest).then(layer => {
          this.loadingProgress = -1;
          resolve(layerRequest);
        }).catch(reason => {
          // Only the spinner stops. "Loading finished" refitted the camera to
          // the whole project and rewrote its summary; the caller reports it.
          this.loadingProgress = -1;
          reject(reason);
        });
      } else {
        // Uploaded to the project it was added in; if another has been opened
        // since, it is not put on that one's map or in its history.
        const epoch = this.loadEpoch;
        this.firebaseApi.uploadLayer(layerRequest, progress => {
          this.loadingProgress = progress;
        }).then(layer => {
          if (epoch !== this.loadEpoch) {
            this.loadingProgress = -1;
            resolve(layer);
            return;
          }
          this.addMapLayer(layer).then(value => {
            if (epoch !== this.loadEpoch) {
              this.loadingProgress = -1;
              resolve(layer);
              return;
            }
            if (layer.type === LayerType.IMAGE_OVERLAY) {
              layer.placeholderUrl = layerRequest.downloadUrl;
            }
            // Adding a layer is a step like drawing a shape. Undo removes its
            // document; the file stays for a redo or a restore point.
            this.layerDocs.set(layer.id, this.cleanLayerDoc(layer));
            this.recordChanges([{kind: 'layer', id: layer.id, before: null, after: this.layerSnapshot(layer.id)}],
              UndoLabel.IMPORT);
            this.loadingProgress = -1;
            resolve(layer);
          });
        }).catch(reason => {
          this.loadingProgress = -1;
          reject(reason);
        });
      }
    });
  }

  /**
   * Removes a map layer as an undoable step. Only its document goes: the file
   * stays while an undo or a restore point may still need it, and the backup
   * clean-up on the server deletes it once nothing refers to it
   * (project-backups.ts). It used to delete the file at once, so a layer
   * removed by mistake was gone for good.
   */
  deleteMapLayer(layer: Layer) {
    if (!this.canEdit()) {
      return Promise.resolve();
    }
    const change: DocChange = {kind: 'layer', id: layer.id, before: this.layerSnapshot(layer.id), after: null};
    this.applyDocStates([{kind: 'layer', id: layer.id, data: null}], {write: true});
    this.recordChanges([change], UndoLabel.DELETE);
    // Done once it is off the map; the save is not waited for (see saveGeoGroupsData).
    this.flushWrites();
    return Promise.resolve();
  }

  /** A layer document for the history: as stored, minus the last-update stamps, timestamp intact. */
  private layerSnapshot(id: string): any {
    const doc = this.layerDocOf(id);
    if (!doc) {
      return null;
    }
    const copy = this.cleanLayerDoc(doc);
    [PolygonField.KEY_LAST_UPDATE_USER, PolygonField.KEY_LAST_UPDATE_DEVICE, PolygonField.KEY_LAST_UPDATE_TIMESTAMP,
      PolygonField.KEY_LAST_UPDATE_SESSION_ID].forEach(key => delete copy[key]);
    return copy;
  }

  /**
   * The demo: asked for in the address, or its map still open. Once seen, the
   * address's demoMode used to hold for the rest of the session, so a real
   * project opened afterwards was treated as the demo: read-only, and followed
   * by nothing.
   */
  isDemoMode() {
    return !!this.activatedRoute.snapshot.queryParams.demoMode
      || this.currentProject?.project_id === DEMO_PROJECT_ID;
  }

  canEdit() {
    if (this.isDemoMode()) {
      return false;
    }

    // Organization roles: a Viewer may open, measure-read and export work,
    // but never change it. Checked here because every editing path in the
    // canvas funnels through canEdit().
    if (!this.permissions.canEditProjects) {
      return false;
    }

    // Pasted raw data is looked at, never saved into the open project.
    if (this.rawView) {
      return false;
    }

    const project = this.currentProject;
    if (project) {
      // A project in the trash is deleted as far as its owner knows: it is
      // read-only for everyone, support included, until it is restored. Its
      // members, link visitors and embed went on drawing on it.
      if (project.trashed) {
        return false;
      }
      // Its own people on a plan that has ended may look, not change
      // (CanvasAuthGuardService, PlanAccessState).
      if (this.planAccess.isLapsed(project.project_id) && !this.authService.isGod()) {
        return false;
      }
      return (this.authService.isGod()
          // Its creator or a member. The creator counts even when no longer
          // listed among the members, as in the rules (a folder share ending
          // takes them off every project in it): they were left read-only on
          // their own project.
          || (this.authService.isRealUser() && (this.isProjectMember()
            // A visitor edits a Public project only with a verified email, as
            // the rules say: an unverified one was shown the tools, and every
            // save was refused and lost.
            || (project.access_level === ProjectAccessLevel.PUBLIC && this.hasVerifiedEmail())))
          || (this.authService.isLoggedIn() && project.access_level === ProjectAccessLevel.OPEN_GUEST)
          || (this.authService.hasActiveEnterprisePlan() && this.authService.currentUserInfo.enterpriseUsers.includes(project.creator_id))
          // An Admin or Manager works on every project of the organisation,
          // as the rules allow; they were left read-only on its members' work.
          || this.organization.isStaffFor(project.creator_id)
        )
        // An embed is for looking, so editing stops inside an iframe -- except on
        // an Open Guest project, whose embed exists precisely so a visitor can
        // measure on the owner's own website. It was blocked there too.
        && (!CommonUtil.inIframe() || project.access_level === ProjectAccessLevel.OPEN_GUEST);
    }
    return false;
  }

  private hasVerifiedEmail(): boolean {
    return !!this.authService.currentUser?.emailVerified;
  }

  /**
   * A signed-in visitor of a Public project who may edit it once their email
   * is verified: the canvas tells them so rather than just "View only".
   */
  editingNeedsVerifiedEmail(): boolean {
    const project = this.currentProject;
    return !!project && project.access_level === ProjectAccessLevel.PUBLIC && !project.trashed && !this.rawView
      && this.authService.isRealUser() && !this.hasVerifiedEmail()
      && this.permissions.canEditProjects && !CommonUtil.inIframe() && !this.canEdit();
  }

  /** The string key that says why the map is view-only, for the canvas's "View only" badge. */
  viewOnlyReasonKey(): string {
    if (this.rawView) {
      return 'view_only_raw_data';
    }
    if (this.currentProject?.trashed) {
      return 'view_only_trashed';
    }
    if (this.currentProject && this.planAccess.isLapsed(this.currentProject.project_id)) {
      return 'view_only_plan_lapsed';
    }
    return this.editingNeedsVerifiedEmail() ? 'view_only_verify_email' : 'view_only';
  }

  /** On the project itself -- its creator or a member -- rather than visiting it. */
  private isProjectMember(): boolean {
    const project = this.currentProject;
    const uid = this.authService.getUserId;
    return !!project && !!uid && this.authService.isRealUser()
      && (project.creator_id === uid || (project.members || []).includes(uid));
  }

  /**
   * Whether this session may clear the whole map or put back an earlier
   * version: the people who work on the project (its creator, its members, the
   * creator's organisation owner, Admins and Managers, support) -- not the visitors a shared link
   * lets draw, not an organisation Viewer, and never inside an embed. The
   * server checks the same for restore points (project-backups-api.ts).
   */
  canManageProject(): boolean {
    const project = this.currentProject;
    if (!project || !this.canEdit() || CommonUtil.inIframe() || !this.authService.isRealUser()) {
      return false;
    }
    const info = this.authService.currentUserInfo;
    return this.authService.isGod() || this.isProjectMember()
      || (this.authService.hasActiveEnterprisePlan() && !!info && (info.enterpriseUsers || []).includes(project.creator_id))
      || this.organization.isStaffFor(project.creator_id);
  }

  newDataGroup(){
    const id = this.getProjectDataGroupsRef().ref.doc().id
    const groupData = new GeoGroupData();
    groupData.id = id;
    groupData.name = '';
    groupData.color = this.settingsService.local.createGeoGroupColor;
    groupData.projectId = this.currentProject.project_id;
    return groupData;
  }

  /**
   * Creates or changes a group, as an undoable step. Fields the app does not
   * model are kept (the document is written whole now, not merged).
   */
  saveGeoGroupData(groupData: GeoGroupData): Promise<void> {
    return this.saveGeoGroupsData([groupData]);
  }

  /**
   * Several groups in one pass: one step, one list refresh, one write. An
   * import saved its folders one awaited round trip at a time, each
   * rebuilding the whole list, with Undo held up all the while.
   *
   * Resolves once the groups are in place here, not when Firestore has them:
   * that waited for every save in flight (a whole import's), and offline it
   * never came, which left the Create Group dialog saving with no way out. A
   * save that fails is reported like any other (failedToSaveEvent).
   */
  private saveGeoGroupsData(groups: GeoGroupData[]): Promise<void> {
    const changes: DocChange[] = [];
    for (const groupData of groups) {
      const group = ObjectUtil.assignIfExists(new GeoGroupData(), groupData);
      group.projectId = this.currentProject.project_id;
      group.updatedOn = Date.now();
      const existing = this.dataGroups[group.id];
      const data = {...(existing ? withoutBookkeeping(existing) : {}), ...JSON.parse(JSON.stringify(group))};
      const before = existing ? withoutBookkeeping(existing) : null;
      this.dataGroups[group.id] = data;
      this.writeDoc('group', group.id, data);
      changes.push({kind: 'group', id: group.id, before, after: withoutBookkeeping(data)});
    }
    this.recordChanges(changes, UndoLabel.GROUP);
    this.dispatchOnGeometriesChanged();
    this.flushWrites();
    return Promise.resolve();
  }

  /**
   * Deletes a group -- with its shapes, or leaving them ungrouped -- as one
   * step. The group document used to be deleted outside the history, so undo
   * gave the shapes back a group that no longer existed and they vanished from
   * every list; and the shapes were deleted one awaited call at a time, which
   * spread them over several steps.
   */
  deleteGeoGroup(groupData: GeoGroup, deleteGeometries: boolean = false): Promise<void> {
    if (!this.canEdit() || !groupData?.id) {
      return Promise.resolve();
    }
    // The group's members as they are now, not as the list showed them when
    // the confirmation opened: a shape a collaborator moved to another group
    // meanwhile was deleted (or taken out of its new group) with this one,
    // and one moved into it was left pointing at a group that no longer exists.
    const members = [...this.polygons, ...this.spots]
      .filter(geo => geo.groupId === groupData.id) as Array<Polygon | Spot>;
    const changes: DocChange[] = [];
    const targets: DocTarget[] = [];
    if (this.dataGroups[groupData.id]) {
      changes.push({kind: 'group', id: groupData.id, before: withoutBookkeeping(this.dataGroups[groupData.id]), after: null});
      targets.push({kind: 'group', id: groupData.id, data: null});
    }
    if (deleteGeometries) {
      const removed = this.withHoles(members);
      for (const geo of removed) {
        changes.push({kind: 'geo', id: geo.id, before: this.snapshotOf(geo), after: null});
        targets.push({kind: 'geo', id: geo.id, data: null});
      }
      // Buffers in other groups keep their bands (see bufferRingsFor).
      for (const ring of this.bufferRingsFor(new Set(removed.map(geo => geo.id)))) {
        changes.push({kind: 'geo', id: ring.id, before: null, after: ring.data});
        targets.push(ring);
      }
    } else {
      for (const geo of members) {
        const before = this.snapshotOf(geo);
        const after = {...before, [PolygonField.KEY_GROUP_ID]: ''};
        changes.push({kind: 'geo', id: geo.id, before, after});
        targets.push({kind: 'geo', id: geo.id, data: after});
      }
    }
    this.applyDocStates(targets, {write: true});
    this.recordChanges(changes, UndoLabel.GROUP, {standalone: true});
    // Done once it is off the map; the save is not waited for (see saveGeoGroupsData).
    this.flushWrites();
    return Promise.resolve();
  }

  // ── Clearing, resetting and restoring ─────────────────────────────────────

  /**
   * Empties the map in this browser only: switching projects, or reloading one.
   * Never touches the database. Whatever was still waiting to be saved is sent
   * first, to the project it belongs to, and the undo history goes with the
   * shapes it points at (unless the caller keeps a step that survives reloads,
   * as restoring a version does).
   */
  resetLocalState(options: {keepHistory?: boolean} = {}) {
    this.flushWrites();
    clearTimeout(this.pendingTimer);
    this.pendingTimer = null;
    this.pendingChanges = new Map();
    this.pendingLabel = null;
    this.undoGroupDepth = 0;
    if (!options.keepHistory) {
      this.history.clear();
    }
    // Every shape object from here on belongs to the next load. One still
    // held from this load is refused by saveGeometry (see geoEpoch), so the
    // ids removed during this load need not be remembered past it.
    this.loadEpoch++;
    this.removedIds.clear();
    this.remoteChangedAt.clear();
    this.unsavedDocs.clear();
    this.unreadableLayers.clear();

    this.polygons = [];
    this.spots = [];
    this.layers = [];
    this.geoIndex.clear();
    this.layerDocs.clear();
    this.dataGroups = {};
    this.fireGeometries = {};
    this.loadingLayers.clear();

    if (this.mapService.map && this.mapService.map.overlayMapTypes) {
      this.mapService.map.overlayMapTypes.clear();
    }
    // Every MBTiles database closes with its layer, those of a map already
    // gone (the canvas was left) included: sql.js frees a file only on close.
    MBTilesProvider.disposeAll();
    this.currentPolygon = null;

    this.dispatchOnGeometriesChanged();
    this.mapCleared.emit({reset: true});
  }

  /**
   * The demo map lives in this browser only; its bin empties it, with no
   * restore point to take and nothing to write.
   */
  clearDemoMap() {
    const all = [...this.polygons, ...this.spots];
    this.removeGeosLocal(new Set(all.map(g => g.id)));
    this.updateHoles();
    this.dispatchOnGeometriesChanged();
    this.mapCleared.emit({reset: false});
  }

  /** The canvas hands over the comments it listens to. */
  setProjectComments(comments: ProjectComment[] | null) {
    this.projectComments = Array.isArray(comments) ? comments : [];
  }

  /** The load (and project) each clear plan was made for: clearItems refuses one made for another. */
  private readonly planOrigins = new WeakMap<ClearPlan, {epoch: number, projectId: string}>();

  /**
   * What a Clear Map choice would remove, counted. Pure: the dialog calls it on
   * every change to show the numbers before anything happens.
   */
  planClear(selection: ClearSelection): ClearPlan {
    const counts: ClearCounts = emptyCounts();
    const view = selection.scope === 'view' && selection.bounds ? new google.maps.LatLngBounds(selection.bounds) : null;
    const groupIds = selection.scope === 'groups' ? new Set(selection.groupIds || []) : null;
    const inScope = (geo: Polygon | Spot) => {
      if (groupIds) {
        const group = geo.groupId && this.dataGroups[geo.groupId] ? geo.groupId : NO_GROUP_ID;
        return groupIds.has(group);
      }
      if (view) {
        if (geo instanceof Spot) {
          return view.contains(geo.latLng);
        }
        const bounds = (geo as Polygon).getBounds();
        return !!bounds && view.intersects(bounds);
      }
      return true;
    };

    const polygons: Polygon[] = [];
    for (const polygon of this.polygons) {
      // Holes go with the area they are cut out of, never on their own.
      if (polygon.isHole) {
        continue;
      }
      const isLine = !polygon.showSurface;
      if ((isLine ? selection.lines : selection.areas) && inScope(polygon)) {
        polygons.push(polygon);
        if (isLine) {
          counts.lines++;
        } else {
          counts.areas++;
        }
      }
    }
    const spots = selection.spots ? this.spots.filter(inScope) : [];
    counts.spots = spots.length;

    // Groups have no place on the map, so there is nothing to clear "in view".
    let groups: GeoGroupData[] = [];
    if (selection.groups && selection.scope !== 'view') {
      groups = (ObjectUtil.toArray(this.dataGroups) as GeoGroupData[])
        .filter(g => !groupIds || groupIds.has(g.id));
    }
    counts.groups = groups.length;

    // Layers belong to no group; in view means their extent overlaps it.
    let layers: Layer[] = [];
    if (selection.scope !== 'groups') {
      layers = this.layers.filter(layer => {
        const isImage = layer.type === LayerType.IMAGE_OVERLAY;
        if (!(isImage ? selection.images : selection.layers)) {
          return false;
        }
        return !view || (!!layer.bounds && view.intersects(new google.maps.LatLngBounds(layer.bounds)));
      });
    }
    counts.images = layers.filter(l => l.type === LayerType.IMAGE_OVERLAY).length;
    counts.layers = layers.length - counts.images;

    // Comments sit on the map but in no group.
    let comments: ProjectComment[] = [];
    if (selection.comments && selection.scope !== 'groups') {
      comments = this.projectComments.filter(c => !!c && !!c.id
        && (!view || view.contains({lat: Number(c.latitude), lng: Number(c.longitude)})));
    }
    counts.comments = comments.length;

    const removedGeos = this.withHoles([...polygons, ...spots]);
    const removedIds = new Set(removedGeos.map(g => g.id));
    const removedGroups = new Set(groups.map(g => g.id));
    const ungrouped = [...this.polygons, ...this.spots]
      .filter(geo => geo.groupId && removedGroups.has(geo.groupId) && !removedIds.has(geo.id));

    const plan: ClearPlan = {
      polygons: removedGeos.filter(g => g instanceof Polygon) as Polygon[],
      spots,
      groups,
      layers,
      comments,
      ungrouped,
      counts,
      total: counts.areas + counts.lines + counts.spots + counts.groups + counts.images + counts.layers
        + counts.comments,
    };
    this.planOrigins.set(plan, {epoch: this.loadEpoch, projectId: this.currentProject?.project_id});
    return plan;
  }

  /**
   * Takes the restore point a clear would take, and clears nothing: an import
   * that replaces the map takes it first, lets the server add the file, and
   * clears the old map only once something has come in (clearItems, backup
   * false). Throws as clearItems does: RestorePointError when it cannot be
   * taken, ClearSupersededError when another project has been opened.
   */
  async takeRestorePoint(plan: ClearPlan, trigger: 'clear' | 'import'): Promise<void> {
    if (!this.canManageProject()) {
      throw new Error('not allowed');
    }
    const epoch = this.loadEpoch;
    const projectId = this.currentProject?.project_id;
    const origin = this.planOrigins.get(plan);
    const superseded = () => epoch !== this.loadEpoch || this.currentProject?.project_id !== projectId
      || (!!origin && (origin.epoch !== epoch || origin.projectId !== projectId));
    if (superseded()) {
      throw new ClearSupersededError();
    }
    await this.restorePointFor(plan, trigger, projectId, superseded);
  }

  private async restorePointFor(plan: ClearPlan, trigger: 'clear' | 'import', projectId: string,
                                superseded: () => boolean): Promise<void> {
    this.flushUndo();
    const counts = plan.counts;
    try {
      // Saves that cannot arrive (offline) make it a restore point that
      // cannot be taken, which the caller can choose to go ahead without.
      await this.flushWritesWithin(RESTORE_POINT_SAVE_WAIT_MS);
      if (superseded()) {
        throw new ClearSupersededError();
      }
      await this.backupsApi.create(projectId, trigger, {
        areas: counts.areas, lines: counts.lines, spots: counts.spots, groups: counts.groups,
        layers: counts.images + counts.layers, comments: counts.comments,
      });
    } catch (e) {
      // Not a question of going ahead without one: the map is not there.
      if (e instanceof ClearSupersededError || superseded()) {
        throw new ClearSupersededError();
      }
      Logger.error('Restore point failed', e);
      throw new RestorePointError(e);
    }
    if (superseded()) {
      throw new ClearSupersededError();
    }
  }

  /**
   * Clears what a plan names, after taking a restore point on the server.
   *
   * The restore point comes first and the clear waits for it: if it cannot be
   * taken, nothing is cleared and RestorePointError says why, so the caller can
   * ask whether to go ahead without one (pass `backup: false`). Saves still
   * waiting are sent first, so the restore point has the newest edits in it.
   *
   * The clear itself is one undo step, applied in one pass and written in
   * batches; Undo puts everything back, Redo clears it again. It used to throw
   * the undo history away.
   */
  async clearItems(plan: ClearPlan, options: {backup: boolean, trigger: 'clear' | 'import'}): Promise<void> {
    if (!this.canManageProject()) {
      throw new Error('not allowed');
    }
    // The restore point can take half a minute and more, with nothing on
    // screen to stop the user opening another project meanwhile; this then
    // cleared that one, and the import that waited on it went into it. A
    // plan made for another load is refused the same way.
    const epoch = this.loadEpoch;
    const projectId = this.currentProject?.project_id;
    const origin = this.planOrigins.get(plan);
    const superseded = () => epoch !== this.loadEpoch || this.currentProject?.project_id !== projectId
      || (!!origin && (origin.epoch !== epoch || origin.projectId !== projectId));
    if (superseded()) {
      throw new ClearSupersededError();
    }
    if (options.backup) {
      await this.restorePointFor(plan, options.trigger, projectId, superseded);
    }

    const changes: DocChange[] = [];
    const targets: DocTarget[] = [];
    for (const geo of [...plan.polygons, ...plan.spots]) {
      if (this.geoIndex.has(geo.id)) {
        changes.push({kind: 'geo', id: geo.id, before: this.snapshotOf(geo), after: null});
        targets.push({kind: 'geo', id: geo.id, data: null});
      }
    }
    // The bands of buffers left standing keep their measure (see bufferRingsFor).
    for (const ring of this.bufferRingsFor(new Set(targets.map(target => target.id)))) {
      changes.push({kind: 'geo', id: ring.id, before: null, after: ring.data});
      targets.push(ring);
    }
    for (const geo of plan.ungrouped) {
      if (this.geoIndex.has(geo.id)) {
        const before = this.snapshotOf(geo);
        const after = {...before, [PolygonField.KEY_GROUP_ID]: ''};
        changes.push({kind: 'geo', id: geo.id, before, after});
        targets.push({kind: 'geo', id: geo.id, data: after});
      }
    }
    for (const group of plan.groups) {
      if (this.dataGroups[group.id]) {
        changes.push({kind: 'group', id: group.id, before: withoutBookkeeping(this.dataGroups[group.id]), after: null});
        targets.push({kind: 'group', id: group.id, data: null});
      }
    }
    for (const layer of plan.layers) {
      // Only a layer still on the map, as for shapes and groups above.
      if (!this.layers.some(onMap => onMap.id === layer.id)) {
        continue;
      }
      changes.push({kind: 'layer', id: layer.id, before: this.layerSnapshot(layer.id), after: null});
      targets.push({kind: 'layer', id: layer.id, data: null});
    }
    // Comments go through the API (the browser cannot write them), into the
    // comment trash, so the step's Undo can bring them back. First: if this
    // fails, nothing has been cleared yet.
    let clearedComments: string[] = [];
    if (plan.comments && plan.comments.length) {
      const result = await this.projectsApi.clearComments(projectId, plan.comments.map(c => c.id));
      clearedComments = (result && Array.isArray(result.cleared)) ? result.cleared : [];
    }
    if (!targets.length && !clearedComments.length) {
      return;
    }
    const label = options.trigger === 'import' ? UndoLabel.IMPORT : UndoLabel.CLEAR;
    if (targets.length) {
      this.geometriesChanged = true;
      this.applyDocStates(targets, {write: true});
      this.recordChanges(changes, label, {standalone: true});
      this.flushWrites();
      this.mapCleared.emit({reset: false});
    }
    if (clearedComments.length && this.canRecordUndo()) {
      this.flushUndo();
      // The step just recorded, or one of its own when only comments went.
      let step = targets.length ? this.history.peekUndo() as MapCommand : null;
      if (!step || step.label !== label) {
        step = this.makeCommand(label, []);
        this.history.add(step);
      }
      step.comments = {projectId, ids: clearedComments};
    }
  }

  /**
   * Saves the map as it is now as a restore point, on request -- before a risky
   * change, say. Pending saves go first, so it holds the newest edits.
   */
  async saveRestorePoint(): Promise<void> {
    if (!this.canManageProject()) {
      throw new Error('not allowed');
    }
    this.flushUndo();
    await this.flushWritesWithin(RESTORE_POINT_SAVE_WAIT_MS);
    await this.backupsApi.create(this.currentProject.project_id, 'manual');
  }

  /**
   * Puts back an earlier version of the project, on the server, then reloads
   * the map. The server takes a restore point of the current map first; the
   * restore is then a step of its own, so Undo (or the "Undo" on the message)
   * goes back to the map as it was, and Redo restores the version again.
   * Every other step is dropped: they point at shapes the reload replaced.
   */
  async restoreVersion(backupId: string): Promise<RestoreResult> {
    if (!this.canManageProject()) {
      throw new Error('not allowed');
    }
    const projectId = this.currentProject.project_id;
    const {result, reloadFailed} = await this.restoreOnServer(backupId);
    if (this.isOpen(projectId)) {
      const command = this.makeRestoreCommand(backupId, result.undoBackup.id);
      this.history.retainOnly(command, false);
    }
    // Restored, and undoable, all the same; "Version restored" over an empty
    // map would not be true.
    if (reloadFailed) {
      throw new RestoreReloadError();
    }
    return result;
  }

  /**
   * Restores on the server, then reloads the map -- unless another project
   * was opened while the server worked: the restore stands, but that
   * project's map is not reloaded with it, nor its history given the step.
   *
   * `reloadFailed`: restored, but the map could not be read back. Its
   * listeners bring it in once it can be (see loadFromDatabase's `reload`).
   */
  private async restoreOnServer(backupId: string): Promise<{result: RestoreResult, reloadFailed: boolean}> {
    const projectId = this.currentProject.project_id;
    this.flushUndo();
    // Saves still on their way would land on top of the restored version, so
    // the restore does not start without them.
    await this.flushWritesWithin(RESTORE_POINT_SAVE_WAIT_MS);
    const result = await this.backupsApi.restore(projectId, backupId);
    let reloadFailed = false;
    if (this.isOpen(projectId)) {
      const status = await this.loadFromDatabase(this.currentProject, {onProgressChanged: () => null},
        {keepHistory: true, reload: true});
      reloadFailed = status === LoadProjectStatus.ERROR;
    }
    return {result, reloadFailed};
  }

  private isOpen(projectId: string): boolean {
    return this.currentProject?.project_id === projectId;
  }

  /**
   * A restore as an undo step. Undoing it restores the restore point taken just
   * before it; redoing restores the version again. Either way the map is
   * reloaded, after which this is the only step left that still makes sense.
   */
  private makeRestoreCommand(forwardId: string, backwardId: string): MapCommand {
    const command: MapCommand = {
      label: UndoLabel.RESTORE,
      changes: [],
      appliedAt: Date.now(),
      conflicts: 0,
      undo: async () => {
        const projectId = this.currentProject.project_id;
        const {result, reloadFailed} = await this.restoreOnServer(backwardId);
        if (this.isOpen(projectId)) {
          const redo = this.makeRestoreCommand(forwardId, result.undoBackup.id);
          this.history.retainOnly(redo, true);
        }
        if (reloadFailed) {
          throw new RestoreReloadError();
        }
      },
      redo: async () => {
        const projectId = this.currentProject.project_id;
        const {result, reloadFailed} = await this.restoreOnServer(forwardId);
        if (this.isOpen(projectId)) {
          const next = this.makeRestoreCommand(forwardId, result.undoBackup.id);
          this.history.retainOnly(next, false);
        }
        if (reloadFailed) {
          throw new RestoreReloadError();
        }
      },
    };
    return command;
  }

  onMeasureUnitsChanged() {
    this.measureUnitsChangedEvent.emit();
    setTimeout(() => {
      this.polygons.forEach(p => {
        p.updateLabel();
        p.calculateDistanceLabels();
      });
    }, 10);
  }

  onPolygonLabelSizeChanged() {
    setTimeout(() => {
      this.polygons.forEach(p => {
        p.updateLabel();
      });
    }, 10);
  }

  onDistanceLabelSizeChanged() {
    setTimeout(() => {
      this.polygons.forEach(p => {
        p.calculateDistanceLabels();
      });
    }, 10);
  }

  onSpotLabelSizeChanged() {
    setTimeout(() => {
      this.spots.forEach(p => {
        p.updateLabel();
      });
    }, 10);
  }

  onDescriptionLabelSizeChanged() {
    setTimeout(() => {
      this.polygons.forEach(p => {
        p.updatePointDescriptionLabels();
      });
    }, 10);
  }

  onColorsChanged() {
    setTimeout(() => {
      const cp = this._currentPolygon;
      if (cp) {
        Logger.log('onColorsChanged ' + this.settings.fillColor);
        cp.fillColor = this.settings.fillColor;
        cp.fillOpacity = 1 - this.settings.fillOpacity;
        cp.lineWidth = this.lineWidthFor(cp);
        cp.lineColor = this.settings.lineColor;
        cp.lineStyle = this.settings.lineStyle;
        this.saveGeometry({addUndoCheckpoint: true}, cp);
      }
    }, 10);
  }

  onLineWidthChanged() {
    const currentPolygon = this._currentPolygon;
    if (currentPolygon) {
      currentPolygon.lineWidth = this.lineWidthFor(currentPolygon);
      this.saveGeometry({addUndoCheckpoint: true}, currentPolygon);
    }
  }

  onDistanceLabelVisibilityChanged(visible: boolean) {
    const currentPolygon = this._currentPolygon;
    if (currentPolygon) {
      currentPolygon.showMiddleDistances = visible;
      this.saveGeometry({addUndoCheckpoint: true}, currentPolygon);
    }
  }

  /**
   * Cut `polygon` in two along `splitPath`. Returns the two halves, or null
   * when the cut is refused (it does not make exactly two pieces) and nothing
   * changed, for the caller to tell the user.
   */
  splitPolygon(polygon: Polygon, splitPath: google.maps.LatLngLiteral[]): Polygon[] | null {

    // slice, not shift: the caller keeps its line intact when the cut is refused.
    if (splitPath[0].lat === splitPath[1].lat && splitPath[0].lng === splitPath[1].lng) {
      splitPath = splitPath.slice(1);
    }

    const p1 = this.clonePolygon(polygon);
    const p2 = this.clonePolygon(polygon);
    const pps = polygon.path

    if (p1.shape === PolygonShape.CIRCLE) {
      p1.points = [];
      p2.points = [];
      p1.shape = PolygonShape.POLYGON;
      p2.shape = PolygonShape.POLYGON;
      pps.forEach(p => {
        p1.points.push(this.newPoint(p1, p));
        p2.points.push(this.newPoint(p2, p));
      });
    }

    // Cut the polygon with the EXACT drawn polyline via JTS noding +
    // polygonization (handles arbitrary hand-drawn lines and any number of
    // holes — crossed holes open into notches along the line, uncrossed holes
    // are preserved). Replaces the fragile edge-index / ring-splice logic that
    // only worked when the line started/ended exactly on a polygon edge.
    const split = PolyUtil.splitByLine(this.firebaseApi, polygon, splitPath);
    if (!split || split.halves.length < 2) {
      Logger.error('splitPolygon: the drawn line does not divide the polygon');
      return null;
    }
    const [h1, h2] = split.halves;

    // Taken before anything moves: the step records the shape and its holes
    // going, and the two halves and theirs arriving -- one Undo, not three.
    const changes: DocChange[] = this.withHoles([polygon])
      .map(g => ({kind: 'geo' as MapDocKind, id: g.id, before: this.snapshotOf(g), after: null}));

    p1.points = h1.points;
    p1.points.forEach(pt => pt.parent = p1);
    p1.holes = [];
    h1.holes.forEach(raw => {
      p1.holes.push(this.buildHole(raw.points.map(p => ({lat: p.lat, lng: p.lng})), p1));
    });

    p2.points = h2.points;
    p2.points.forEach(pt => pt.parent = p2);
    p2.holes = [];
    h2.holes.forEach(raw => {
      p2.holes.push(this.buildHole(raw.points.map(p => ({lat: p.lat, lng: p.lng})), p2));
    });

    changes.push(...this.removeGeometries([polygon], {record: false}));

    p1.name += '-1';
    this.lockPolygon(p1, p1.name, true, false);

    p2.name += '-2';
    this.lockPolygon(p2, p2.name, true, false);

    for (const g of [p1, p2, ...p1.holes, ...p2.holes]) {
      changes.push({kind: 'geo', id: g.id, before: null, after: this.snapshotOf(g)});
    }
    this.updateHoles();
    this.dispatchOnGeometriesChanged();
    this.recordChanges(changes, UndoLabel.SPLIT, {standalone: true});

    return [p1, p2];
  }

  /**
   * Join two areas into one. Returns the joined area, or null when they do not
   * union into one area and nothing changed, for the caller to tell the user.
   */
  joinPolygons(poly1: Polygon, poly2: Polygon): Polygon | null {
    // A hole joins only the area it is cut out of, which fills it in, or
    // another hole of the same area. Joined as two shapes, the result took
    // the first one's flags: started from the hole's panel, the whole area
    // became a hole of nothing, left out of every total and never priced.
    if (!!poly1.isHole !== !!poly2.isHole) {
      const hole = poly1.isHole ? poly1 : poly2;
      const area = hole === poly1 ? poly2 : poly1;
      if (hole.parentId !== area.id && !(area.holes || []).includes(hole)) {
        Logger.error('joinPolygons: a hole joins only the area it is cut out of');
        return null;
      }
      return this.fillHole(area, hole);
    }
    if (poly1.isHole && poly1.parentId !== poly2.parentId) {
      Logger.error('joinPolygons: holes of different areas');
      return null;
    }
    const joined = PolyUtil.joinPolygons(this.firebaseApi, poly1, poly2);
    if (!joined || (poly1.isHole && joined.holes.length > 0)) {
      Logger.error('joinPolygons: could not union the polygons');
      return null;
    }

    const changes: DocChange[] = this.withHoles([poly1, poly2])
      .map(g => ({kind: 'geo' as MapDocKind, id: g.id, before: this.snapshotOf(g), after: null}));

    // Inherit the source polygon's full styling (clone), then take the unioned
    // geometry. The union already carries every hole — the original hole rebuilt
    // from the two split notches AND any uncrossed holes — so use those directly.
    const newPoly = this.clonePolygon(poly1);
    // A circle or a rectangle joined is a shape of its own corners: kept a
    // circle, its first two corners were read as a centre and a radius.
    newPoly.shape = PolygonShape.POLYGON;
    newPoly.name = poly1.name;
    this.takeAttachments(newPoly, poly2);
    // Two holes of one area make one hole of it.
    if (poly1.isHole) {
      newPoly.parentId = poly1.parentId;
    }
    newPoly.points = joined.points;
    newPoly.points.forEach(pt => pt.parent = newPoly);
    newPoly.holes = [];

    const newHoles: Polygon[] = [];
    joined.holes.forEach(raw => {
      const hole = this.buildHole(raw.points.map(p => ({lat: p.lat, lng: p.lng})), newPoly);
      newPoly.holes.push(hole);
      newHoles.push(hole);
    });

    this.lockPolygon(newPoly, newPoly.name, true, false);

    changes.push(...this.removeGeometries([poly1, poly2], {record: false}));

    for (const g of [newPoly, ...newHoles]) {
      changes.push({kind: 'geo', id: g.id, before: null, after: this.snapshotOf(g)});
    }
    this.updateHoles();
    this.dispatchOnGeometriesChanged();
    this.recordChanges(changes, UndoLabel.JOIN, {standalone: true});
    return newPoly;
  }

  /** A hole joined with the area it is cut out of: the area, whole again, as one Join step. */
  private fillHole(area: Polygon, hole: Polygon): Polygon | null {
    if (!this.canEdit() && !this.isDemoMode()) {
      return null;
    }
    const changes: DocChange[] = [{kind: 'geo', id: hole.id, before: this.snapshotOf(hole), after: null}];
    // The hole's photos and notes stay with the area it is filled into.
    const areaBefore = this.snapshotOf(area);
    if (this.takeAttachments(area, hole)) {
      const areaAfter = this.snapshotOf(area);
      this.writeDoc('geo', area.id, areaAfter);
      changes.push({kind: 'geo', id: area.id, before: areaBefore, after: areaAfter});
    }
    changes.push(...this.removeGeometries([hole], {record: false}));
    this.recordChanges(changes, UndoLabel.JOIN, {standalone: true});
    return area;
  }

  /**
   * Gives `into` the photos and description of `from`, a shape a join takes
   * away. The joined area used to be a copy of the first one only: the
   * second's photos were left attached to nothing -- not on the map, in the
   * gallery or in an export -- and its description went with it. Both
   * descriptions are kept, one after the other. Returns whether anything was
   * added.
   */
  private takeAttachments(into: Polygon, from: Polygon): boolean {
    const images = [...new Set([...(into.images || []), ...(from.images || [])])];
    const own = (into.description || '').trim();
    const theirs = (from.description || '').trim();
    let description = into.description;
    if (theirs && theirs !== own) {
      description = own ? `${into.description}\n\n${from.description}` : from.description;
    }
    const changed = images.length !== (into.images || []).length || description !== into.description;
    into.images = images;
    into.description = description;
    return changed;
  }

  /**
   * Takes the ground `cutter` covers off `polygon` (Subtract). `polygon`
   * makes way for the pieces left, largest first, each a shape of its own
   * with its look, as a split's halves have it: the largest keeps its name
   * and photos, the others are named as a split's halves are. Its holes that
   * are left uncovered are cut out of the pieces; `cutter` stays as it is. A
   * circle or a rectangle cut is a shape of its own corners. One undo step
   * takes it all back.
   *
   * Returns the pieces, or why nothing changed for the caller to tell the
   * user; none, and nothing to say, for someone who may not edit.
   */
  subtractPolygons(polygon: Polygon, cutter: Polygon): Polygon[] | SubtractRefusal {
    if (!polygon || !cutter || polygon === cutter || polygon.id === cutter.id) {
      return 'error_subtract_polygons_itself';
    }
    if (!polygon.isLocked || !cutter.isLocked) {
      return 'error_subtract_polygons_not_locked';
    }
    // A hole is ground taken away already, with none of its own to give or
    // to lose; a line has none either (see PolyUtil.subtractPolygons).
    if (polygon.isHole || cutter.isHole) {
      return 'error_subtract_polygons_no_overlap';
    }
    if (!this.canEdit() && !this.isDemoMode()) {
      return [];
    }
    const pieces = PolyUtil.subtractPolygons(this.firebaseApi, polygon, cutter);
    if (!pieces) {
      return 'error_subtract_polygons_no_overlap';
    }
    if (!pieces.length) {
      return 'error_subtract_polygons_nothing_left';
    }

    // Taken before anything moves: the step records the shape and its holes
    // going, and the pieces and theirs arriving -- one Undo.
    const changes: DocChange[] = this.withHoles([polygon])
      .map(g => ({kind: 'geo' as MapDocKind, id: g.id, before: this.snapshotOf(g), after: null}));

    const added: Polygon[] = [];
    const made = pieces.map((piece, index) => {
      const shape = this.clonePolygon(polygon);
      shape.shape = PolygonShape.POLYGON;
      if (index > 0) {
        shape.name = `${polygon.name}-${index + 1}`;
        shape.images = [];
      }
      shape.points = piece.points;
      shape.points.forEach(pt => pt.parent = shape);
      shape.holes = piece.holes.map(raw => this.buildHole(raw.points.map(p => ({lat: p.lat, lng: p.lng})), shape));
      this.lockPolygon(shape, shape.name, true, false);
      added.push(shape, ...shape.holes);
      return shape;
    });

    // A piece of a buffer cut by hand: the rest of it lets go (see
    // detachBufferSetsOf), or the next change of its shape drew it back whole.
    // A buffer drawn round it keeps its outline cut out (bufferRingsFor) and
    // follows nothing, as when a shape is deleted; Undo links it again.
    changes.push(...this.removeGeometries([polygon], {record: false}));

    for (const g of added) {
      changes.push({kind: 'geo', id: g.id, before: null, after: this.snapshotOf(g)});
    }
    this.updateHoles();
    this.dispatchOnGeometriesChanged();
    this.recordChanges(changes, UndoLabel.SUBTRACT, {standalone: true});
    return made;
  }

  // Presence is not the listeners' business (see updateDataEventsListeners):
  // each join, leave or tab restarted them, re-reading the whole project.
  onConnectedUsersChanged(connectedUsers: UserInfo[]) {
    this.connectedUsers = connectedUsers;
  }

  onOpenSessionsChanged(sessionsOpen: number) {
    this.openSessions = sessionsOpen;
  }

  /**
   * Link every hole (and buffer source) to its parent.
   *
   * Only holes and buffers can be linked, so only they are scanned: checking
   * every polygon against every polygon took about 324 million comparisons
   * after importing a CAD file of 18,000 shapes. Same result as
   * updateHolesFor on each polygon.
   *
   * A buffer's source is drawn inside it like a hole, but it is not the
   * buffer's hole and its parentId is left alone. It used to be pointed at the
   * buffer (in memory only, and saved by accident later), so a second buffer
   * around the same shape was never linked, and undoing a buffer left its
   * source pointing at a shape that no longer existed.
   *
   * Which area a hole belongs to: see isHoleOf.
   */
  private updateHoles() {
    const holes = this.polygons.filter(p => p.isHole);
    this.placeLooseHoles(holes);
    const byId = new Map(this.polygons.map(p => [p.id, p] as [string, Polygon]));
    this.polygons.forEach(parent => {
      const had = parent.holes && parent.holes.length > 0;
      parent.holes = [];
      if (parent.isHole && !parent.isBuffer) {
        if (had) {
          parent.update();
        }
        return;
      }
      holes.forEach(p2 => {
        if (p2 !== parent && this.isHoleOf(p2, parent)) {
          this.linkHole(p2, parent);
        }
      });
      if (parent.isBuffer && parent.parentId) {
        const source = byId.get(parent.parentId);
        if (source && source !== parent && this.isCutFromBuffer(source) && !parent.holes.includes(source)) {
          parent.holes.push(source);
        }
      }
      if (had || parent.holes.length) {
        parent.update();
      }
    });
  }

  /**
   * Links the holes (and buffer source) of one shape. `onlyIfHoles`
   * recomputes the shape only when it has or had any, as updateHoles does.
   *
   * Only the holes are looked through (`holes`, the project's: a caller
   * relinking many shapes lists them once), and a buffer's source is found by
   * its id. Every shape used to be looked through for every shape relinked:
   * undoing a Clear Map of 15,000 shapes, or a collaborator's session
   * receiving it, went through 225 million pairs.
   */
  private updateHolesFor(parent: Polygon, onlyIfHoles: boolean = false, holes?: Polygon[]) {
    const had = !!parent.holes && parent.holes.length > 0;
    parent.holes = [];
    if (!parent.isHole || parent.isBuffer) {
      const list = holes || this.polygons.filter(p => p.isHole);
      // A hole saved without an area that lies in this one goes to the
      // smallest area around it, which may be another one inside this.
      const elsewhere = [...this.placeLooseHoles(list.filter(p2 => p2 !== parent && !p2.parentId && parent.contains(p2)))]
        .filter(area => area !== parent);
      list.forEach(p2 => {
        if (p2 !== parent && this.isHoleOf(p2, parent)) {
          this.linkHole(p2, parent);
        }
      });
      if (parent.isBuffer && parent.parentId) {
        const source = this.geoIndex.get(parent.parentId);
        if (source instanceof Polygon && source !== parent && this.isCutFromBuffer(source)
          && !parent.holes.includes(source)) {
          parent.holes.push(source);
        }
      }
      if (!onlyIfHoles || had || parent.holes.length) {
        parent.update();
      }
      elsewhere.forEach(area => this.updateHolesFor(area, true, list));
    } else if (onlyIfHoles && had) {
      parent.update();
    }
  }

  /**
   * Links holes again after the shapes `ids` changed or went away, touching
   * only what that can move: those shapes, the shapes that held one of them as
   * a hole, the buffers around them, and the areas a changed hole belongs to
   * (see placeLooseHoles for one saved without an area). Every collaborator's
   * save and every undo used to relink every hole in the project and recompute
   * every shape with one.
   */
  private updateHolesAround(ids: Set<string>) {
    if (!ids.size) {
      return;
    }
    const affected = new Set<Polygon>();
    const changedHoles: Polygon[] = [];
    this.polygons.forEach(p => {
      if (ids.has(p.id)) {
        affected.add(p);
        if (p.isHole) {
          changedHoles.push(p);
        }
      } else if ((p.holes || []).some(hole => ids.has(hole.id)) || (p.isBuffer && ids.has(p.parentId))) {
        affected.add(p);
      }
    });
    // A changed hole's area is the one it names (see isHoleOf); one saved
    // without an area is given the smallest it lies inside first.
    this.placeLooseHoles(changedHoles);
    changedHoles.forEach(hole => {
      const area = hole.parentId ? this.geoIndex.get(hole.parentId) : null;
      if (area instanceof Polygon && area !== hole && (!area.isHole || area.isBuffer)) {
        affected.add(area);
      }
    });
    const holes = this.polygons.filter(p => p.isHole);
    this.polygons.filter(p => affected.has(p)).forEach(p => this.updateHolesFor(p, true, holes));
  }

  /**
   * Whether a hole is cut out of an area: the one it was made a hole of
   * (parentId), wherever it now lies, as the server's exports and project
   * total have it. It used to need to lie wholly inside as well, so a hole
   * moved part way out of its area stopped counting here -- the area showed
   * its full size and the hole counted nowhere -- while the exports and the
   * estimate still took it off. A hole saved without an area (older apps) is
   * given one by where it lies before it is linked; see placeLooseHoles.
   */
  private isHoleOf(hole: Polygon, area: Polygon): boolean {
    return !!hole.parentId && hole.parentId === area.id;
  }

  /**
   * Gives each of `holes` saved without an area -- the mobile apps' and older
   * .mmp files have no PARENTID, and a duplicated hole has none yet -- the
   * smallest area it lies inside, as the lock prompt does
   * (checkIfPolygonContained): a courtyard in a building that stands in a
   * field is the building's hole. Taken in list order, it often went to the
   * field, which lost the courtyard's area while the building kept it. The
   * link is saved (see adoptHole). Returns the areas that took one.
   */
  private placeLooseHoles(holes: Polygon[]): Set<Polygon> {
    const placed = new Set<Polygon>();
    const loose = holes.filter(hole => hole.isHole && !hole.parentId);
    if (!loose.length) {
      return placed;
    }
    const areas = this.polygons.filter(p => !p.isHole || p.isBuffer);
    loose.forEach(hole => {
      const area = PolyUtil.findContainer(areas, hole);
      if (area) {
        hole.parentId = area.id;
        this.adoptHole(hole);
        placed.add(area);
      }
    });
    return placed;
  }

  /** Puts a hole in its area (one saved without an area has been given one: see placeLooseHoles). */
  private linkHole(hole: Polygon, area: Polygon) {
    area.holes.push(hole);
  }

  /** Holes given an area by where they lie, whose link is yet to be saved. */
  private adoptedHoles = new Set<Polygon>();
  private adoptTimer: ReturnType<typeof setTimeout> = null;

  /**
   * Saves the area a hole saved without one was linked to here. Kept in
   * memory only, the server's exports and project total -- which go by the
   * saved link -- listed such a hole as an area of its own and left its area
   * whole, while the canvas took it off. Saved after the pass that linked it,
   * when the shapes' saved state is known (a load links before it is), by
   * someone who may edit, quietly: nobody changed the shape.
   */
  private adoptHole(hole: Polygon) {
    this.adoptedHoles.add(hole);
    if (this.adoptTimer) {
      return;
    }
    this.adoptTimer = setTimeout(() => {
      this.adoptTimer = null;
      const holes = [...this.adoptedHoles];
      this.adoptedHoles.clear();
      if (this.rawView || !this.canEdit()) {
        return;
      }
      // Only shapes on the map now: another load makes new ones.
      holes.filter(hole => this.geoIndex.get(hole.id) === hole && hole.parentId && this.fireGeometries[hole.id])
        .forEach(hole => this.writeDoc('geo', hole.id, this.snapshotOf(hole), {quiet: true}));
    }, 0);
  }

  /**
   * Whether a buffer's source is cut out of it, making the buffer a band. Only
   * an area is: a line's buffer is a corridor along it, and used as a ring the
   * open line closed itself into a shape whose area came off the corridor's,
   * down to a negative figure. The export measures a buffer the same way
   * (export/geometry.ts on the server).
   */
  private isCutFromBuffer(source: Polygon): boolean {
    return !!source.showSurface;
  }

  private updateCurrentPolygon() {
    const empty = this.polygons.filter(poly => !poly.hasPoints());
    if (empty.length) {
      this.removeGeometries(empty, {record: false});
    }
    const unlockedPolys = {};
    this.polygons.forEach(poly => {
      if (!poly.isLocked) {
        let userPolys = unlockedPolys[poly.lastUserUpdateId];
        if (!userPolys) {
          userPolys = [];
          unlockedPolys[poly.lastUserUpdateId] = userPolys;
        }
        userPolys.push(poly);
      }
    });
    ObjectUtil.every(unlockedPolys, (uid, polys) => {
      if (polys.length > 1) {
        polys.pop();
        polys.forEach(poly => {
          poly.isLocked = true;
          poly.update();
          this.saveGeometry({addUndoCheckpoint: false}, poly);
        });
      }
    });

    let cp = null;
    this.polygons.forEach(poly => {
      if (this.isCurrentPolygon(poly)) {
        cp = poly;
      }
    });
    this.currentPolygon = cp;
  }

  private isCurrentPolygon(poly: Polygon) {
    return !poly.isLocked && (!poly.lastUserUpdateId || poly.lastUserUpdateId === this.authService.getUserId);
  }

  /**
   * `listen` is false for pasted raw data: a local view of shapes that are not
   * the project's, which the project's listener would otherwise take off the
   * map again as "deleted while not listening".
   */
  private finishLoadingGeometries(geometries, geometriesData, listen: boolean = true) {
    this.geometriesChanged = false;
    this.setGeometries(geometries);
    this.updateHoles();
    this.fireGeometries = geometriesData ? geometriesData : {};
    if (listen) {
      this.setDataEventsEnabled(true);
    }
    this.updateCurrentPolygon();
  }

  private loadMapLayers(pid: string) {
    // Layers of a project left (or reloaded) meanwhile stay off the map.
    const epoch = this.loadEpoch;
    return new Promise<void>((resolve) => {
      this.firebaseApi.getProjectMapLayers(pid).then(layers => {
        if (epoch !== this.loadEpoch) {
          resolve();
          return;
        }
        layers.forEach(layer => this.layerDocs.set(layer.id, this.cleanLayerDoc(layer)));
        // The first map, whenever it exists. take(1) alone took the current
        // value even when that was "no map yet", and the layers never loaded.
        this.mapService.mapObservable.pipe(filter(map => !!map), take(1)).subscribe(async () => {
          for (const layer of layers) {
            if (epoch !== this.loadEpoch) {
              break;
            }
            await this.addMapLayer(layer);
          }
          resolve();
        });
      });
    });
  }

  /** Layers being put on the map right now, each with its load, so two callers cannot load one twice. */
  private loadingLayers = new Map<string, Promise<void>>();
  /**
   * MBTiles layers whose file could not be opened: listed, so they can be
   * removed, but drawing nothing. Each with the ImportError code an upload
   * of it reports ('vector_tiles' for a file of vector tiles).
   */
  private unreadableLayers = new Map<string, string>();

  /**
   * Puts a layer on the map, once. The initial load, an upload finishing and
   * the layers listener can all announce the same layer, and an MBTiles file
   * takes a moment to open, so both "already there" and "on its way" count.
   *
   * A caller that finds the layer on its way waits for that load. The
   * listener announces an upload before the upload has finished, so the
   * upload used to go on before its file was even open: nothing to zoom to,
   * and no way to tell that the file could not be read.
   */
  private addMapLayer(layer: Layer): Promise<void> {
    if (!layer.id || this.layers.some(l => l.id === layer.id)) {
      return Promise.resolve();
    }
    const loading = this.loadingLayers.get(layer.id);
    if (loading) {
      return loading;
    }
    let settle: () => void;
    const load = new Promise<void>(resolve => settle = resolve);
    this.loadingLayers.set(layer.id, load);
    // Removed (cleared, undone) while it was loading, which takes it out of
    // loadingLayers: it is not drawn.
    const removed = () => this.loadingLayers.get(layer.id) !== load;
    const done = () => {
      if (!removed()) {
        this.loadingLayers.delete(layer.id);
      }
      settle();
    };
    this.openMapLayer(layer, removed).then(done, e => {
      Logger.error('Layer could not be added', layer.name, e);
      done();
    });
    return load;
  }

  /** addMapLayer's work: opens and draws an MBTiles file, then lists the layer. */
  private async openMapLayer(layer: Layer, removed: () => boolean) {
    try {
      if (layer.type === LayerType.MBTILES) {
        // Tiles are drawn by the map itself, so they wait for it.
        const map = await firstValueFrom(this.mapService.mapObservable.pipe(filter(m => !!m), take(1)));
        // Only a file in the app's own storage: anyone who may draw can write
        // the layer's document, and every viewer downloads the whole file.
        if (!isOwnStorageUrl(layer.downloadUrl)) {
          throw new Error('MBTiles layer outside the app\'s storage');
        }
        const db = await MBTilesUtil.load(layer.downloadUrl);
        // Takes the database over (closing one it refuses), with the
        // layer's Transparency, which the tiles never showed.
        const provider = new MBTilesProvider(db, layer.id, layer.opacity ?? 1);
        // Bounds are optional in an MBTiles file. Their absence used to stop
        // the tiles being drawn at all; now there is just nothing to zoom to.
        try {
          layer.bounds = provider.getBounds();
        } catch (e) {
          Logger.error('MBTiles file without bounds', layer.name, e);
        }
        if (!layer.name) {
          layer.name = provider.name;
        }
        if (removed()) {
          provider.dispose();
          return;
        }
        map.overlayMapTypes.push(provider);
      }
    } catch (e) {
      Logger.error('Layer could not be loaded', layer.name, e);
      this.unreadableLayers.set(layer.id, e instanceof MBTilesFormatError ? 'vector_tiles' : 'unreadable');
    }
    if (removed()) {
      this.unreadableLayers.delete(layer.id);
      return;
    }
    this.layers.push(layer);
    this.dispatchOnGeometriesChanged();

    if (layer.type === LayerType.MBTILES) {
      this.googleAnalyticsService.logEvent('canvas', 'add_mbtiles');
    } else {
      this.googleAnalyticsService.logEvent('canvas', 'add_image_layer');
    }
  }
}
